// =====================================================================================
// FILE: src/pages/Operator/OperatorDashboard.jsx
// 
// Main Operator Portal Dashboard
// Displays aggregated CG/Ribbon content from all clubs with operator controls
// Features: content preview, send to encoder, row selection, delete, edit, 
//           drag & drop, conditional status colors, editable fields
// =====================================================================================

import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useOperator } from '../../context/OperatorContext';
import { useAuth } from '../../context/AuthContext';
import { PresenceAvatars } from '../../components/presence';
import { usePresence } from '../../context/PresenceContext';
import { useToast } from '../../components/ui/UploaderToast';
import RibbonPreviewModal from '../../components/ui/RibbonPreviewModal';
import ThreeSixtyPreviewModal from '../../components/ui/ThreeSixtyPreviewModal';
import OperatorOverview from './OperatorOverview';
import { checkSyncStatus } from '../../services/bidirectionalSync';
import { IPTV_CATEGORY_ORDER } from '../../constants/categories';

// ─── Operator Category Order ───
// Defines the display order for categories in the Operator View.
// Follows the MatchFlow Builder structure: game phases first, then Team List at the end.
// Categories not in this list appear after the defined ones.
const OPERATOR_CG_CATEGORY_ORDER = [
  // Ancillary Content
  'Ponsford Stage', 'DJ Booth', 'Goal Celebration', 'In-Game Triggers Interchange', 'Interchange', 'In-Game Triggers', 'Robbie Flowers',
  // Pre-Game
  'Gates-30 Loop', 'Gates-30 Moments', '30-Bounce Loop', '30-Bounce Moments',
  // In-Game (Quarters)
  'Q1 LED Loop', 'Q1 Moments', 'Q2 LED Loop', 'Q2 Moments', 'Q3 LED Loop', 'Q3 Moments', 'Q4 LED Loop', 'Q4 Moments',
  // Quarter Breaks
  'QT Loop', 'QT Moments', 'HT Loop', 'HT Moments', '3QT Loop', '3QT Moments',
  // Post-Game
  'PG WIN Loop', 'PG WIN Moments', 'PG LOSE Moments', 'PG LOSE Loop',
  // Team List
  'Team List',
  // MCG Venue Entitlements (shows when data exists)
  'MCG Venue Entitlements',
  // Marvel Venue Entitlements (injected for Marvel views, below Team List)
  'Disney Entitlement', 'AFL Marketing',
  'Venue Operations Pre-Match', 'Venue Operations Post-Match',
];

function getOperatorCategoryOrder(contentType) {
  if (contentType === 'cg' || contentType === 'ribbon') return OPERATOR_CG_CATEGORY_ORDER;
  if (contentType === 'iptv') return IPTV_CATEGORY_ORDER;
  return []; // LED, TVC — use Airtable order
}

// ─── Drag-to-Scroll + Custom Fixed Scrollbar Hook ───
function useDragToScroll() {
  const scrollRef = useRef(null);
  const stickyRef = useRef(null);
  const isDragging = useRef(false);
  const isThumbDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeftStart = useRef(0);
  const thumbStartX = useRef(0);
  const tableAttached = useRef(false);
  const thumbAttached = useRef(false);

  // Update custom thumb position and size
  const updateThumb = useCallback(() => {
    const el = scrollRef.current;
    const bar = stickyRef.current;
    if (!el || !bar) return;
    const track = bar.querySelector('.operator-fixed-scrollbar-track');
    const thumb = bar.querySelector('.operator-fixed-scrollbar-thumb');
    if (!track || !thumb) return;
    const trackW = track.clientWidth;
    const contentW = el.scrollWidth;
    const viewW = el.clientWidth;
    if (contentW <= viewW) { thumb.style.display = 'none'; return; }
    thumb.style.display = 'block';
    const thumbW = Math.max(60, (viewW / contentW) * trackW);
    const scrollRatio = el.scrollLeft / (contentW - viewW);
    const thumbLeft = scrollRatio * (trackW - thumbW);
    thumb.style.width = thumbW + 'px';
    thumb.style.left = thumbLeft + 'px';
  }, []);

  const attach = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    
    // Attach table drag listeners (once)
    if (!tableAttached.current) {
      tableAttached.current = true;
      const onMouseDown = (e) => {
        const tag = e.target.tagName.toLowerCase();
        if (tag === 'button' || tag === 'input' || tag === 'select' || tag === 'textarea' || tag === 'a' || tag === 'label') return;
        if (e.target.closest('button, input, select, textarea, a, label, [role="button"]')) return;
        isDragging.current = true;
        startX.current = e.pageX - el.offsetLeft;
        scrollLeftStart.current = el.scrollLeft;
        el.style.cursor = 'grabbing';
        el.style.userSelect = 'none';
      };
      const onMouseMove = (e) => {
        if (isDragging.current) {
          e.preventDefault();
          const x = e.pageX - el.offsetLeft;
          el.scrollLeft = scrollLeftStart.current - (x - startX.current) * 1.5;
        }
        if (isThumbDragging.current) {
          e.preventDefault();
          const bar = stickyRef.current;
          const track = bar?.querySelector('.operator-fixed-scrollbar-track');
          const thumb = bar?.querySelector('.operator-fixed-scrollbar-thumb');
          if (!track || !thumb) return;
          const trackW = track.clientWidth;
          const thumbW = thumb.clientWidth;
          const contentW = el.scrollWidth;
          const viewW = el.clientWidth;
          const dx = e.clientX - thumbStartX.current;
          const trackRange = trackW - thumbW;
          if (trackRange > 0) {
            el.scrollLeft = scrollLeftStart.current + (dx / trackRange) * (contentW - viewW);
          }
        }
      };
      const onMouseUp = () => {
        if (isDragging.current) {
          isDragging.current = false;
          el.style.cursor = 'grab';
          el.style.userSelect = '';
        }
        if (isThumbDragging.current) {
          isThumbDragging.current = false;
          document.body.style.cursor = '';
          document.body.style.userSelect = '';
        }
      };
      el.addEventListener('mousedown', onMouseDown);
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
      el.addEventListener('scroll', updateThumb);
    }

    // Attach thumb drag listeners (retries until scrollbar div exists)
    const bar = stickyRef.current;
    if (bar && !thumbAttached.current) {
      const thumb = bar.querySelector('.operator-fixed-scrollbar-thumb');
      const track = bar.querySelector('.operator-fixed-scrollbar-track');
      if (thumb && track) {
        thumbAttached.current = true;
        thumb.addEventListener('mousedown', (e) => {
          e.preventDefault();
          e.stopPropagation();
          isThumbDragging.current = true;
          thumbStartX.current = e.clientX;
          scrollLeftStart.current = el.scrollLeft;
          document.body.style.cursor = 'grabbing';
          document.body.style.userSelect = 'none';
        });
        track.addEventListener('mousedown', (e) => {
          if (e.target === thumb) return;
          const rect = track.getBoundingClientRect();
          const clickRatio = (e.clientX - rect.left) / rect.width;
          el.scrollLeft = clickRatio * (el.scrollWidth - el.clientWidth);
        });
        updateThumb();
      }
    }
  }, [updateThumb]);

  const onMainScroll = useCallback(() => { updateThumb(); }, [updateThumb]);
  const syncBarWidth = useCallback(() => { updateThumb(); setTimeout(updateThumb, 500); }, [updateThumb]);

  return { scrollRef, stickyRef, attach, onMainScroll, syncBarWidth };
}

// ================= THEME (matching AppShell) =================

const THEME = {
  bg: '#0a0a0f',
  bgElevated: '#12121a',
  bgCard: '#16161f',
  bgHover: '#1e1e2a',
  accent: '#2fd6b5',
  accentHover: '#26b89a',
  lime: '#cdde25',
  textPrimary: '#ffffff',
  textSecondary: '#a1a1aa',
  textMuted: '#71717a',
  border: 'rgba(255, 255, 255, 0.08)',
  borderHover: 'rgba(255, 255, 255, 0.15)',
  
  // Status colors for row highlighting - high visibility for operators
  // Operator View: Ready for Testing (amber), Rejected (red), Approved (bright green), Content Unlocked (purple)
  statusColors: {
    'New Submission': { bg: 'rgba(161, 161, 170, 0.22)', border: 'rgba(161, 161, 170, 0.6)' },
    'Ready for Testing': { bg: 'rgba(251, 191, 36, 0.25)', border: 'rgba(251, 191, 36, 0.7)' },
    'Rejected': { bg: 'rgba(239, 68, 68, 0.30)', border: 'rgba(239, 68, 68, 0.7)' },
    'Approved by Lumen8': { bg: 'rgba(16, 185, 129, 0.30)', border: 'rgba(16, 185, 129, 0.8)' },
    'Content Unlocked': { bg: 'rgba(168, 85, 247, 0.28)', border: 'rgba(168, 85, 247, 0.6)' },
  },
};

// Statuses hidden from Operator Dashboard
// Statuses hidden from Operator Dashboard (Example Content is a placeholder, not real content)
const HIDDEN_STATUSES = ['Example Content'];

// Categories hidden from Operator Dashboard (Example rows are placeholders)
const HIDDEN_CATEGORIES = ['Example Content', 'Example Row'];

// Row names to hide (these are placeholder/example rows, not real content)
const HIDDEN_ROW_NAMES = ['Example Content', 'Example Row', 'Example', 'example content', 'example row'];

// Categories managed directly in CG Operator table (not via MatchFlow allocation)
// These bypass the "Example Content" hiding — all rows show regardless of name
// Marvel venue categories (injected for Marvel views only)
const CG_MARVEL_MANUAL_CATEGORIES = [
  'AFL Marketing',
  'Disney Entitlement',
  'Venue Operations Pre-Match',
  'Venue Operations Post-Match',
];

// Combined for filtering logic
const CG_ALL_MANUAL_CATEGORIES = [...CG_MARVEL_MANUAL_CATEGORIES, 'MCG Venue Entitlements'];

// IPTV operator-only category patterns (managed at venue, not from MatchFlow)
const IPTV_OPERATOR_PATTERNS = ['[AFL Marketing]', '[Disney]', '[Victory]'];
const isIptvOperatorCategory = (category) => {
  if (!category) return false;
  return IPTV_OPERATOR_PATTERNS.some(pattern => category.includes(pattern));
};

// Check if a category is manually managed (bypasses Example Content filtering)
const isManualOrOperatorCategory = (category, contentType) => {
  if (CG_ALL_MANUAL_CATEGORIES.includes(category)) return true;
  if (contentType === 'iptv' && isIptvOperatorCategory(category)) return true;
  return false;
};

// IPTV content folder in Dropbox (for "Open Folder" link)
const IPTV_DROPBOX_FOLDER = 'https://www.dropbox.com/home/AFL/VenueFlow_Exports/2026/Ancillary_Content';

// Categories list removed - DJ Booth and Ponsford now go to Tessera like normal LED content

// Categories are now extracted dynamically from loaded records
// This ensures they always match what's actually in Airtable

// ================= THEMED DIALOGS =================

function ConfirmDialog({ isOpen, title, message, confirmLabel, cancelLabel, confirmColor, icon, onConfirm, onCancel }) {
  if (!isOpen) return null;
  
  const color = confirmColor || '#ef4444';
  
  return (
    <>
      <div
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
          zIndex: 10000,
          animation: 'fadeIn 0.15s ease-out',
        }}
        onClick={onCancel}
      />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        background: THEME.bgElevated,
        borderRadius: '16px',
        border: `1px solid ${THEME.border}`,
        boxShadow: `0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px ${color}15`,
        zIndex: 10001,
        width: '100%',
        maxWidth: '480px',
        overflow: 'hidden',
        animation: 'slideUp 0.2s ease-out',
      }}>
        <div style={{ padding: '28px 24px 20px', textAlign: 'center' }}>
          {/* Icon */}
          <div style={{
            width: 56, height: 56, borderRadius: 14,
            background: `${color}18`,
            border: `1px solid ${color}30`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
            fontSize: '24px',
          }}>
            {icon || '🗑️'}
          </div>
          
          <h3 style={{
            fontSize: '18px', fontWeight: 700, color: THEME.textPrimary,
            margin: '0 0 8px',
            fontFamily: "'Space Grotesk', sans-serif",
          }}>
            {title || 'Confirm Action'}
          </h3>
          
          <p style={{
            fontSize: '14px', color: THEME.textSecondary,
            lineHeight: 1.5, margin: 0,
            whiteSpace: 'pre-line',
            textAlign: 'left',
          }}>
            {message}
          </p>
        </div>
        
        <div style={{
          padding: '16px 24px 20px',
          display: 'flex', gap: '12px', justifyContent: 'center',
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '10px 24px',
              background: 'transparent',
              border: `1px solid ${THEME.border}`,
              borderRadius: '10px',
              color: THEME.textSecondary,
              fontSize: '14px', fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
            onMouseOver={(e) => { e.currentTarget.style.background = THEME.bgHover; }}
            onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            {cancelLabel || 'Cancel'}
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: '10px 24px',
              background: color,
              border: 'none',
              borderRadius: '10px',
              color: '#ffffff',
              fontSize: '14px', fontWeight: 600,
              cursor: 'pointer',
              boxShadow: `0 4px 12px ${color}40`,
              transition: 'all 0.15s ease',
            }}
            onMouseOver={(e) => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'scale(1.02)'; }}
            onMouseOut={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'scale(1)'; }}
          >
            {confirmLabel || 'Delete'}
          </button>
        </div>
      </div>
    </>
  );
}

function AlertDialog({ isOpen, title, message, type, onClose }) {
  if (!isOpen) return null;
  
  const config = {
    error: { color: '#ef4444', icon: '❌', label: 'Error' },
    success: { color: '#22c55e', icon: '✅', label: 'Success' },
    warning: { color: '#f59e0b', icon: '⚠️', label: 'Warning' },
    info: { color: '#2dd4bf', icon: 'ℹ️', label: 'Info' },
  };
  const { color, icon } = config[type] || config.error;
  
  return (
    <>
      <div
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
          zIndex: 10000,
          animation: 'fadeIn 0.15s ease-out',
        }}
        onClick={onClose}
      />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        background: THEME.bgElevated,
        borderRadius: '16px',
        border: `1px solid ${THEME.border}`,
        boxShadow: `0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px ${color}15`,
        zIndex: 10001,
        width: '100%',
        maxWidth: '400px',
        overflow: 'hidden',
        animation: 'slideUp 0.2s ease-out',
      }}>
        <div style={{ padding: '28px 24px 20px', textAlign: 'center' }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14,
            background: `${color}18`,
            border: `1px solid ${color}30`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
            fontSize: '24px',
          }}>
            {icon}
          </div>
          
          <h3 style={{
            fontSize: '18px', fontWeight: 700, color: THEME.textPrimary,
            margin: '0 0 8px',
            fontFamily: "'Space Grotesk', sans-serif",
          }}>
            {title}
          </h3>
          
          <p style={{
            fontSize: '14px', color: THEME.textSecondary,
            lineHeight: 1.5, margin: 0,
            wordBreak: 'break-word',
          }}>
            {message}
          </p>
        </div>
        
        <div style={{ padding: '12px 24px 20px', display: 'flex', justifyContent: 'center' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 32px',
              background: color,
              border: 'none',
              borderRadius: '10px',
              color: '#ffffff',
              fontSize: '14px', fontWeight: 600,
              cursor: 'pointer',
              boxShadow: `0 4px 12px ${color}40`,
            }}
          >
            OK
          </button>
        </div>
      </div>
    </>
  );
}

// Custom hook for themed dialogs
function useThemedDialogs() {
  const [confirmState, setConfirmState] = useState(null);
  const [alertState, setAlertState] = useState(null);
  const resolveRef = useRef(null);
  
  const confirm = useCallback(({ title, message, confirmLabel, cancelLabel, confirmColor, icon }) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setConfirmState({ title, message, confirmLabel, cancelLabel, confirmColor, icon });
    });
  }, []);
  
  const showAlert = useCallback(({ title, message, type }) => {
    return new Promise((resolve) => {
      setAlertState({ title, message, type, onDone: resolve });
    });
  }, []);
  
  const handleConfirm = useCallback(() => {
    resolveRef.current?.(true);
    setConfirmState(null);
  }, []);
  
  const handleCancel = useCallback(() => {
    resolveRef.current?.(false);
    setConfirmState(null);
  }, []);
  
  const handleAlertClose = useCallback(() => {
    alertState?.onDone?.();
    setAlertState(null);
  }, [alertState]);
  
  const DialogComponents = (
    <>
      <ConfirmDialog
        isOpen={!!confirmState}
        {...(confirmState || {})}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
      <AlertDialog
        isOpen={!!alertState}
        {...(alertState || {})}
        onClose={handleAlertClose}
      />
    </>
  );
  
  return { confirm, showAlert, DialogComponents };
}

// ================= STYLES =================

const styles = {
  container: {
    padding: '24px',
    height: '100%',
    overflow: 'auto',
    background: THEME.bg,
  },
  
  header: {
    marginBottom: '24px',
  },
  
  title: {
    fontSize: '28px',
    fontWeight: 700,
    color: THEME.textPrimary,
    marginBottom: '8px',
  },
  
  subtitle: {
    fontSize: '14px',
    color: THEME.textSecondary,
  },
  
  controls: {
    display: 'flex',
    gap: '16px',
    marginBottom: '24px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  
  controlGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  
  controlLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: THEME.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  
  select: {
    padding: '10px 14px',
    borderRadius: '8px',
    border: `1px solid ${THEME.border}`,
    background: THEME.bgCard,
    color: THEME.textPrimary,
    fontSize: '14px',
    minWidth: '180px',
    cursor: 'pointer',
    outline: 'none',
    transition: 'all 0.15s ease',
  },
  
  contentTypeToggle: {
    display: 'flex',
    gap: '2px',
    padding: '3px',
    background: THEME.bgCard,
    borderRadius: '8px',
    border: `1px solid ${THEME.border}`,
    flexWrap: 'wrap',
  },
  
  toggleButton: {
    padding: '8px 14px',
    borderRadius: '5px',
    border: 'none',
    background: 'transparent',
    color: THEME.textSecondary,
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    whiteSpace: 'nowrap',
  },
  
  toggleButtonActive: {
    background: THEME.accent,
    color: '#000',
  },
  
  // Bulk actions bar
  bulkActionsBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 20px',
    background: THEME.bgCard,
    borderRadius: '8px',
    marginBottom: '16px',
    border: `1px solid ${THEME.accent}`,
    flexWrap: 'wrap',
  },
  
  bulkButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  
  statsBar: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '10px',
    marginBottom: '16px',
  },
  
  statCard: {
    padding: '16px 18px',
    background: THEME.bgCard,
    borderRadius: '10px',
    border: `1px solid ${THEME.border}`,
    minHeight: '90px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  
  statValue: {
    fontSize: '28px',
    fontWeight: 700,
    color: THEME.textPrimary,
    marginBottom: '4px',
  },
  
  statLabel: {
    fontSize: '13px',
    color: '#cbd5e1',
    fontWeight: 500,
  },
  
  categorySection: {
    marginBottom: '32px',
  },
  
  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '16px',
    padding: '12px 16px',
    background: 'linear-gradient(90deg, rgba(0,201,167,0.08) 0%, rgba(0,201,167,0.02) 100%)',
    borderRadius: '8px',
    border: `1px solid rgba(0,201,167,0.25)`,
    borderLeft: '4px solid #00c9a7',
    flexWrap: 'wrap',
    minWidth: '2200px',
  },
  
  categoryName: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#00c9a7',
  },
  
  categoryCount: {
    fontSize: '13px',
    color: THEME.textMuted,
    padding: '4px 10px',
    background: THEME.bgHover,
    borderRadius: '4px',
    fontWeight: 600,
  },
  
  tableWrapper: {
    overflowX: 'visible',
    borderRadius: '12px',
    border: `1px solid ${THEME.border}`,
  },
  
  table: {
    width: '100%',
    minWidth: '2200px',
    borderCollapse: 'collapse',
    background: THEME.bgCard,
  },
  
  th: {
    padding: '10px 10px',
    textAlign: 'left',
    fontSize: '11px',
    fontWeight: 700,
    color: 'rgba(255, 255, 255, 0.85)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    background: THEME.bgElevated,
    borderBottom: `2px solid ${THEME.border}`,
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
  },
  
  td: {
    padding: '8px 10px',
    fontSize: '13px',
    color: THEME.textPrimary,
    borderBottom: `1px solid ${THEME.border}`,
    verticalAlign: 'middle',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  
  row: {
    transition: 'background 0.15s ease',
    cursor: 'grab',
    background: THEME.bgCard,
  },
  
  rowDragging: {
    opacity: 0.5,
    background: THEME.bgHover,
  },
  
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    accentColor: THEME.accent,
  },
  
  statusBadge: {
    display: 'inline-block',
    padding: '5px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  
  clubBadge: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 600,
    background: THEME.bgHover,
    color: THEME.textSecondary,
  },
  
  venueBadge: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 500,
    background: 'rgba(47, 214, 181, 0.15)',
    color: THEME.accent,
  },
  
  inlineInput: {
    padding: '6px 8px',
    borderRadius: '4px',
    border: `1px solid ${THEME.border}`,
    background: THEME.bgElevated,
    color: THEME.textPrimary,
    fontSize: '13px',
    width: '100%',
    outline: 'none',
    transition: 'border-color 0.15s ease',
  },
  
  inlineSelect: {
    padding: '6px 8px',
    borderRadius: '4px',
    border: `1px solid ${THEME.border}`,
    background: THEME.bgElevated,
    color: THEME.textPrimary,
    fontSize: '13px',
    cursor: 'pointer',
    outline: 'none',
    minWidth: '110px',
  },
  
  // Action buttons
  actionButton: {
    padding: '5px 10px',
    borderRadius: '4px',
    border: 'none',
    background: 'transparent',
    color: THEME.textMuted,
    cursor: 'pointer',
    fontSize: '13px',
    transition: 'all 0.15s ease',
  },
  
  deleteButton: {
    color: '#ef4444',
  },
  
  editButton: {
    color: THEME.accent,
  },
  
  dragHandle: {
    cursor: 'grab',
    color: THEME.textMuted,
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
  },
  
  loadingOverlay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px',
    color: THEME.textSecondary,
  },
  
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: THEME.textMuted,
  },
  
  errorState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#ef4444',
    background: 'rgba(239, 68, 68, 0.1)',
    borderRadius: '12px',
    marginBottom: '24px',
  },
};

// Column widths for consistent alignment (table-layout: fixed)
// Total should roughly sum to minWidth (1600px)
const COLUMN_WIDTHS = {
  select: '36px',
  drag: '28px',
  name: '280px',
  thumbnail: '75px',
  lumen8Id: '130px',
  instructions: '200px',
  contentType: '140px',
  hasAudio: '60px',
  hasAlpha: '60px',
  duration: '90px',
  dropbox: '100px',
  venue: '80px',
  comments: '160px',
  status: '160px',
  sendToWatchFolder: '130px',
  ingestStatus: '105px',
  builtAtVenue: '120px',
  takeId: '90px',
  lastModified: '100px',
  actions: '70px',
  download: '44px',
  timing: '140px',
  location: '160px',
};

// Status sort priority (lower = shown first in status-priority mode)
const SORT_STATUS_PRIORITY = {
  'Content Unlocked': 0,
  'Rejected': 1,
  'New Submission': 2,
  'Ready for Testing': 3,
  '': 4,
  'Approved by Lumen8': 5,
};

// ================= STICKY COLUMNS =================
// Name, thumbnail, and Lumen8 ID freeze during horizontal scroll
const STICKY_COLS = {
  drag:      { left: 0, width: 28 },
  name:      { left: 28, width: 220 },
  thumbnail: { left: 248, width: 75 },
  lumen8Id:  { left: 323, width: 130 },
};
const stickyStyle = (col, isHeader = false, rowBg = null) => ({
  position: 'sticky',
  left: STICKY_COLS[col]?.left ?? 0,
  zIndex: isHeader ? 4 : 3,
  background: isHeader ? THEME.bgElevated : (rowBg || '#14141c'),
  // Prevent content from bleeding under sticky columns
  borderRight: col === 'lumen8Id' ? `1px solid ${THEME.border}` : undefined,
});

// ================= ICONS =================

const Icons = {
  drag: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="9" cy="6" r="2" />
      <circle cx="15" cy="6" r="2" />
      <circle cx="9" cy="12" r="2" />
      <circle cx="15" cy="12" r="2" />
      <circle cx="9" cy="18" r="2" />
      <circle cx="15" cy="18" r="2" />
    </svg>
  ),
  play: (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  ),
  delete: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  ),
  noImage: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  ),
  link: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  ),
  plus: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  download: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  ),
  edit: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
};

// ================= HELPER FUNCTIONS =================

// Check if a record's content has been ingested at the venue (green dot)
function isRecordIngested(record) {
  const fields = record?.fields || {};
  const marvelIngest = fields['_marvelIngest'] || false;
  const mcgIngest = fields['_mcgIngest'] || false;
  const venue = Array.isArray(fields['Venue']) ? fields['Venue'][0] : (fields['Venue'] || '');
  const destinationVenue = fields['Destination Venue'] || '';
  const v = (venue || destinationVenue || '').toLowerCase();
  const isTrue = (val) => val === true || val === 'checked' || val === 'true' || val === '✓';
  
  // IPTV: Send To Watch Folder = ingested (no XPression build step)
  const dest = (fields['_destination'] || fields['Content Type'] || '').toLowerCase();
  if (dest.includes('iptv')) return !!fields['Send To Watch Folder'];
  
  if (v.includes('marvel') || v.includes('mrv')) return isTrue(marvelIngest);
  if (v.includes('mcg') || v.includes('melbourne cricket')) return isTrue(mcgIngest);
  return false;
}

function getRowStatusStyle(status, fields) {
  const watchFolder = fields?.['Send To Watch Folder'];
  const builtAtVenue = fields?.['Built at Venue'];
  
  if (builtAtVenue) return { background: 'rgba(52, 211, 153, 0.12)', borderLeft: '4px solid #34d399' };
  if (status === 'Rejected') return { background: 'rgba(239, 68, 68, 0.10)', borderLeft: '4px solid #f87171' };
  if (status === 'Content Unlocked') return { background: 'rgba(192, 132, 252, 0.08)', borderLeft: '4px solid #c084fc' };
  if (watchFolder) return { background: '#1e1e2a', borderLeft: '4px solid #60a5fa' };
  // Not yet checked — needs action (orange)
  return { background: 'rgba(251, 191, 36, 0.08)', borderLeft: '4px solid #fbbf24' };
}

// Opaque version of row background for sticky cells (can't be rgba or scrolled content shows through)
function getRowBgOpaque(status, fields) {
  const watchFolder = fields?.['Send To Watch Folder'];
  const builtAtVenue = fields?.['Built at Venue'];
  
  if (builtAtVenue) return '#101f19';
  if (status === 'Rejected') return '#1c1012';
  if (status === 'Content Unlocked') return '#16101e';
  if (watchFolder) return '#1e1e2a';
  return '#181610';
}

// ================= SUB-COMPONENTS =================

// Status dropdown selector (excludes Example Content - that's Match Builder only)
// ─── Pipeline Display ─── 
// 2-step visual pipeline: Ingested → Built
// Shows ingest and build status for each content row
function PipelineDisplay({ status, watchFolder, builtAtVenue, isIngested, isArchived }) {
  const isRejected = status === 'Rejected';
  const isChanged = status === 'Content Unlocked';
  
  const isBuilt = !!builtAtVenue;
  const isSent = !!watchFolder && !isBuilt;
  
  if (isArchived) {
    return (
      <span style={{ fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 5, color: '#f59e0b', background: 'rgba(245,158,11,0.2)', border: '1px solid rgba(245,158,11,0.5)', lineHeight: 1.4, display: 'inline-block', whiteSpace: 'normal' }}>
        ⚠ Archived
      </span>
    );
  }
  
  if (isRejected) {
    return (
      <span style={{ fontSize: 10, fontWeight: 700, padding: '4px 12px', borderRadius: 5, color: '#f87171', background: 'rgba(248,113,113,0.25)', border: '1px solid rgba(248,113,113,0.55)', lineHeight: 1.4, display: 'inline-block' }}>
        Rejected — do not build
      </span>
    );
  }
  if (isChanged) {
    return (
      <span style={{ fontSize: 10, fontWeight: 700, padding: '4px 12px', borderRadius: 5, color: '#c084fc', background: 'rgba(192,132,252,0.25)', border: '1px solid rgba(192,132,252,0.55)', lineHeight: 1.4, display: 'inline-block' }}>
        Content Changed — re-ingest
      </span>
    );
  }

  // Single badge — shows current state only, no grey "Built" placeholder
  if (isBuilt) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 10, background: 'rgba(52,211,153,0.18)' }}>
        <div style={{ width: 16, height: 16, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, border: '2px solid #34d399', color: '#34d399', background: 'rgba(52,211,153,0.22)' }}>✓</div>
        <span style={{ fontSize: 10, fontWeight: 600, color: '#34d399' }}>Built</span>
      </div>
    );
  }

  if (isIngested) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 10, background: 'rgba(52,211,153,0.18)' }}>
        <div style={{ width: 16, height: 16, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, border: '2px solid #34d399', color: '#34d399', background: 'rgba(52,211,153,0.22)' }}>✓</div>
        <span style={{ fontSize: 10, fontWeight: 600, color: '#34d399' }}>Ingested</span>
      </div>
    );
  }

  if (isSent) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 10, background: 'rgba(251,191,36,0.14)' }}>
        <div style={{ width: 16, height: 16, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, border: '2px solid #fbbf24', color: '#fbbf24' }}>◌</div>
        <span style={{ fontSize: 10, fontWeight: 600, color: '#fbbf24' }}>Ingesting...</span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 10, background: 'rgba(249,115,22,0.14)' }}>
      <div style={{ width: 16, height: 16, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, border: '2px solid #f97316', color: '#f97316' }}>!</div>
      <span style={{ fontSize: 10, fontWeight: 600, color: '#f97316' }}>Not Ingested</span>
    </div>
  );
}

// ─── Row Action Button ───
// If not ingested (red dot): show "Ingest Content" button
// If ingested (green dot) but not built: show "QA & Built by Operator" checkbox
// If built: show "✓ Built by Operator" with timestamp
function RowActionButton({ status, watchFolder, builtAtVenue, builtAtVenueTime, isPending, qaCheckedAt, isIngested, isArchived, onCheck, onBuild, onRecheck }) {
  const isRejected = status === 'Rejected';
  const isChanged = status === 'Content Unlocked';
  
  // Archived overrides everything — content needs replacement
  if (isArchived) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <span style={{ 
          fontSize: 10, fontWeight: 700, padding: '6px 10px', borderRadius: 8,
          background: 'rgba(245,158,11,0.15)', color: '#f59e0b',
          border: '1px solid rgba(245,158,11,0.4)',
          whiteSpace: 'nowrap', textAlign: 'center',
        }}>⚠ Do Not Build</span>
        <span style={{ fontSize: 7, color: '#f59e0b', fontWeight: 600, textAlign: 'center', lineHeight: 1.3 }}>
          Archived — replace in MatchFlow
        </span>
      </div>
    );
  }
  
  // Rejected and Changed override everything
  if (isRejected) {
    return <span style={{ fontSize: 12, fontWeight: 700, color: '#f87171' }}>Rejected</span>;
  }

  if (isChanged) {
    return (
      <button onClick={onRecheck} disabled={isPending}
        title="Re-send updated content to watch folder for re-ingestion"
        style={{
          fontSize: 12, fontWeight: 700, padding: '8px 14px', borderRadius: 8,
          border: '1px solid #c084fc', cursor: isPending ? 'wait' : 'pointer',
          background: 'rgba(192,132,252,0.18)', color: '#c084fc',
          whiteSpace: 'nowrap', minWidth: 130, textAlign: 'center',
          opacity: isPending ? 0.6 : 1,
        }}
      >Re-ingest Content</button>
    );
  }

  const isBuilt = !!builtAtVenue;

  // Already built — show completed state
  if (isBuilt) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#34d399' }}>✓ Built by Operator</span>
        {builtAtVenueTime && (
          <span style={{ fontSize: 8, color: '#34d399', fontFamily: 'JetBrains Mono, monospace', background: 'rgba(52,211,153,0.15)', padding: '2px 6px', borderRadius: 3 }}>
            {new Date(builtAtVenueTime).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>
    );
  }

  // Content is ingested (green dot) — skip ingest, show QA & Built button
  if (isIngested && !isBuilt) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
        <button onClick={onBuild} disabled={isPending}
          title="Confirm content has been QA'd and built in XPression at venue"
          style={{
            fontSize: 12, fontWeight: 700, padding: '8px 14px', borderRadius: 8,
            border: 'none', cursor: isPending ? 'wait' : 'pointer',
            background: '#2563eb', color: '#fff',
            whiteSpace: 'nowrap', minWidth: 130, textAlign: 'center',
            boxShadow: '0 2px 8px rgba(37,99,235,0.4)',
            opacity: isPending ? 0.6 : 1,
          }}
        >QA & Built by Operator</button>
      </div>
    );
  }

  // Content sent to watch folder but not yet ingested (red dot still) — show waiting state
  if (!!watchFolder && !isIngested && !isBuilt) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#fbbf24' }}>◌ Ingesting...</span>
        {qaCheckedAt && (
          <span style={{
            fontSize: 8, color: '#60a5fa', fontFamily: 'JetBrains Mono, monospace',
            background: 'rgba(96,165,250,0.12)', padding: '2px 6px', borderRadius: 3,
          }}>
            Sent {new Date(qaCheckedAt).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>
    );
  }

  // Not sent, not ingested — show Ingest Content button
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
      <button onClick={onCheck} disabled={isPending}
        title="Send content to encoder watch folder for ingestion"
        style={{
          fontSize: 12, fontWeight: 700, padding: '8px 14px', borderRadius: 8,
          border: 'none', cursor: isPending ? 'wait' : 'pointer',
          background: '#b45309', color: '#fff', whiteSpace: 'nowrap',
          boxShadow: '0 2px 8px rgba(180,83,9,0.4)',
          minWidth: 130, textAlign: 'center',
          opacity: isPending ? 0.6 : 1,
        }}
      >Ingest Content</button>
    </div>
  );
}

// ─── Row Overflow Menu ───
// Edge cases: Reject, Unlock — not in the main flow
function RowOverflowMenu({ status, watchFolder, builtAtVenue, isPending, comments, isDuplicate, isStale, onAction, onSaveComment }) {
  const [open, setOpen] = React.useState(false);
  const [showCommentEditor, setShowCommentEditor] = React.useState(false);
  const [commentDraft, setCommentDraft] = React.useState(comments || '');
  const btnRef = React.useRef(null);
  const [menuPos, setMenuPos] = React.useState({ top: 0, right: 0 });
  const actions = [];
  const isBuilt = !!builtAtVenue;
  const isSent = !!watchFolder;

  if (isBuilt || isSent)
    actions.push({ key: 'unlock', label: '🔓 Unlock — return to review', color: '#c084fc' });
  if (isSent || isBuilt)
    actions.push({ key: 'forceResend', label: '↻ Force resend to watch folder', color: '#60a5fa', hint: 'Clears ingest flag and re-triggers delivery — use if encoding failed at venue' });
  if (status !== 'Rejected' && !isBuilt)
    actions.push({ key: 'reject', label: '❗ Reject — doesn\'t meet spec', color: '#f87171' });
  if (status === 'Rejected')
    actions.push({ key: 'unreject', label: '↩ Return to review — undo rejection', color: '#fbbf24' });
  if (isDuplicate)
    actions.push({ key: 'deleteDuplicate', label: '🗑️ Delete duplicate row', color: '#ef4444', hint: 'This row is a duplicate — safe to remove' });
  if (isStale)
    actions.push({ key: 'deleteStale', label: '🗑️ Remove (deleted by club)', color: '#ef4444', hint: 'The source row was deleted from MatchFlow by the club — removing this operator row will clear it from the view' });

  const handleOpen = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setCommentDraft(comments || '');
    setShowCommentEditor(false);
    setOpen(!open);
  };

  const handleSaveComment = () => {
    onSaveComment(commentDraft);
    setShowCommentEditor(false);
    setOpen(false);
  };

  const hasComments = !!(comments && comments.trim());
  const isRejected = status === 'Rejected';

  return (
    <div style={{ position: 'relative' }}>
      <button ref={btnRef} onClick={handleOpen} disabled={isPending}
        style={{
          background: isRejected ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)',
          border: isRejected ? '1px solid rgba(239,68,68,0.4)' : '1px solid rgba(34,197,94,0.4)',
          cursor: 'pointer',
          color: isRejected ? '#f87171' : '#22c55e',
          fontSize: 10, fontWeight: 700, padding: '5px 10px', borderRadius: 5,
          position: 'relative', whiteSpace: 'nowrap',
          transition: 'all 0.15s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = isRejected ? 'rgba(239,68,68,0.25)' : 'rgba(34,197,94,0.25)';
          e.currentTarget.style.color = isRejected ? '#fca5a5' : '#4ade80';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = isRejected ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)';
          e.currentTarget.style.color = isRejected ? '#f87171' : '#22c55e';
        }}
      >
        Actions
        {(hasComments || isRejected) && (
          <span style={{ marginLeft: 4, fontSize: 10, letterSpacing: 1 }}>
            {isRejected && '❗'}{hasComments && '💬'}
          </span>
        )}
      </button>
      {open && (<>
        <div style={{ position: 'fixed', inset: 0, zIndex: 10000 }} onClick={() => setOpen(false)} />
        <div style={{
          position: 'fixed', top: Math.min(menuPos.top, window.innerHeight - 400), right: Math.max(menuPos.right, 8),
          background: '#16161f', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 12,
          padding: 6, zIndex: 10001, width: 420,
          boxShadow: '0 12px 32px rgba(0,0,0,0.7)',
          maxHeight: 'calc(100vh - 80px)', overflowY: 'auto',
        }}>
          {/* Rejected status banner */}
          {isRejected && (
            <div style={{
              padding: '10px 12px', margin: '4px',
              background: 'rgba(239,68,68,0.12)', borderRadius: 8,
              border: '1px solid rgba(239,68,68,0.3)',
              display: 'flex', alignItems: 'flex-start', gap: 8,
            }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>❗</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#f87171', whiteSpace: 'normal' }}>Content Rejected</div>
                <div style={{ fontSize: 10, color: '#fca5a5', marginTop: 2, lineHeight: 1.4, wordBreak: 'break-word', whiteSpace: 'normal' }}>
                  This asset has been rejected. Use "Return to review" below if this was an error.
                </div>
              </div>
            </div>
          )}

          {/* Comment section */}
          {!showCommentEditor ? (
            <button onClick={() => setShowCommentEditor(true)}
              style={{
                display: 'flex', width: '100%', textAlign: 'left', alignItems: 'flex-start', gap: 10,
                background: 'none', border: 'none', padding: '12px 14px',
                fontSize: 13, color: '#60a5fa', cursor: 'pointer', borderRadius: 6, fontWeight: 600,
                boxSizing: 'border-box',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#1e1e2a'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
            >
              <span style={{ flexShrink: 0, fontSize: 14, marginTop: 1 }}>💬</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                {hasComments ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: 10, color: '#8a8a96', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Lumen8 Comment</span>
                    <div style={{
                      fontSize: 12, color: '#e4e4e7', fontWeight: 400, lineHeight: 1.5,
                      padding: '8px 10px', background: 'rgba(96,165,250,0.08)',
                      borderRadius: 6, borderLeft: '3px solid #60a5fa',
                      wordBreak: 'break-word', overflowWrap: 'break-word',
                      whiteSpace: 'pre-wrap',
                    }}>{comments}</div>
                    <span style={{ fontSize: 10, color: '#60a5fa', fontWeight: 600, marginTop: 1 }}>Click to edit</span>
                  </div>
                ) : 'Add Lumen8 comment'}
              </div>
            </button>
          ) : (
            <div style={{ padding: '12px 14px', boxSizing: 'border-box' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#8a8a96', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                💬 Lumen8 Comment
              </div>
              <textarea
                autoFocus
                value={commentDraft}
                onChange={(e) => setCommentDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveComment(); } if (e.key === 'Escape') { setShowCommentEditor(false); } }}
                placeholder="Add feedback for the club or operator..."
                rows={4}
                style={{
                  width: '100%', minHeight: 90, background: '#0a0a0f',
                  border: '1px solid rgba(96,165,250,0.4)', borderRadius: 6,
                  color: '#fff', fontSize: 12, padding: '10px 12px', resize: 'vertical',
                  fontFamily: 'inherit', outline: 'none', lineHeight: 1.6,
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <button onClick={handleSaveComment} style={{
                  fontSize: 11, fontWeight: 700, padding: '6px 16px', borderRadius: 5,
                  border: 'none', cursor: 'pointer', background: '#2fd6b5', color: '#000',
                }}>Save</button>
                <button onClick={() => setShowCommentEditor(false)} style={{
                  fontSize: 11, fontWeight: 600, padding: '6px 16px', borderRadius: 5,
                  border: '1px solid rgba(255,255,255,0.10)', cursor: 'pointer', background: 'none', color: '#c4c4cc',
                }}>Cancel</button>
                {hasComments && (
                  <button onClick={() => { onSaveComment(''); setCommentDraft(''); setShowCommentEditor(false); setOpen(false); }} style={{
                    fontSize: 11, fontWeight: 600, padding: '6px 16px', borderRadius: 5,
                    border: '1px solid rgba(239,68,68,0.3)', cursor: 'pointer', background: 'none', color: '#f87171',
                    marginLeft: 'auto',
                  }}>Clear Comment</button>
                )}
              </div>
            </div>
          )}

          {/* Divider */}
          {actions.length > 0 && (
            <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '4px 0' }} />
          )}

          {/* Status actions — rejected return-to-review is prominent */}
          {actions.map(a => (
            <button key={a.key} onClick={() => { onAction(a.key); setOpen(false); }}
              style={{
                display: 'flex', width: '100%', textAlign: 'left', alignItems: 'center', gap: 8,
                background: a.key === 'unreject' ? 'rgba(251,191,36,0.08)' : 'none',
                border: a.key === 'unreject' ? '1px solid rgba(251,191,36,0.25)' : 'none',
                padding: a.key === 'unreject' ? '12px 14px' : '10px 14px',
                margin: a.key === 'unreject' ? '2px 4px' : 0,
                fontSize: 13, color: a.color, cursor: 'pointer', borderRadius: 6, fontWeight: 600,
                boxSizing: 'border-box',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = a.key === 'unreject' ? 'rgba(251,191,36,0.15)' : '#1e1e2a'}
              onMouseLeave={(e) => e.currentTarget.style.background = a.key === 'unreject' ? 'rgba(251,191,36,0.08)' : 'none'}
            >{a.label}</button>
          ))}
        </div>
      </>)}
    </div>
  );
}

function EditableInput({ value, onChange, disabled, placeholder = '', style = {} }) {
  const [localValue, setLocalValue] = useState(value || '');
  const [isEditing, setIsEditing] = useState(false);
  
  const handleBlur = () => {
    setIsEditing(false);
    if (localValue !== value) {
      onChange(localValue);
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
    if (e.key === 'Escape') {
      setLocalValue(value || '');
      setIsEditing(false);
    }
  };
  
  // Sync local value when prop changes
  React.useEffect(() => {
    if (!isEditing) {
      setLocalValue(value || '');
    }
  }, [value, isEditing]);
  
  return (
    <input
      type="text"
      style={{ ...styles.inlineInput, ...style }}
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onFocus={() => setIsEditing(true)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      placeholder={placeholder}
    />
  );
}

// Copyable Lumen8 ID display (STK_123456, CAR_789012, etc)
function Lumen8IdDisplay({ contentName }) {
  const [copied, setCopied] = React.useState(false);

  if (!contentName) {
    return <span style={{ color: THEME.textMuted, fontSize: '12px' }}>-</span>;
  }

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(contentName);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = contentName;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div
      onClick={handleCopy}
      style={{
        fontSize: '12px',
        fontWeight: 500,
        color: copied ? '#34d399' : THEME.textPrimary,
        fontFamily: 'JetBrains Mono, monospace',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px 6px',
        borderRadius: '4px',
        border: `1px solid ${copied ? 'rgba(52, 211, 153, 0.4)' : 'transparent'}`,
        background: copied ? 'rgba(52, 211, 153, 0.1)' : 'transparent',
        transition: 'all 0.15s',
      }}
      title={`Click to copy: ${contentName}`}
      onMouseEnter={(e) => { if (!copied) { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.border = '1px solid rgba(255,255,255,0.1)'; } }}
      onMouseLeave={(e) => { if (!copied) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.border = '1px solid transparent'; } }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{contentName}</span>
      <span style={{ fontSize: '11px', flexShrink: 0, opacity: copied ? 1 : 0.5 }}>
        {copied ? '✓' : '⎘'}
      </span>
    </div>
  );
}

// Display for content type (Asset Destination)
function ContentTypeDisplay({ assetDestination }) {
  if (!assetDestination) {
    return <span style={{ color: THEME.textMuted, fontSize: '12px' }}>-</span>;
  }
  
  return (
    <div 
      style={{ 
        fontSize: '12px', 
        fontWeight: 500,
        color: THEME.textPrimary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        background: 'rgba(47, 214, 181, 0.08)',
        padding: '2px 6px',
        borderRadius: 3,
        border: `1px solid ${THEME.accent}22`,
      }} 
      title={assetDestination}
    >
      {assetDestination}
    </div>
  );
}

// Checkbox-style display for boolean fields (read-only)
function BooleanDisplay({ value, label }) {
  const isTrue = value === true || value === 'checked' || value === 'true' || value === '✓';
  
  return (
    <div style={{ textAlign: 'center' }}>
      {isTrue ? (
        <span style={{ color: THEME.accent, fontSize: '16px', fontWeight: 700 }}>✓</span>
      ) : (
        <span style={{ color: THEME.textMuted, fontSize: '13px' }}>-</span>
      )}
    </div>
  );
}

// Ingest status indicator (green if ingested, red if not) - only for CG/Ribbon
// Tooltip wrapper component - uses title attribute for simplicity and stability
function SyncStatusTooltip({ syncStatus }) {
  const [expanded, setExpanded] = React.useState(false);
  const items = syncStatus.missingItems || [];
  const previewCount = 2;
  const hasMore = items.length > previewCount;
  const displayItems = expanded ? items : items.slice(0, previewCount);

  return (
    <div style={{
      position: 'absolute', top: '100%', left: 0, marginTop: 5,
      padding: '8px 12px', borderRadius: 8,
      background: '#1a1a1d', border: '1px solid #f59e0b44',
      fontSize: 10, color: '#f59e0b', fontWeight: 600,
      width: expanded ? 320 : 220, zIndex: 9999,
      boxShadow: '0 8px 32px rgba(0,0,0,0.6), 0 2px 8px rgba(0,0,0,0.4)',
    }}>
      ⚠ {syncStatus.missing} asset{syncStatus.missing !== 1 ? 's' : ''} not synced to operator view
      {items.length > 0 && (
        <div style={{
          marginTop: 4, fontWeight: 400, fontSize: 9, color: '#a0a0b0', lineHeight: 1.5,
          ...(expanded ? { maxHeight: 200, overflowY: 'auto', paddingRight: 4 } : {}),
        }}>
          {displayItems.map((item, i) => (
            <div key={i} style={{ padding: '2px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              {item.name} ({item.category}){item.where ? ` [${item.where}]` : ''}
            </div>
          ))}
        </div>
      )}
      {hasMore && (
        <div
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          style={{
            marginTop: 4, fontSize: 9, fontWeight: 500,
            color: '#f59e0b', cursor: 'pointer',
            textDecoration: 'underline', textDecorationStyle: 'dotted',
          }}
        >
          {expanded ? '▴ Show less' : `▾ Show all ${items.length} items`}
        </div>
      )}
      <div style={{ marginTop: 4, fontSize: 9, color: '#f59e0b80', fontWeight: 500 }}>
        Hit Sync to pull them in ↑
      </div>
    </div>
  );
}

function Tooltip({ children, text, position = 'top' }) {
  // Use native title for simple tooltips to avoid jittering
  return (
    <span title={text} style={{ display: 'inline-flex', alignItems: 'center' }}>
      {children}
    </span>
  );
}

// Help icon for column headers - uses native title attribute
function HeaderHelp({ tooltip }) {
  return (
    <span 
      title={tooltip}
      style={{ 
        marginLeft: 4, 
        cursor: 'help',
        opacity: 0.5,
        fontSize: 10,
      }}
    >
      ⓘ
    </span>
  );
}

function IngestStatusDisplay({ marvelIngest, mcgIngest, venue, destinationVenue, contentType, watchFolder }) {
  // Safely convert venue to string (could be array from Airtable)
  const safeString = (val) => {
    if (!val) return '';
    if (typeof val === 'string') return val;
    if (Array.isArray(val)) return val[0] || '';
    return String(val);
  };
  
  // Use venue if available, otherwise fallback to destinationVenue
  const venueStr = safeString(venue);
  const destVenueStr = safeString(destinationVenue);
  const effectiveVenue = venueStr || destVenueStr;
  const venueLower = effectiveVenue.toLowerCase();
  
  // Determine which ingest field to check based on venue (using contains)
  const isMarvelVenue = venueLower.includes('marvel') || venueLower.includes('mrv');
  const isMcgVenue = venueLower.includes('mcg') || venueLower.includes('melbourne cricket');
  
  // Check if the relevant ingest field is true
  // For IPTV: watchFolder = ingested (no venue ingest flag needed)
  let isIngested = false;
  if (contentType === 'iptv') {
    isIngested = !!watchFolder;
  } else if (isMarvelVenue) {
    isIngested = marvelIngest === true || marvelIngest === 'checked' || marvelIngest === 'true' || marvelIngest === '✓';
  } else if (isMcgVenue) {
    isIngested = mcgIngest === true || mcgIngest === 'checked' || mcgIngest === 'true' || mcgIngest === '✓';
  }
  
  // Build tooltip
  const venueName = isMarvelVenue ? 'Marvel' : (isMcgVenue ? 'MCG' : 'Unknown');
  
  return (
    <Tooltip 
      text={
        isIngested 
          ? `✓ Content has been ingested to ${venueName} encoder` 
          : `✗ Content not yet ingested to ${venueName} encoder`
      } 
      position="left"
    >
      <div style={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        cursor: 'help',
        width: '100%',
      }}>
        <div style={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          background: isIngested ? '#22c55e' : '#ef4444',
          boxShadow: isIngested ? '0 0 6px rgba(34, 197, 94, 0.5)' : '0 0 6px rgba(239, 68, 68, 0.5)',
        }} />
        <span style={{
          fontSize: 7, fontWeight: 700, letterSpacing: '0.3px',
          color: isIngested ? '#22c55e' : '#ef4444',
          textTransform: 'uppercase',
        }}>
          {isIngested ? 'sent' : 'not sent'}
        </span>
      </div>
    </Tooltip>
  );
}

// Thumbnail display for content preview - clickable to open preview modal
function ThumbnailDisplay({ url, alt, onClick, hasPreview }) {
  if (!url) {
    return (
      <div style={{
        width: '48px',
        height: '32px',
        borderRadius: '4px',
        background: THEME.bgHover,
        border: `1px solid ${THEME.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: THEME.textMuted,
        fontSize: '10px',
      }}>
        -
      </div>
    );
  }
  
  return (
    <div
      onClick={onClick}
      style={{
        cursor: hasPreview ? 'pointer' : 'default',
        position: 'relative',
      }}
      title={hasPreview ? 'Click to preview' : ''}
    >
      <img
        src={url}
        alt={alt || 'Thumbnail'}
        style={{
          width: '48px',
          height: '32px',
          borderRadius: '4px',
          objectFit: 'cover',
          background: THEME.bgHover,
          border: `1px solid ${THEME.border}`,
          transition: 'all 0.15s ease',
        }}
      />
      {hasPreview && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.4)',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0,
          transition: 'opacity 0.15s ease',
        }}
        onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
        onMouseLeave={(e) => e.currentTarget.style.opacity = 0}
        >
          <span style={{ color: '#fff', fontSize: '16px' }}>▶</span>
        </div>
      )}
    </div>
  );
}

// Duration display - formats to Xm:XXs
function DurationDisplay({ duration }) {
  if (!duration) {
    return <span style={{ color: THEME.textMuted, fontSize: '12px' }}>-</span>;
  }
  
  // Parse duration and convert to minutes:seconds format
  const formatDuration = (dur) => {
    // If already in desired format, return as-is
    if (typeof dur === 'string' && dur.includes('m')) return dur;
    
    // Try to parse as seconds (number or string)
    let totalSeconds = 0;
    
    if (typeof dur === 'number') {
      totalSeconds = dur;
    } else if (typeof dur === 'string') {
      // Try parsing as HH:MM:SS or MM:SS or just seconds
      const parts = dur.split(':').map(p => parseFloat(p.trim()));
      if (parts.length === 3) {
        // HH:MM:SS
        totalSeconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
      } else if (parts.length === 2) {
        // MM:SS
        totalSeconds = parts[0] * 60 + parts[1];
      } else if (parts.length === 1 && !isNaN(parts[0])) {
        // Just seconds
        totalSeconds = parts[0];
      } else {
        // Can't parse, return original
        return dur;
      }
    }
    
    if (isNaN(totalSeconds) || totalSeconds === 0) return dur;
    
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.round(totalSeconds % 60);
    
    return `${minutes}m:${seconds.toString().padStart(2, '0')}s`;
  };
  
  return (
    <span style={{ 
      fontSize: '12px', 
      fontWeight: 500,
      color: THEME.textPrimary,
      fontFamily: 'JetBrains Mono, monospace',
    }}>
      {formatDuration(duration)}
    </span>
  );
}

function DropboxLink({ url, isIptv = false }) {
  // For IPTV: show folder link only
  if (isIptv) {
    return (
      <Tooltip text="Open IPTV exports folder in Dropbox" position="left">
        <a
          href={IPTV_DROPBOX_FOLDER}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            color: THEME.accent,
            textDecoration: 'none',
            fontSize: '12px',
            padding: '3px 6px',
            borderRadius: '4px',
            background: 'rgba(47, 214, 181, 0.1)',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(47, 214, 181, 0.2)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(47, 214, 181, 0.1)'}
        >
          {Icons.link}
          <span>Folder</span>
        </a>
      </Tooltip>
    );
  }
  
  // Non-IPTV: original behavior
  if (!url) {
    return <span style={{ color: THEME.textMuted, fontSize: '12px' }}>-</span>;
  }
  
  return (
    <Tooltip text="Click to open content location in Dropbox" position="left">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          color: THEME.accent,
          textDecoration: 'none',
          fontSize: '12px',
          fontWeight: 500,
          padding: '4px 8px',
          borderRadius: '4px',
          background: 'rgba(47, 214, 181, 0.1)',
          transition: 'all 0.15s ease',
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(47, 214, 181, 0.2)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(47, 214, 181, 0.1)'}
      >
        {Icons.link}
        <span>View</span>
      </a>
    </Tooltip>
  );
}

// Format last modified date/time
function formatLastModified(isoString) {
  if (!isoString) return null;
  
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return null;
    
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    // Format time as HH:MM
    const timeStr = date.toLocaleTimeString('en-AU', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
    
    // Format date as DD/MM
    const dateStr = date.toLocaleDateString('en-AU', { 
      day: '2-digit', 
      month: '2-digit'
    });
    
    // Relative time for recent edits
    if (diffMins < 60) {
      return { relative: `${diffMins}m ago`, full: `${dateStr} ${timeStr}` };
    } else if (diffHours < 24) {
      return { relative: `${diffHours}h ago`, full: `${dateStr} ${timeStr}` };
    } else if (diffDays < 7) {
      return { relative: `${diffDays}d ago`, full: `${dateStr} ${timeStr}` };
    } else {
      return { relative: dateStr, full: `${dateStr} ${timeStr}` };
    }
  } catch (e) {
    return null;
  }
}

function LastModifiedCell({ value }) {
  const formatted = formatLastModified(value);
  
  if (!formatted) {
    return <span style={{ color: THEME.textMuted, fontSize: '11px' }}>—</span>;
  }
  
  const date = new Date(value);
  const isRecent = (Date.now() - date.getTime()) < 86400000;
  const isToday = new Date().toDateString() === date.toDateString();
  
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <span 
        style={{ 
          fontSize: '11px', 
          color: isRecent ? '#34d399' : THEME.textSecondary,
          fontWeight: isRecent ? 600 : 400,
        }}
        title={formatted.full}
      >
        {date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })}
      </span>
      <span style={{
        fontSize: '9px', fontFamily: 'JetBrains Mono, monospace',
        color: isToday ? '#60a5fa' : '#62626e',
      }}>
        {date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true })}
      </span>
    </div>
  );
}

function OperatorRow({ 
  record, 
  isSelected,
  isRecentlyUpdated,
  isDuplicate,
  isStale,
  onSelect,
  onUpdateStatus, 
  onUpdateComments,
  onUpdateTakeId,
  onToggleWatchFolder,
  onForceResend,
  onToggleBuiltAtVenue,
  onUnlock,
  actionedBy,
  onDelete,
  onEdit,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
  isDragging,
  contentType, // Added to determine which columns to show
  stabilityState, // 'ready' | 'settling' | null
  isManualCategory, // true for operator-managed categories (allows delete)
  onOpenPreview, // callback to open tiled/360/standard preview at dashboard level
}) {
  const [isHovered, setIsHovered] = useState(false);
  const { pendingUpdates } = useOperator();
  
  // Presence tracking
  const { isRowBeingEdited, setEditingRow, clearEditingRow } = usePresence();
  const editingUser = isRowBeingEdited(record.id);
  const [isEditingLocally, setIsEditingLocally] = useState(false);
  
  // Track when we start/stop editing
  React.useEffect(() => {
    if (isEditingLocally) {
      setEditingRow(record.id);
    } else {
      clearEditingRow();
    }
  }, [isEditingLocally, record.id, setEditingRow, clearEditingRow]);
  
  const isPending = pendingUpdates.has(record.id);
  const fields = record.fields || {};
  
  // Map status from MatchFlow Builder to Operator View
  // "New Submission" (club portal) -> "Ready for Testing" (operator view)
  const rawStatus = fields['Content Status'] || '';
  const status = rawStatus === 'New Submission' ? 'Ready for Testing' : (rawStatus || 'Ready for Testing');
  const comments = fields['Lumen8 Comments'];
  const watchFolder = fields['Send To Watch Folder'];
  const name = fields['Name'] || 'Unnamed';
  const takeId = fields['Take ID'] || '';
  const venue = Array.isArray(fields['Venue']) ? fields['Venue'][0] : (fields['Venue'] || '');
  const destinationVenue = fields['Destination Venue'] || '';
  const clubInstructions = fields['Club Instructions'] || '';
  const dropboxUrl = fields['Dropbox URL'] || '';
  const downloadUrl = fields['_downloadUrl'] || '';
  const assetMissing = fields['_assetMissing'] || false;
  const isArchived = fields['_isArchived'] || false;
  
  // Determine if Take ID should be shown (only for CG and Ribbon)
  const showTakeId = contentType === 'cg' || contentType === 'ribbon';
  
  // Get content field based on content type
  const getContentField = () => {
    switch (contentType) {
      case 'tvc':
        // TVC Content - show Big Screen TVC or Corner Screen TVC
        return fields['Big Screen - TVC'] || fields['Corner Screen - TVC'] || '';
      case 'iptv':
        // IPTV - show IPTV GFX
        return fields['IPTV GFX'] || '';
      case 'led':
        // LED Entitlements - show LED All Types
        return fields['LED - All Types'] || '';
      case 'ribbon':
        // Ribbon - show Ribbon Content
        return fields['Ribbon Content'] || '';
      case 'cg':
      default:
        // CG - show CG Content
        return fields['CG Content'] || '';
    }
  };
  
  // Asset metadata fields (lookups from Assets table in CG Operator View)
  // Lumen8 ID is a lookup field from CG Content → Lumen8 ID in Assets table
  // Falls back to CG Content if lookup doesn't exist (will show AFL ID)
  const lumen8IdLookup = fields['Lumen8 ID'] || '';
  const contentFieldValue = getContentField();
  const lumen8Id = lumen8IdLookup || 
                   (typeof contentFieldValue === 'string' ? contentFieldValue : 
                   (Array.isArray(contentFieldValue) ? contentFieldValue[0] : ''));
  
  // These all require lookup fields to be created in CG Operator View
  // Or enrichment from operatorAirtable for shared tables
  const assetDestination = fields['Asset Destination'] || '';
  const hasAudio = fields['Has Audio'];
  const hasAlpha = fields['Has Alpha'];
  const duration = fields['Duration'] || '';
  const thumbnailUrl = fields['_thumbnailUrl'] || '';
  const previewUrl = fields['_previewUrl'] || '';
  const assetName = fields['_assetName'] || name;
  
  // Ingest status fields (for CG/Ribbon only)
  const marvelIngest = fields['_marvelIngest'] || false;
  const mcgIngest = fields['_mcgIngest'] || false;
  
  // Compute isIngested based on venue (mirrors IngestStatusDisplay logic)
  // For IPTV: Send To Watch Folder = ingested (no XPression build step)
  const effectiveVenue = (venue || destinationVenue || '').toLowerCase();
  const rawIsIngested = contentType === 'iptv'
    ? !!watchFolder  // IPTV: watch folder IS the final delivery — no venue ingest flag needed
    : effectiveVenue.includes('marvel') || effectiveVenue.includes('mrv')
      ? (marvelIngest === true || marvelIngest === 'checked' || marvelIngest === 'true' || marvelIngest === '✓')
      : effectiveVenue.includes('mcg') || effectiveVenue.includes('melbourne cricket')
        ? (mcgIngest === true || mcgIngest === 'checked' || mcgIngest === 'true' || mcgIngest === '✓')
        : false;
  
  // Built at Venue fields (for CG/Ribbon only)
  const rawBuiltAtVenue = fields['Built at Venue'] || false;
  const builtAtVenueTime = fields['Built at Venue Time'] || '';
  
  // ARCHIVED OVERRIDE: If content is archived, visually reset to initial state
  // Don't show as built or ingested — treat as needing attention
  const isIngested = isArchived ? false : rawIsIngested;
  const builtAtVenue = isArchived ? false : rawBuiltAtVenue;
  
  // Last modified time (when content was last edited)
  const lastModified = fields['_lastModified'] || fields['Last Modified Time'] || fields['Last Modified'] || '';
  
  // Show ingest status only for CG and Ribbon
  const showIngestStatus = contentType === 'cg' || contentType === 'ribbon' || contentType === 'iptv';
  
  // IPTV-specific fields: Timing and Location
  const selectTiming = fields['Select Timing'] || [];
  const selectLocation = fields['Select Location'] || [];
  
  // Preview modal state — standard preview stays row-level (lightweight popup)
  const [showPreview, setShowPreview] = useState(false);
  
  // Ribbon 3D preview data — tiled and 360 assets with grouped level files
  const ribbonItems = fields['_ribbonItems'] || [];
  const destLower = (assetDestination || '').toLowerCase();
  const isTiledAsset = (contentType === 'ribbon' || contentType === 'cg') && destLower.includes('tiled') && ribbonItems.length > 0;
  const is360Asset = (contentType === 'ribbon' || contentType === 'cg') && destLower.includes('360') && ribbonItems.length > 0;
  
  // DEBUG: Log available fields for first record
  if (record.id && !window._operatorFieldsLogged) {
    console.log('[Operator] Content type:', contentType);
    console.log('[Operator] Available fields in record:', Object.keys(fields));
    console.log('[Operator] Lumen8 ID:', lumen8Id);
    console.log('[Operator] Thumbnail URL:', thumbnailUrl);
    console.log('[Operator] Marvel Ingest:', marvelIngest);
    console.log('[Operator] MCG Ingest:', mcgIngest);
    window._operatorFieldsLogged = true;
  }
  
  const rowBg = isHovered ? THEME.bgHover : getRowBgOpaque(status, fields);
  
  const rowStyle = {
    ...styles.row,
    ...getRowStatusStyle(status, fields),
    ...(isHovered ? { background: THEME.bgHover } : {}),
    ...(isDragging ? styles.rowDragging : {}),
    opacity: isPending ? 0.6 : 1,
    // Presence glow when someone else is editing
    ...(editingUser ? {
      boxShadow: `0 0 0 2px ${editingUser.color}50, 0 0 12px ${editingUser.color}30`,
      position: 'relative',
    } : {}),
    // "Removed by Club" highlight — applied AFTER hover/status styles so
    // the yellow tint always wins (operators need this visible at a glance).
    // Left-border stripe + subtle background tint; does not delete the row,
    // operator chooses whether to remove via the overflow menu action.
    // Guarded to not clobber the presence glow boxShadow when both fire.
    ...(isStale ? {
      background: isHovered
        ? 'rgba(234, 179, 8, 0.18)'
        : 'rgba(234, 179, 8, 0.09)',
      borderLeft: '3px solid #eab308',
    } : {}),
  };
  
  return (
    <tr
      style={rowStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsEditingLocally(true)}
      onBlur={(e) => {
        // Only clear if focus is leaving the row entirely
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setIsEditingLocally(false);
        }
      }}
      draggable
      onDragStart={(e) => onDragStart(e, record)}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDrop={(e) => onDrop(e, record)}
    >
      {/* Presence indicator when someone else is editing */}
      {editingUser && (
        <td style={{ 
          position: 'absolute', 
          top: -10, 
          right: 12, 
          background: editingUser.color,
          color: '#fff',
          fontSize: 10,
          fontWeight: 600,
          padding: '3px 10px',
          borderRadius: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          zIndex: 20,
          border: 'none',
        }}>
          {editingUser.avatar ? (
            <img 
              src={editingUser.avatar} 
              alt={editingUser.name}
              style={{ width: 16, height: 16, borderRadius: '50%', objectFit: 'cover' }}
            />
          ) : (
            <span style={{ 
              width: 16, 
              height: 16, 
              borderRadius: '50%', 
              background: 'rgba(255,255,255,0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 8,
            }}>
              {editingUser.name?.slice(0, 2).toUpperCase() || '?'}
            </span>
          )}
          <span>{editingUser.name?.split(' ')[0] || 'Someone'} editing</span>
        </td>
      )}
      
      
      {/* Drag Handle */}
      <td style={{ ...styles.td, width: COLUMN_WIDTHS.drag, textAlign: 'center', ...stickyStyle('drag', false, rowBg) }}>
        <span style={styles.dragHandle} title="Drag to reorder">
          {Icons.drag}
        </span>
      </td>
      
      {/* Name */}
      <td style={{ ...styles.td, width: contentType === 'iptv' ? '140px' : COLUMN_WIDTHS.name, fontWeight: 500, ...stickyStyle('name', false, rowBg) }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {stabilityState === 'returning' && !watchFolder && !builtAtVenue && (
            <span style={{
              fontSize: 8, fontWeight: 800, color: '#34d399',
              background: 'rgba(34, 197, 94, 0.12)',
              padding: '1px 5px', borderRadius: 3,
              textTransform: 'uppercase', letterSpacing: '0.3px',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              alignSelf: 'flex-start',
            }}>🔄 Returning</span>
          )}
          {isStale && (
            <span style={{
              fontSize: 8, fontWeight: 800, color: '#eab308',
              background: 'rgba(234, 179, 8, 0.12)',
              padding: '1px 5px', borderRadius: 3,
              textTransform: 'uppercase', letterSpacing: '0.3px',
              border: '1px solid rgba(234, 179, 8, 0.4)',
              alignSelf: 'flex-start',
            }}>⚠ Removed by Club</span>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', whiteSpace: 'normal', flex: 1, maxWidth: contentType === 'iptv' ? '120px' : '260px', lineHeight: 1.3 }} title={name}>
              {name}
            </span>
            {isRecentlyUpdated && (
              <span style={{
                fontSize: 8, fontWeight: 800, color: '#0ea5e9',
                background: 'rgba(14, 165, 233, 0.15)',
                padding: '1px 4px', borderRadius: 3,
                textTransform: 'uppercase', letterSpacing: '0.3px',
                flexShrink: 0,
              }}>Updated</span>
            )}
          </div>
        </div>
      </td>
      
      {/* Content Thumbnail - Click to preview */}
      <td style={{ ...styles.td, width: COLUMN_WIDTHS.thumbnail, ...stickyStyle('thumbnail', false, rowBg) }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          <ThumbnailDisplay 
            url={thumbnailUrl} 
            alt={assetName}
            hasPreview={!!(thumbnailUrl || previewUrl || dropboxUrl || isTiledAsset || is360Asset)}
            onClick={() => {
              if (isTiledAsset && onOpenPreview) {
                onOpenPreview({ type: 'tiled', asset: { name: assetName, lumen8Id }, ribbonItems });
              } else if (is360Asset && onOpenPreview) {
                onOpenPreview({ type: '360', asset: { name: assetName, lumen8Id }, ribbonItems });
              } else {
                setShowPreview(true);
              }
            }}
          />
        </div>
        {showPreview && ReactDOM.createPortal(
          <PreviewModal
            isOpen={showPreview}
            onClose={() => setShowPreview(false)}
            thumbnailUrl={thumbnailUrl}
            previewUrl={previewUrl}
            assetName={assetName}
            lumen8Id={lumen8Id}
            dropboxUrl={dropboxUrl}
          />,
          document.body
        )}
      </td>
      
      {/* Lumen8 ID (STK_123456, etc) */}
      <td style={{ ...styles.td, width: COLUMN_WIDTHS.lumen8Id, ...stickyStyle('lumen8Id', false, rowBg) }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Lumen8IdDisplay contentName={lumen8Id} />
          {assetMissing && (
            <span style={{
              fontSize: 9, fontWeight: 800, color: '#ef4444',
              background: 'rgba(239, 68, 68, 0.15)',
              padding: '1px 6px', borderRadius: 3,
              border: '1px solid rgba(239, 68, 68, 0.3)',
              textTransform: 'uppercase', letterSpacing: '0.3px',
              animation: 'pulse 2s infinite',
            }}>⚠ Asset Deleted</span>
          )}
          {isArchived && !assetMissing && (
            <span style={{
              fontSize: 9, fontWeight: 800, color: '#f59e0b',
              background: 'rgba(245, 158, 11, 0.15)',
              padding: '1px 6px', borderRadius: 3,
              border: '1px solid rgba(245, 158, 11, 0.3)',
              textTransform: 'uppercase', letterSpacing: '0.3px',
            }}>⚠ Archived</span>
          )}
        </div>
      </td>
      
      {/* Club Instructions (read-only, 2-line inline display) */}
      <td style={{ ...styles.td, width: '200px', verticalAlign: 'middle' }}>
        {clubInstructions ? (
          <ClubInstructionsInline value={clubInstructions} />
        ) : (
          <span style={{ fontSize: 11, color: THEME.textMuted }}>—</span>
        )}
      </td>
      
      {/* Content Type (Asset Destination) */}
      <td style={{ ...styles.td, width: COLUMN_WIDTHS.contentType }}>
        <ContentTypeDisplay assetDestination={assetDestination} />
      </td>
      
      {/* Has Audio */}
      <td style={{ ...styles.td, width: COLUMN_WIDTHS.hasAudio }}>
        <BooleanDisplay value={hasAudio} label="Audio" />
      </td>
      
      {/* Has Alpha - hidden for IPTV */}
      {contentType !== 'iptv' && (
        <td style={{ ...styles.td, width: COLUMN_WIDTHS.hasAlpha }}>
          <BooleanDisplay value={hasAlpha} label="Alpha" />
        </td>
      )}
      
      {/* Duration - hidden for IPTV */}
      {contentType !== 'iptv' && (
        <td style={{ ...styles.td, width: COLUMN_WIDTHS.duration }}>
          <DurationDisplay duration={duration} />
        </td>
      )}
      
      {/* Dropbox URL / IPTV Folder */}
      <td style={{ ...styles.td, width: COLUMN_WIDTHS.dropbox, textAlign: 'center' }}>
        <DropboxLink 
          url={dropboxUrl} 
          isIptv={contentType === 'iptv'} 
        />
      </td>
      
      {/* Pipeline Display — 2-step: Checked → Built */}
      <td style={{ ...styles.td, width: '120px' }}>
        <PipelineDisplay status={status} watchFolder={watchFolder} builtAtVenue={builtAtVenue} isIngested={isIngested} isArchived={isArchived} />
      </td>
      
      {/* Action Button — one per role, big and obvious */}
      <td style={{ ...styles.td, width: '170px', textAlign: 'center' }}>
        <RowActionButton
          status={status}
          watchFolder={watchFolder}
          builtAtVenue={builtAtVenue}
          builtAtVenueTime={builtAtVenueTime}
          isPending={isPending}
          qaCheckedAt={watchFolder && actionedBy?.get(record.id)?.time}
          isIngested={isIngested}
          isArchived={isArchived}
          onCheck={() => onToggleWatchFolder(record, true)}
          onBuild={() => onToggleBuiltAtVenue(record, true)}
          onRecheck={() => onToggleWatchFolder(record, true)}
        />
        {!isArchived && isIngested && !builtAtVenue && status !== 'Rejected' && status !== 'Content Unlocked' && (
          <div style={{ fontSize: 8, fontWeight: 700, color: '#34d399', marginTop: 3, letterSpacing: '0.3px' }}>
            ✓ AT VENUE — READY TO BUILD
          </div>
        )}
        {!isArchived && !isIngested && stabilityState === 'ready' && !watchFolder && !builtAtVenue && status !== 'Rejected' && (
          <div style={{ fontSize: 8, fontWeight: 700, color: '#ef4444', marginTop: 3, letterSpacing: '0.3px' }}>
            ● NEEDS INGESTING
          </div>
        )}
        {!isArchived && !isIngested && stabilityState === 'settling' && !watchFolder && !builtAtVenue && status !== 'Rejected' && (
          <div style={{ fontSize: 8, fontWeight: 600, color: '#fbbf24', marginTop: 3, letterSpacing: '0.3px' }}>
            ◌ Settling...
          </div>
        )}
      </td>
      
      {/* Ingest Status - Green/Red indicator (only for CG/Ribbon) */}
      {showIngestStatus && (
        <td style={{ ...styles.td, width: COLUMN_WIDTHS.ingestStatus, textAlign: 'center', verticalAlign: 'middle' }}>
          <IngestStatusDisplay 
            marvelIngest={marvelIngest} 
            mcgIngest={mcgIngest} 
            venue={venue}
            destinationVenue={destinationVenue}
            contentType={contentType}
            watchFolder={watchFolder}
          />
        </td>
      )}
      
      {/* More Menu — Comment, Reject, Unlock */}
      <td style={{ ...styles.td, width: '100px', textAlign: 'center' }}>
        <RowOverflowMenu
          status={status}
          watchFolder={watchFolder}
          builtAtVenue={builtAtVenue}
          isPending={isPending}
          comments={comments}
          isDuplicate={isDuplicate}
          isStale={isStale}
          onSaveComment={(val) => onUpdateComments(record, val)}
          onAction={(action) => {
            if (action === 'reject') onUpdateStatus(record, 'Rejected');
            else if (action === 'unlock') onUnlock(record);
            else if (action === 'unreject') onUpdateStatus(record, 'Ready for Testing');
            else if (action === 'forceResend') onForceResend(record);
            else if (action === 'deleteDuplicate') onDelete && onDelete(record);
            else if (action === 'deleteStale') onDelete && onDelete(record);
          }}
        />
      </td>
      
      {/* Take ID - Editable (only for CG and Ribbon) - moved after Watch Folder */}
      {showTakeId && (
        <td 
          style={{ ...styles.td, width: COLUMN_WIDTHS.takeId }}
          title="Enter the Take ID from the playout system to mark up this content"
        >
          <EditableInput
            value={takeId}
            onChange={(val) => onUpdateTakeId(record, val)}
            disabled={isPending}
            placeholder="Enter Take ID"
            style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px' }}
          />
        </td>
      )}
      
      {/* Last Modified - When content was last edited */}
      <td style={{ ...styles.td, width: COLUMN_WIDTHS.lastModified }}>
        <LastModifiedCell value={lastModified} />
      </td>
      
      {/* Actioned By — reads from Airtable 'Last Actioned By' field (cross-browser) with localStorage fallback */}
      <td style={{ ...styles.td, width: '120px' }}>
        {(() => {
          // Priority 1: Airtable field (visible to everyone)
          const airtableStamp = fields['Last Actioned By'];
          if (airtableStamp && typeof airtableStamp === 'string' && airtableStamp.trim()) {
            // Format: "Annie — Ingested — 08 Mar 10:42 am"
            const parts = airtableStamp.split(' — ');
            const who = parts[0] || '';
            const what = parts[1] || '';
            const when = parts[2] || '';
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: '#c4c4cc', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={airtableStamp}>
                  {who}
                </span>
                {what && (
                  <span style={{ fontSize: 8, fontWeight: 600, color: '#60a5fa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={what}>
                    {what}
                  </span>
                )}
                {when && (
                  <span style={{ fontSize: 8, color: '#62626e', fontFamily: 'JetBrains Mono, monospace' }}>
                    {when}
                  </span>
                )}
              </div>
            );
          }
          
          // Priority 2: localStorage (current browser only)
          const action = actionedBy?.get(record.id);
          if (action) {
            const firstName = action.name.split(' ')[0].split('@')[0];
            const actionDate = new Date(action.time);
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: '#c4c4cc', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={action.name}>
                  {firstName}
                </span>
                <span style={{ fontSize: 8, color: '#62626e', fontFamily: 'JetBrains Mono, monospace' }}>
                  {actionDate.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })}{' '}
                  {actionDate.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true })}
                </span>
              </div>
            );
          }
          
          // Priority 3: status-based hint
          if (status === 'Approved by Lumen8' || builtAtVenue) {
            return <span style={{ fontSize: 9, color: '#34d399', fontWeight: 600 }}>✓ Actioned</span>;
          }
          if (watchFolder) {
            return <span style={{ fontSize: 9, color: '#60a5fa', fontWeight: 600 }}>✓ Checked</span>;
          }
          return <span style={{ fontSize: 10, color: '#52525b' }}>—</span>;
        })()}
      </td>
      
      {/* IPTV Timing - Only for IPTV */}
      {contentType === 'iptv' && (
        <td style={{ ...styles.td, width: '130px' }}>
          {Array.isArray(selectTiming) && selectTiming.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
              {selectTiming.map((timing, idx) => (
                <span 
                  key={idx}
                  style={{
                    display: 'inline-block',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: 600,
                    background: 'rgba(139, 92, 246, 0.15)',
                    color: '#a78bfa',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {timing}
                </span>
              ))}
            </div>
          ) : (
            <span style={{ color: THEME.textMuted, fontSize: '12px' }}>—</span>
          )}
        </td>
      )}
      
      {/* IPTV Location - Only for IPTV */}
      {contentType === 'iptv' && (
        <td style={{ ...styles.td, width: '120px' }}>
          {Array.isArray(selectLocation) && selectLocation.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
              {selectLocation.map((location, idx) => (
                <span 
                  key={idx}
                  style={{
                    display: 'inline-block',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: 600,
                    background: 'rgba(236, 72, 153, 0.15)',
                    color: '#f472b6',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {location}
                </span>
              ))}
            </div>
          ) : (
            <span style={{ color: THEME.textMuted, fontSize: '12px' }}>—</span>
          )}
        </td>
      )}
      
      {/* Actions - Delete for manual/operator categories, lock for MatchFlow rows */}
      <td style={{ ...styles.td, width: COLUMN_WIDTHS.actions, textAlign: 'center' }}>
        {isManualCategory ? (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete && onDelete(record); }}
            title="Delete this row"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              fontSize: 13, color: THEME.textMuted, padding: '4px 6px', borderRadius: 4,
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = THEME.textMuted; e.currentTarget.style.background = 'transparent'; }}
          >
            🗑️
          </button>
        ) : (
          <span 
            style={{ fontSize: 11, color: THEME.textMuted, cursor: 'default' }}
            title={fields['Source Record ID'] 
              ? "This row is managed by MatchFlow Builder — delete it there"
              : "Row is locked — manage content via MatchFlow Builder"
            }
          >
            🔒
          </span>
        )}
      </td>
      
      {/* Download - For CG, Ribbon, TVC, LED, and IPTV */}
      {['cg', 'ribbon', 'tvc', 'led', 'iptv'].includes(contentType) && (
        <td style={{ ...styles.td, width: COLUMN_WIDTHS.download, textAlign: 'center' }}>
          {downloadUrl ? (
            <Tooltip text="Download content to watch folder" position="left">
              <a
                href={(() => {
                  // Convert Dropbox URL to direct download
                  let url = downloadUrl;
                  if (url.includes('dropbox.com')) {
                    // Add dl=1 parameter to force download instead of preview
                    url = url.includes('?') ? `${url}&dl=1` : `${url}?dl=1`;
                  }
                  return url;
                })()}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#3b82f6',
                  textDecoration: 'none',
                  fontSize: '14px',
                  padding: '6px',
                  borderRadius: '4px',
                  background: 'rgba(59, 130, 246, 0.15)',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.25)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.15)'}
              >
                {Icons.download}
              </a>
            </Tooltip>
          ) : (
            <span style={{ color: THEME.textMuted, fontSize: '11px' }}>—</span>
          )}
        </td>
      )}
    </tr>
  );
}

// ================= QUICK ADD ROW (inline add for manual categories) =================

function QuickAddRow({ category, onAdd }) {
  const [isAdding, setIsAdding] = useState(false);

  const handleAdd = async () => {
    if (isAdding) return;
    setIsAdding(true);
    try {
      await onAdd(category);
    } catch (err) {
      console.error('[QuickAddRow] Failed:', err);
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <tr style={{ borderTop: `1px dashed ${THEME.border}` }}>
      <td colSpan="100%" style={{ padding: '4px 8px' }}>
        <button
          onClick={handleAdd}
          disabled={isAdding}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'transparent', border: 'none', cursor: isAdding ? 'wait' : 'pointer',
            color: isAdding ? THEME.textMuted : THEME.accent,
            fontSize: 11, fontWeight: 600, padding: '4px 6px', borderRadius: 4,
            transition: 'all 0.15s', opacity: isAdding ? 0.5 : 0.7,
            width: '100%',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = THEME.bgHover; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.7'; e.currentTarget.style.background = 'transparent'; }}
        >
          <span style={{ fontSize: 14 }}>+</span>
          {isAdding ? 'Adding...' : `Add to ${category}`}
        </button>
      </td>
    </tr>
  );
}

function CategorySection({ 
  category, 
  records, 
  selectedRecords,
  onSelectRecord,
  onSelectAllInCategory,
  onUpdateStatus, 
  onUpdateComments,
  onUpdateTakeId,
  onToggleWatchFolder,
  onForceResend,
  onToggleBuiltAtVenue,
  onUnlock,
  actionedBy,
  onDelete,
  onEdit,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
  draggingRecord,
  contentType,
  sortByStatus,
  stableReadyIds,
  settlingIds,
  returningIds,
  isManualCategory,
  staleSourceIds,
  onQuickAdd,
  onOpenPreview,
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Sort records: by status priority or cue order
  const sortedRecords = React.useMemo(() => {
    if (!sortByStatus) return records; // Default: cue order from Airtable
    return [...records].sort((a, b) => {
      const sa = SORT_STATUS_PRIORITY[a.fields?.['Content Status'] || ''] ?? 4;
      const sb = SORT_STATUS_PRIORITY[b.fields?.['Content Status'] || ''] ?? 4;
      if (sa !== sb) return sa - sb;
      const ca = parseFloat(a.fields?.['Cue ID']) || 0;
      const cb = parseFloat(b.fields?.['Cue ID']) || 0;
      return ca - cb;
    });
  }, [records, sortByStatus]);
  
  // Category-level stats — full pipeline tracking
  const builtInCat = records.filter(r => r.fields?.['Built at Venue']).length;
  const sentInCat = records.filter(r => r.fields?.['Send To Watch Folder'] && !r.fields?.['Built at Venue']).length;
  const needsReviewInCat = records.filter(r => !r.fields?.['Send To Watch Folder'] && !r.fields?.['Built at Venue'] && r.fields?.['Content Status'] !== 'Rejected' && r.fields?.['Content Status'] !== 'Content Unlocked' && !r.fields?.['_isArchived']).length;
  const totalInCat = records.length;
  
  // Pipeline progress: unchecked → checked/sent → built (3 stages)
  const checkedOrBuilt = sentInCat + builtInCat; // everything that's been through Ingest
  const pipelinePct = totalInCat > 0 ? Math.round((checkedOrBuilt / totalInCat) * 100) : 0;
  const builtPct = totalInCat > 0 ? Math.round((builtInCat / totalInCat) * 100) : 0;
  const isFullyBuilt = builtInCat === totalInCat && totalInCat > 0;
  const isFullyChecked = (checkedOrBuilt === totalInCat) && totalInCat > 0;

  // Determine if Take ID and Ingest Status should be shown (only for CG and Ribbon)
  const showTakeId = contentType === 'cg' || contentType === 'ribbon';
  const showIngestStatus = contentType === 'cg' || contentType === 'ribbon' || contentType === 'iptv';
  
  // Duration entitlement tracking — show for all CG/Ribbon categories
  const ENTITLEMENT_SECONDS = {
    'AFL Marketing': 180,
  };
  const showDuration = contentType === 'cg' || contentType === 'ribbon';
  const entitlementSecs = showDuration ? (ENTITLEMENT_SECONDS[category] || null) : null;
  const totalDurationSecs = showDuration ? records.reduce((sum, r) => {
    // Playout Duration (operator-set, writable) takes priority over Duration (computed lookup from asset)
    const dur = parseInt(r.fields?.['Playout Duration']) || parseInt(r.fields?.['Duration']) || 0;
    return sum + dur;
  }, 0) : 0;
  const entitlementPct = entitlementSecs ? Math.min((totalDurationSecs / entitlementSecs) * 100, 100) : 0;
  const isOverEntitlement = entitlementSecs && totalDurationSecs > entitlementSecs;

  return (
    <div style={styles.categorySection}>
      <div
        style={{ ...styles.categoryHeader, cursor: 'pointer' }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span style={{ 
          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0)', 
          transition: 'transform 0.2s',
          fontSize: '12px',
          color: THEME.textMuted,
          display: 'inline-block',
        }}>
          ▶
        </span>
        <span style={styles.categoryName}>
          {isFullyBuilt && <span style={{ color: '#34d399', marginRight: 6 }}>✓</span>}
          {category}
        </span>
        <span style={styles.categoryCount}>{records.length} assets</span>
        
        {/* Duration entitlement tracker */}
        {showDuration && entitlementSecs && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 8, maxWidth: 200 }}>
            <div style={{ flex: 1, height: 5, borderRadius: 3, background: THEME.bgHover, overflow: 'hidden', minWidth: 60 }}>
              <div style={{
                height: '100%', borderRadius: 3,
                width: `${Math.min(entitlementPct, 100)}%`,
                background: isOverEntitlement ? '#ef4444' : entitlementPct >= 90 ? '#fbbf24' : THEME.accent,
                transition: 'width 0.3s ease',
              }} />
            </div>
            <span style={{
              fontSize: 10, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace',
              color: isOverEntitlement ? '#ef4444' : entitlementPct >= 90 ? '#fbbf24' : THEME.accent,
              whiteSpace: 'nowrap',
            }}>
              {totalDurationSecs}s / {entitlementSecs}s
            </span>
            {isOverEntitlement && (
              <span style={{ fontSize: 8, fontWeight: 800, color: '#ef4444', background: 'rgba(239,68,68,0.12)', padding: '1px 5px', borderRadius: 3, border: '1px solid rgba(239,68,68,0.3)' }}>
                OVER
              </span>
            )}
          </div>
        )}
        {showDuration && !entitlementSecs && totalDurationSecs > 0 && (
          <span style={{ fontSize: 10, fontWeight: 600, color: THEME.textMuted, marginLeft: 8, fontFamily: 'JetBrains Mono, monospace' }}>
            {totalDurationSecs}s total
          </span>
        )}
        
        {/* Inline progress bar — dual track: checked (blue) + built (green) */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, marginLeft: 8, maxWidth: 280 }}>
          <div style={{ flex: 1, height: 5, borderRadius: 3, background: THEME.bgHover, overflow: 'hidden', position: 'relative' }}>
            {/* Blue layer: checked/sent (includes built) */}
            <div style={{
              height: '100%', width: `${pipelinePct}%`, borderRadius: 3,
              background: '#60a5fa', transition: 'width 0.3s ease',
              position: 'absolute', top: 0, left: 0,
            }} />
            {/* Green layer: built (on top, shorter) */}
            <div style={{
              height: '100%', width: `${builtPct}%`, borderRadius: 3,
              background: isFullyBuilt ? '#34d399' : '#22c55e',
              transition: 'width 0.3s ease',
              position: 'absolute', top: 0, left: 0,
            }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1, whiteSpace: 'nowrap' }}>
            <span style={{ 
              fontSize: 10, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace',
              color: isFullyBuilt ? '#34d399' : isFullyChecked ? '#60a5fa' : '#f97316',
            }}>
              {isFullyBuilt ? `✓ ${builtInCat}/${totalInCat}` : `${builtInCat}/${totalInCat} built`}
            </span>
            {!isFullyBuilt && (
              <span style={{ fontSize: 8, color: '#60a5fa', fontWeight: 600 }}>
                {checkedOrBuilt}/{totalInCat} ingested
              </span>
            )}
          </div>
          {needsReviewInCat > 0 && (
            <span style={{ fontSize: 9, color: '#f97316', fontWeight: 600, whiteSpace: 'nowrap' }}>
              {needsReviewInCat} not ingested
            </span>
          )}
        </div>
        
        {/* Bulk Actions — counts reflect remaining work only */}
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
          {needsReviewInCat > 0 && (
            <button
              onClick={() => {
                records.filter(r => !r.fields?.['Send To Watch Folder'] && !r.fields?.['Built at Venue'] && r.fields?.['Content Status'] !== 'Rejected' && r.fields?.['Content Status'] !== 'Content Unlocked' && !r.fields?.['_isArchived'])
                  .forEach(r => onToggleWatchFolder(r, true));
              }}
              style={{
                fontSize: 11, fontWeight: 700, padding: '6px 14px', borderRadius: 6,
                border: 'none', cursor: 'pointer', background: '#b45309', color: '#fff',
                boxShadow: '0 2px 8px rgba(180,83,9,0.4)', whiteSpace: 'nowrap',
              }}
            >
              Ingest All ({needsReviewInCat} remaining)
            </button>
          )}
          {sentInCat > 0 && (
            <button
              onClick={() => {
                records.filter(r => r.fields?.['Send To Watch Folder'] && !r.fields?.['Built at Venue'])
                  .forEach(r => onToggleBuiltAtVenue(r, true));
              }}
              style={{
                fontSize: 11, fontWeight: 700, padding: '6px 14px', borderRadius: 6,
                border: 'none', cursor: 'pointer', background: '#34d399', color: '#000',
                boxShadow: '0 2px 8px rgba(52,211,153,0.3)', whiteSpace: 'nowrap',
              }}
            >
              ⚡ Mark All Built ({sentInCat} remaining)
            </button>
          )}
          {isFullyBuilt && (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#34d399', padding: '7px 0' }}>
              ✓ All {totalInCat} built
            </span>
          )}
        </div>
        
      </div>
      
      {isExpanded && (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: COLUMN_WIDTHS.drag, ...stickyStyle('drag', true) }}></th>
                <th style={{ ...styles.th, width: contentType === 'iptv' ? '140px' : COLUMN_WIDTHS.name, ...stickyStyle('name', true) }}>Name</th>
                <th style={{ ...styles.th, width: COLUMN_WIDTHS.thumbnail, ...stickyStyle('thumbnail', true) }}>Preview</th>
                <th style={{ ...styles.th, width: COLUMN_WIDTHS.lumen8Id, ...stickyStyle('lumen8Id', true) }}>
                  Lumen8 ID
                  <HeaderHelp tooltip="Click on the thumbnail to preview the content" />
                </th>
                <th style={{ ...styles.th, width: '200px' }} title="Club Instructions">Club Instructions</th>
                <th style={{ ...styles.th, width: COLUMN_WIDTHS.contentType }}>Content Type</th>
                <th style={{ ...styles.th, width: COLUMN_WIDTHS.hasAudio, textAlign: 'center' }} title="Has Audio">Audio</th>
                {contentType !== 'iptv' && (
                  <th style={{ ...styles.th, width: COLUMN_WIDTHS.hasAlpha, textAlign: 'center' }} title="Has Alpha Channel">Alpha</th>
                )}
                {contentType !== 'iptv' && (
                  <th style={{ ...styles.th, width: COLUMN_WIDTHS.duration }}>Duration</th>
                )}
                <th style={{ ...styles.th, width: COLUMN_WIDTHS.dropbox }}>
                  {contentType === 'iptv' ? 'Files' : 'Dropbox'}
                  <HeaderHelp tooltip={contentType === 'iptv' 
                    ? "Open IPTV exports folder or download individual asset files" 
                    : "Click the link to open the content location in Dropbox"
                  } />
                </th>
                <th style={{ ...styles.th, width: '120px' }}>
                  Pipeline
                </th>
                <th style={{ ...styles.th, width: '170px', textAlign: 'center' }}>
                  Action
                </th>
                {showIngestStatus && (
                  <th style={{ ...styles.th, width: COLUMN_WIDTHS.ingestStatus, textAlign: 'center' }}>
                    Ingested
                    <HeaderHelp tooltip="🟢 Green = Content has been ingested to venue encoder. 🔴 Red = Content not yet ingested." />
                  </th>
                )}
                <th style={{ ...styles.th, width: '100px', textAlign: 'center' }}>Actions</th>
                {showTakeId && (
                  <th style={{ ...styles.th, width: COLUMN_WIDTHS.takeId }}>
                    Take ID
                    <HeaderHelp tooltip="Enter the Take ID to mark up content in the playout system" />
                  </th>
                )}
                <th style={{ ...styles.th, width: COLUMN_WIDTHS.lastModified }}>
                  Last Edit
                  <HeaderHelp tooltip="When this content was last updated in the source system" />
                </th>
                <th style={{ ...styles.th, width: '120px' }}>
                  Actioned By
                </th>
                {contentType === 'iptv' && (
                  <th style={{ ...styles.th, width: '130px' }}>
                    Timing
                    <HeaderHelp tooltip="When this content should be displayed (Pre-Game, In-Game, etc.)" />
                  </th>
                )}
                {contentType === 'iptv' && (
                  <th style={{ ...styles.th, width: '120px' }}>
                    Location
                    <HeaderHelp tooltip="Where this content should be displayed (Corporate Suites, etc.)" />
                  </th>
                )}
                <th style={{ ...styles.th, width: COLUMN_WIDTHS.actions }}></th>
                {['cg', 'ribbon', 'tvc', 'led', 'iptv'].includes(contentType) && (
                  <th style={{ ...styles.th, width: COLUMN_WIDTHS.download, textAlign: 'center' }}>
                    ⬇️
                    <HeaderHelp tooltip="Download content to watch folder" />
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Detect duplicates: multiple rows with the same Source Record ID
                const sourceIdCounts = {};
                sortedRecords.forEach(r => {
                  const sid = r.fields?.['Source Record ID'];
                  if (sid) sourceIdCounts[sid] = (sourceIdCounts[sid] || 0) + 1;
                });
                
                return sortedRecords.map(record => {
                const lastMod = record.fields?.['Last Modified'] || record.fields?.['Last Edit Time'] || '';
                const isRecentlyUpdated = lastMod && (Date.now() - new Date(lastMod).getTime()) < 24 * 60 * 60 * 1000;
                const sourceId = record.fields?.['Source Record ID'];
                const isDuplicate = sourceId && sourceIdCounts[sourceId] > 1;
                const isStale = sourceId && staleSourceIds && staleSourceIds.has(sourceId);
                
                return (
                <OperatorRow
                  key={record.id}
                  record={record}
                  isSelected={selectedRecords.has(record.id)}
                  isRecentlyUpdated={isRecentlyUpdated}
                  isDuplicate={isDuplicate}
                  isStale={isStale}
                  onSelect={onSelectRecord}
                  onUpdateStatus={onUpdateStatus}
                  onUpdateComments={onUpdateComments}
                  onUpdateTakeId={onUpdateTakeId}
                  onToggleWatchFolder={onToggleWatchFolder}
                  onForceResend={onForceResend}
                  onToggleBuiltAtVenue={onToggleBuiltAtVenue}
                  onUnlock={onUnlock}
                  actionedBy={actionedBy}
                  onDelete={onDelete}
                  onEdit={onEdit}
                  onDragStart={onDragStart}
                  onDragOver={onDragOver}
                  onDragEnd={onDragEnd}
                  onDrop={onDrop}
                  isDragging={draggingRecord?.id === record.id}
                  contentType={contentType}
                  stabilityState={returningIds?.has(record.id) ? 'returning' : stableReadyIds?.has(record.id) ? 'ready' : settlingIds?.has(record.id) ? 'settling' : null}
                  isManualCategory={isManualCategory}
                  onOpenPreview={onOpenPreview}
                />
                );
              });
              })()}
              {/* Inline Quick Add for manual categories */}
              {isManualCategory && onQuickAdd && (
                <QuickAddRow category={category} onAdd={onQuickAdd} />
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ================= MAIN COMPONENT =================

export default function OperatorDashboard() {
  const {
    isConfigured,
    clubs,
    contentTypes,
    selectedClub,
    setSelectedClub,
    contentType,
    setContentType,
    selectedView,
    setSelectedView,
    records,
    recordsByCategory,
    allCategories,
    views,
    loading,
    error,
    updateStatus,
    batchUpdateStatus,
    updateComments,
    toggleWatchFolder,
    forceResend,
    updateTakeId,
    toggleBuiltAtVenue,
    unlockRow,
    forceResyncAll,
    refresh,
    createRow,
    deleteRow,
    staleSourceIds,
  } = useOperator();
  
  // Current user info for action attribution
  const { userName, userEmail } = useAuth();

  // ─── Sync Status Check ───
  // Lightweight count comparison between MatchFlow source and Operator view
  const [syncStatus, setSyncStatus] = useState(null); // { missing, sourceCount, operatorCount, missingItems }
  
  // ── Dashboard-level preview state (persists across row re-renders/polling) ──
  const [activeRibbonPreview, setActiveRibbonPreview] = useState(null); // { asset, ribbonItems }
  const [activeThreeSixtyPreview, setActiveThreeSixtyPreview] = useState(null); // { asset, ribbonItems }
  const syncCheckRef = useRef(null);

  useEffect(() => {
    if (!selectedView || contentType === 'overview') {
      setSyncStatus(null);
      return;
    }
    // Debounce — check 2s after view/content type changes
    clearTimeout(syncCheckRef.current);
    syncCheckRef.current = setTimeout(async () => {
      try {
        const status = await checkSyncStatus(selectedView);
        setSyncStatus(status);
        if (status.missing > 0) {
          console.log(`[SyncCheck] ${status.missing} assets not in Operator View`, status.missingItems);
        }
      } catch (err) {
        console.warn('[SyncCheck] Failed:', err.message);
      }
    }, 2000);
    return () => clearTimeout(syncCheckRef.current);
  }, [selectedView, contentType, records.length]); // Re-check when records change (after sync)
  
  // ─── Browser Notifications ───
  // Scoped to current content type — only fires alerts for what you're looking at
  const prevRecordStatesRef = useRef(null);
  const notificationPermRef = useRef(false);
  
  // Request notification permission once
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(perm => {
        notificationPermRef.current = perm === 'granted';
      });
    } else if ('Notification' in window) {
      notificationPermRef.current = Notification.permission === 'granted';
    }
  }, []);
  
  // Detect changes and fire scoped notifications
  useEffect(() => {
    if (!notificationPermRef.current || !records.length || contentType === 'overview') return;
    
    const currentStates = new Map();
    records.forEach(r => {
      currentStates.set(r.id, {
        status: r.fields?.['Content Status'] || '',
        watchFolder: !!r.fields?.['Send To Watch Folder'],
        built: !!r.fields?.['Built at Venue'],
      });
    });
    
    if (prevRecordStatesRef.current) {
      const prev = prevRecordStatesRef.current;
      const ctLabel = contentType.toUpperCase();
      let newRejected = 0;
      let newContent = 0;
      let newUnlocked = 0;
      
      currentStates.forEach((cur, id) => {
        const old = prev.get(id);
        if (!old) {
          newContent++;
        } else {
          if (cur.status === 'Rejected' && old.status !== 'Rejected') newRejected++;
          if (cur.status === 'Content Unlocked' && old.status !== 'Content Unlocked') newUnlocked++;
        }
      });
      
      // Only notify for meaningful changes
      if (newRejected > 0) {
        new Notification(`❌ VenueFlow — ${ctLabel}`, {
          body: `${newRejected} content item${newRejected > 1 ? 's' : ''} rejected`,
          tag: `rejected-${contentType}`,
        });
      }
      if (newContent > 0) {
        new Notification(`📥 VenueFlow — ${ctLabel}`, {
          body: `${newContent} new content row${newContent > 1 ? 's' : ''} synced`,
          tag: `new-${contentType}`,
        });
      }
      if (newUnlocked > 0) {
        new Notification(`🔓 VenueFlow — ${ctLabel}`, {
          body: `${newUnlocked} row${newUnlocked > 1 ? 's' : ''} updated by club — needs re-check`,
          tag: `unlocked-${contentType}`,
        });
      }
    }
    
    prevRecordStatesRef.current = currentStates;
  }, [records, contentType]);
  
  // Actioned By — persists in localStorage so it survives refresh
  const ACTIONED_STORAGE_KEY = `venueflow_actioned_${selectedClub}_${contentType}`;
  
  const loadActionedBy = useCallback(() => {
    try {
      const stored = localStorage.getItem(ACTIONED_STORAGE_KEY);
      return stored ? new Map(JSON.parse(stored)) : new Map();
    } catch { return new Map(); }
  }, [ACTIONED_STORAGE_KEY]);
  
  const [actionedBy, setActionedBy] = useState(() => loadActionedBy());
  
  // Reload from localStorage when club/content type changes
  useEffect(() => {
    setActionedBy(loadActionedBy());
  }, [loadActionedBy]);
  
  const stampAction = useCallback((recordId, record, actionLabel) => {
    const name = userName || userEmail || 'Unknown';
    const now = new Date();
    const entry = { name, time: now.toISOString() };
    
    // 1. localStorage (immediate, per-browser)
    setActionedBy(prev => {
      const next = new Map(prev);
      next.set(recordId, entry);
      try { localStorage.setItem(ACTIONED_STORAGE_KEY, JSON.stringify([...next])); } catch {}
      return next;
    });
    
    // 2. Airtable (persistent, cross-browser) — fire and forget
    if (record?._clubId && record?._contentType) {
      const stamp = `${name} — ${actionLabel || 'Actioned'} — ${now.toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true })}`;
      import('../../services/operatorAirtable').then(({ updateOperatorField }) => {
        updateOperatorField(record._clubId, record._contentType, recordId, 'Last Actioned By', stamp).catch(err => {
          console.warn('[Operator] Failed to stamp Last Actioned By:', err?.message);
        });
      });
    }
  }, [userName, userEmail, ACTIONED_STORAGE_KEY]);
  
  // Presence for real-time collaboration
  const { setCurrentPage } = usePresence();
  
  // Track current page for presence
  useEffect(() => {
    const pageName = `operator-${selectedClub}-${contentType}-${selectedView}`;
    setCurrentPage(pageName);
  }, [selectedClub, contentType, selectedView, setCurrentPage]);
  
  // Themed dialogs (replaces native window.confirm/alert)
  const { confirm, showAlert, DialogComponents } = useThemedDialogs();
  
  // Toast notifications (top-right info banners)
  const { showSuccess, ToastComponent } = useToast();
  
  // Wrap toggleWatchFolder to show reminder toast and stamp user
  const handleToggleWatchFolder = useCallback((record, value) => {
    // Block ingest for archived content
    if (value && record.fields?.['_isArchived']) {
      showAlert({
        title: '⚠ Content Archived',
        message: 'This content cannot be ingested because the asset has been archived or marked for deletion in the content library.\n\nPlease ask the club to either:\n• Unarchive the content in IngestFlow\n• Replace it with a new asset in MatchFlow Builder',
        type: 'warning',
      });
      return;
    }
    toggleWatchFolder(record, value);
    if (value) {
      stampAction(record.id, record, 'Ingested');
      showSuccess(
        "Content sent to Incoder watch folder. QA alongside the operator during build.",
        { title: "✓ Sent to Incoder", duration: 10000 }
      );
    }
  }, [toggleWatchFolder, showSuccess, showAlert, stampAction]);

  // Wrap toggleBuiltAtVenue to stamp user
  const handleToggleBuiltAtVenue = useCallback((record, value) => {
    if (value && record.fields?.['_isArchived']) {
      showAlert({
        title: '⚠ Content Archived',
        message: 'This content cannot be built because the asset has been archived or marked for deletion in the content library.\n\nPlease ask the club to either:\n• Unarchive the content in IngestFlow\n• Replace it with a new asset in MatchFlow Builder',
        type: 'warning',
      });
      return;
    }
    toggleBuiltAtVenue(record, value);
    if (value) stampAction(record.id, record, 'Built by Operator');
  }, [toggleBuiltAtVenue, stampAction, showAlert]);

  // Dedicated unlock handler — uses context's atomic unlockRow which resets all fields
  // with extended polling protection (12s) to prevent race conditions.
  const handleUnlock = useCallback(async (record) => {
    stampAction(record.id, record, 'Unlocked');
    await unlockRow(record);
  }, [unlockRow, stampAction]);
  
  // Force resync progress
  const [syncProgress, setSyncProgress] = useState(null); // { current, total, name } or null
  
  // Add row modal state
  const [showAddRowModal, setShowAddRowModal] = useState(false);
  
  // Edit row modal state
  const [editingRecord, setEditingRecord] = useState(null);
  
  // Selection state
  const [selectedRecords, setSelectedRecords] = useState(new Set());
  
  // Drag-to-scroll for horizontal table scrolling (external mouse support)
  const { scrollRef, stickyRef, attach, onMainScroll, syncBarWidth } = useDragToScroll();
  
  // Attach drag listeners and sync bar width when scroll container mounts
  useEffect(() => {
    attach();
    syncBarWidth();
  });
  
  // Drag and drop state
  const [draggingRecord, setDraggingRecord] = useState(null);
  
  // Download modal state
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadModalItems, setDownloadModalItems] = useState({}); // { category: [items] }
  
  // Venue content importer removed — using manual category allocation instead
  
  // Filter records by category - exclude hidden statuses, hidden categories, and example rows
  const filteredRecordsByCategory = React.useMemo(() => {
    const filtered = {};
    
    Object.entries(recordsByCategory).forEach(([category, categoryRecords]) => {
      // Skip hidden categories entirely (Example Content, Example Row)
      if (HIDDEN_CATEGORIES.includes(category)) return;
      
      // Manual categories (AFL Marketing, Venue Ops, IPTV operator categories) — show ALL rows, no filtering
      const isManual = isManualOrOperatorCategory(category, contentType);
      
      // Filter out hidden statuses, hidden row names, and rows named "Unnamed" with no content
      const visibleRecords = categoryRecords.filter(r => {
        const status = r.fields?.['Content Status'];
        const name = r.fields?.['Name'] || r.fields?.['Asset Name'] || '';
        
        // Hide if status is in hidden list
        if (HIDDEN_STATUSES.includes(status)) return false;
        
        // Skip name filtering for manual/operator categories — they're not populated from MatchFlow
        if (isManual) return true;
        
        // Hide if row name matches example patterns (only for MatchFlow-allocated categories)
        if (HIDDEN_ROW_NAMES.some(hidden => name.toLowerCase().includes(hidden.toLowerCase()))) return false;
        
        return true;
      });
      
      if (visibleRecords.length === 0) {
        // Marvel manual/operator categories show empty so operators can add content
        const isMarvelView = selectedView && (selectedView.toUpperCase().includes('MRV') || selectedView.toUpperCase().includes('MARVEL'));
        if (!(isManual && isMarvelView)) return;
      }
      
      filtered[category] = visibleRecords;
    });
    
    // For Marvel views: ensure manual/operator categories exist even if no records yet
    if (selectedView) {
      const isMarvelView = selectedView.toUpperCase().includes('MRV') || selectedView.toUpperCase().includes('MARVEL');
      
      if (isMarvelView) {
        if (contentType === 'cg') {
          CG_MARVEL_MANUAL_CATEGORIES.forEach(cat => {
            if (!filtered[cat]) filtered[cat] = [];
          });
        }
        if (contentType === 'iptv') {
          // Inject IPTV operator categories for Marvel views
          const IPTV_MARVEL_CATEGORIES = [
            'AFL Marketing [Victory]',
            'Aisle 31 [AFL Marketing]',
            'Gate 4 [Disney]',
            'Gate 6 [Disney]',
            'Gate 8 [AFL Marketing]',
            'Gate 9 [AFL Marketing]',
          ];
          IPTV_MARVEL_CATEGORIES.forEach(cat => {
            if (!filtered[cat]) filtered[cat] = [];
          });

          // Split "Gate 4/6 [Disney]" records into virtual Gate 4 and Gate 6 categories
          // Records are distributed by name convention or shown in both
          if (filtered['Gate 4/6 [Disney]']) {
            const combined = filtered['Gate 4/6 [Disney]'];
            combined.forEach(rec => {
              const name = (rec.fields?.Name || rec.fields?.['File/Folder Name'] || '').toLowerCase();
              if (name.includes('gate_6') || name.includes('gate-6') || name.includes('gate6')) {
                filtered['Gate 6 [Disney]'].push(rec);
              } else if (name.includes('gate_4') || name.includes('gate-4') || name.includes('gate4')) {
                filtered['Gate 4 [Disney]'].push(rec);
              } else {
                // No gate indicator in name — show in both
                filtered['Gate 4 [Disney]'].push(rec);
                filtered['Gate 6 [Disney]'].push(rec);
              }
            });
            // Hide the combined category from display
            delete filtered['Gate 4/6 [Disney]'];
          }
        }
      }
    }
    
    return filtered;
  }, [recordsByCategory, contentType, selectedView]);
  
  // Extract available assets from records for linking
  const availableAssets = React.useMemo(() => {
    const assets = [];
    const seenIds = new Set();
    
    records.forEach(record => {
      const fields = record.fields || {};
      const lumenId = fields['Lumen8 ID'];
      const name = fields['Name'] || fields['_assetName'];
      
      // Only add if we have a Lumen8 ID and haven't seen it
      if (lumenId && !seenIds.has(lumenId)) {
        seenIds.add(lumenId);
        assets.push({
          lumenId,
          name,
          recordId: record.id,
        });
      }
    });
    
    // Sort by name
    return assets.sort((a, b) => {
      const nameA = a.name || a.lumenId;
      const nameB = b.name || b.lumenId;
      return nameA.localeCompare(nameB);
    });
  }, [records]);
  
  // Handle record selection
  const handleSelectRecord = useCallback((record, isSelected) => {
    setSelectedRecords(prev => {
      const next = new Set(prev);
      if (isSelected) {
        next.add(record.id);
      } else {
        next.delete(record.id);
      }
      return next;
    });
  }, []);
  
  // Handle select all in category
  const handleSelectAllInCategory = useCallback((category, categoryRecords, isSelected) => {
    setSelectedRecords(prev => {
      const next = new Set(prev);
      categoryRecords.forEach(r => {
        if (isSelected) {
          next.add(r.id);
        } else {
          next.delete(r.id);
        }
      });
      return next;
    });
  }, []);
  
  // Handle delete (only applies to manually added operator rows, not MatchFlow Builder entries)
  const handleDelete = useCallback(async (record) => {
    const confirmed = await confirm({
      title: 'Delete Content',
      message: `Are you sure you want to delete "${record.fields?.Name || 'this item'}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      confirmColor: '#ef4444',
      icon: '🗑️',
    });
    
    if (confirmed) {
      try {
        await deleteRow(selectedClub, record.id);
        console.log('[OperatorDashboard] Deleted record:', record.id);
      } catch (err) {
        console.error('[OperatorDashboard] Failed to delete:', err);
        await showAlert({
          title: 'Delete Failed',
          message: err.message || 'An unknown error occurred while deleting.',
          type: 'error',
        });
      }
    }
  }, [deleteRow, selectedClub, confirm, showAlert]);
  
  // Handle edit
  const handleEdit = useCallback((record) => {
    setEditingRecord(record);
  }, []);
  
  // Quick add for manual categories — creates blank row, then opens edit modal
  const handleQuickAdd = useCallback(async (category) => {
    if (selectedClub === 'all') return;
    
    // Resolve fixture from existing records in this view
    let fixtureIds = null;
    for (const r of records) {
      const fids = r.fields?.['Fixtures'];
      if (fids && Array.isArray(fids) && fids.length > 0) {
        fixtureIds = fids;
        break;
      }
    }
    
    // Map virtual display categories to real Airtable select options
    const CATEGORY_WRITE_MAP = {
      'Gate 4 [Disney]': 'Gate 4/6 [Disney]',
      'Gate 6 [Disney]': 'Gate 4/6 [Disney]',
    };
    const airtableCategory = CATEGORY_WRITE_MAP[category] || category;

    const fields = {
      Name: 'New Content',
      Category: airtableCategory,
      'Content Status': 'New Submission',
      'Playout Duration': 15,
    };
    
    if (fixtureIds) {
      fields['Fixtures'] = fixtureIds;
    }
    
    console.log(`[QuickAdd] Creating blank row in "${category}" (15s default)`, fixtureIds ? `fixture: ${fixtureIds[0]}` : '(no fixture)');
    
    const newRecord = await createRow(selectedClub, fields);
    
    // Open edit modal so operator can fill in name and pick content
    if (newRecord) {
      setEditingRecord(newRecord);
    }
  }, [selectedClub, records, createRow]);
  
  // Drag and drop handlers
  const handleDragStart = useCallback((e, record) => {
    setDraggingRecord(record);
    e.dataTransfer.effectAllowed = 'move';
  }, []);
  
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);
  
  const handleDragEnd = useCallback(() => {
    setDraggingRecord(null);
  }, []);
  
  const handleDrop = useCallback((e, targetRecord) => {
    e.preventDefault();
    if (draggingRecord && draggingRecord.id !== targetRecord.id) {
      console.log('Reorder:', draggingRecord.id, 'to position of:', targetRecord.id);
      // TODO: Implement reorder API call
    }
    setDraggingRecord(null);
  }, [draggingRecord]);
  
  // Bulk actions
  // eslint-disable-next-line no-unused-vars
  const handleBulkDelete = useCallback(async () => {
    if (selectedRecords.size === 0) return;
    const confirmed = await confirm({
      title: 'Delete Selected Items',
      message: `Are you sure you want to delete ${selectedRecords.size} selected item${selectedRecords.size > 1 ? 's' : ''}? This action cannot be undone.`,
      confirmLabel: `Delete ${selectedRecords.size} Item${selectedRecords.size > 1 ? 's' : ''}`,
      confirmColor: '#ef4444',
      icon: '🗑️',
    });
    if (confirmed) {
      console.log('Bulk delete:', Array.from(selectedRecords));
      // TODO: Implement bulk delete
      setSelectedRecords(new Set());
    }
  }, [selectedRecords, confirm]);
  
  // eslint-disable-next-line no-unused-vars
  const handleBulkStatusChange = useCallback(async (newStatus) => {
    if (selectedRecords.size === 0) return;
    const recordsToUpdate = records.filter(r => selectedRecords.has(r.id));
    
    const confirmed = await confirm({
      title: `Bulk ${newStatus}`,
      message: `Update ${recordsToUpdate.length} selected item${recordsToUpdate.length !== 1 ? 's' : ''} to "${newStatus}"?`,
      confirmLabel: "Update",
      confirmColor: newStatus === 'Rejected' ? '#ef4444' : THEME.accent,
    });
    if (!confirmed) return;

    console.log(`[OperatorDashboard] Bulk status change: ${newStatus} for ${recordsToUpdate.length} records`);
    try {
      await batchUpdateStatus(recordsToUpdate, newStatus);
      showSuccess(`Updated ${recordsToUpdate.length} item${recordsToUpdate.length !== 1 ? 's' : ''} to "${newStatus}"`, { title: "Bulk Update Complete", duration: 4000 });
      setSelectedRecords(new Set());
    } catch (err) {
      console.error("[OperatorDashboard] Bulk status change failed:", err);
      await showAlert({ title: "Bulk Update Failed", message: err.message || "Unknown error", type: "error" });
    }
  }, [selectedRecords, records, batchUpdateStatus, confirm, showAlert, showSuccess]);
  
  // Handle print - opens print-friendly view in new tab
  const handlePrint = useCallback(() => {
    // Get current timestamp for version
    const now = new Date();
    const versionDate = now.toLocaleDateString('en-AU', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
    const versionTime = now.toLocaleTimeString('en-AU', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
    
    // Get club display name
    const clubDisplay = selectedClub === 'all' 
      ? 'All Clubs' 
      : (clubs.find(c => c.id === selectedClub)?.name || selectedClub);
    
    // Build print content HTML
    let printHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Operator Markup Sheet - ${selectedView || 'All Rounds'}</title>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            margin: 20px; 
            background: white; 
            color: black; 
            font-size: 9px;
          }
          .print-header { 
            text-align: center; 
            margin-bottom: 16px; 
            border-bottom: 2px solid #333;
            padding-bottom: 12px;
          }
          .print-header h1 { 
            font-size: 18px; 
            margin: 0 0 4px 0; 
          }
          .print-header .match-name {
            font-size: 14px;
            font-weight: 600;
            color: #333;
            margin: 4px 0;
          }
          .print-header .meta { 
            font-size: 10px; 
            color: #666; 
            margin: 2px 0;
          }
          .print-header .version {
            font-size: 9px;
            color: #888;
            margin-top: 8px;
            font-style: italic;
          }
          .print-category { 
            background: #e5e7eb; 
            font-weight: 700; 
            font-size: 11px; 
            padding: 6px 8px; 
            margin-top: 14px; 
            border: 1px solid #d1d5db; 
          }
          .print-table { 
            width: 100%; 
            border-collapse: collapse; 
            font-size: 8px; 
            margin-bottom: 6px; 
          }
          .print-table th { 
            background: #f3f4f6; 
            border: 1px solid #d1d5db; 
            padding: 4px 3px; 
            text-align: left; 
            font-weight: 600; 
            font-size: 7px;
          }
          .print-table td { 
            border: 1px solid #d1d5db; 
            padding: 3px; 
            vertical-align: top;
          }
          .print-table tr:nth-child(even) td { 
            background: #f9fafb; 
          }
          .status-cell {
            font-size: 7px;
            padding: 2px 4px;
            border-radius: 3px;
            text-align: center;
            font-weight: 600;
          }
          .status-new { background: #e5e7eb; color: #374151; }
          .status-testing { background: #ffedd5; color: #c2410c; }
          .status-playout { background: #dbeafe; color: #1d4ed8; }
          .status-rejected { background: #fde2e2; color: #dc2626; }
          .status-approved { background: #dcfce7; color: #16a34a; }
          .sent-yes { color: #16a34a; font-weight: 700; }
          .sent-no { color: #9ca3af; }
          .mono { font-family: monospace; font-size: 7px; }
          .small { font-size: 7px; }
          .center { text-align: center; }
          .notes-cell { min-width: 80px; }
          @media print {
            @page { size: landscape; margin: 8mm; }
            body { margin: 0; }
          }
        </style>
      </head>
      <body>
        <div class="print-header">
          <h1>Operator Markup Sheet</h1>
          <div class="match-name">${selectedView || 'All Rounds'}</div>
          <div class="meta">${contentType.toUpperCase()} Content • ${clubDisplay}</div>
          <div class="version">Version: ${versionDate} at ${versionTime}</div>
        </div>
    `;
    
    // Determine columns based on content type
    const showTakeId = contentType === 'cg' || contentType === 'ribbon';
    
    // Build tables by category
    Object.entries(filteredRecordsByCategory).forEach(([category, categoryRecords]) => {
      if (categoryRecords.length === 0) return;
      
      printHTML += `<div class="print-category">${category} (${categoryRecords.length} items)</div>`;
      
      printHTML += `
        <table class="print-table">
          <thead>
            <tr>
              <th style="width:22px" class="center">#</th>
              <th style="width:130px">Name</th>
              <th style="width:100px">Club Instructions</th>
              <th style="width:60px" class="small">Lumen8 ID</th>
              <th style="width:70px" class="small">Content Type</th>
              <th style="width:55px" class="center">Status</th>
              <th style="width:32px" class="center">Audio</th>
              ${contentType !== 'iptv' ? '<th style="width:32px" class="center">Alpha</th>' : ''}
              <th style="width:40px">Dur</th>
              <th style="width:50px">Venue</th>
              <th style="width:90px">Lumen8 Notes</th>
              ${showTakeId ? '<th style="width:75px">Take ID</th>' : ''}
            </tr>
          </thead>
          <tbody>
      `;
      
      categoryRecords.forEach((record, index) => {
        const fields = record.fields || {};
        
        // Get values
        const rowNum = index + 1;
        const name = fields['Name'] || fields['Asset Name'] || '-';
        const lumen8Id = fields['Lumen8 ID'] || '-';
        const contentTypeVal = fields['Asset Destination'] || fields['Content Type'] || '-';
        const status = fields['Content Status'] || 'New Submission';
        const hasAudio = fields['Has Audio'] === true ? '✓' : '-';
        const hasAlpha = fields['Has Alpha'] === true ? '✓' : '-';
        const duration = fields['Duration'] || '-';
        const venue = fields['Venue'] || fields['Destination Venue'] || '-';
        const venueRaw = typeof venue === 'string' ? venue : (Array.isArray(venue) ? venue[0] : '-');
        // Shorten common venue names
        const shortenVenue = (v) => {
          if (v.toLowerCase().includes('marvel')) return 'Marvel';
          if (v.toLowerCase().includes('mcg')) return 'MCG';
          if (v.toLowerCase().includes('gabba')) return 'Gabba';
          if (v.toLowerCase().includes('optus')) return 'Optus';
          if (v.toLowerCase().includes('adelaide')) return 'Adelaide';
          if (v.toLowerCase().includes('scg')) return 'SCG';
          return v.length > 10 ? v.substring(0, 10) : v;
        };
        const venueStr = shortenVenue(venueRaw);
        const takeId = fields['Take ID'] || '';
        const notes = fields['Lumen8 Comments'] || fields['Notes'] || '';
        const clubInstructions = (fields['Club Instructions'] || '').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, ' ').replace(/`/g, "'");
        
        // Status styling - MatchFlow Builder statuses only
        let statusClass = 'status-new';
        if (status.includes('Testing')) statusClass = 'status-testing';
        else if (status.includes('Playout')) statusClass = 'status-playout';
        else if (status.includes('Rejected')) statusClass = 'status-rejected';
        else if (status.includes('Approved')) statusClass = 'status-approved';
        
        // Truncate long values
        const truncate = (str, len) => str.length > len ? str.substring(0, len) + '…' : str;
        
        printHTML += `
          <tr>
            <td class="center" style="font-weight:600">${rowNum}</td>
            <td>${truncate(name, 30)}</td>
            <td class="small">${truncate(clubInstructions, 40)}</td>
            <td class="mono">${truncate(lumen8Id, 15)}</td>
            <td class="small">${truncate(contentTypeVal, 18)}</td>
            <td><span class="status-cell ${statusClass}">${status.replace('by Lumen8', '').trim()}</span></td>
            <td class="center">${hasAudio}</td>
            ${contentType !== 'iptv' ? `<td class="center">${hasAlpha}</td>` : ''}
            <td>${duration}</td>
            <td>${venueStr}</td>
            <td class="notes-cell">${truncate(notes, 25)}</td>
            ${showTakeId ? `<td class="mono">${takeId}</td>` : ''}
          </tr>
        `;
      });
      
      printHTML += `
          </tbody>
        </table>
      `;
    });
    
    printHTML += `
      </body>
      </html>
    `;
    
    // Open in new tab
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printHTML);
    printWindow.document.close();
    
    // Focus the new tab
    printWindow.focus();
    
    // Trigger print dialog after content loads
    setTimeout(() => {
      printWindow.print();
    }, 300);
  }, [contentType, selectedClub, selectedView, clubs, filteredRecordsByCategory]);
  
  // Stats - filter out hidden statuses and example rows
  const visibleRecords = records.filter(r => {
    const status = r.fields?.['Content Status'];
    const name = r.fields?.['Name'] || r.fields?.['Asset Name'] || '';
    
    // Hide if status is in hidden list
    if (HIDDEN_STATUSES.includes(status)) return false;
    
    // Hide if row name matches example patterns
    if (HIDDEN_ROW_NAMES.some(hidden => name.toLowerCase().includes(hidden.toLowerCase()))) return false;
    
    return true;
  });
  const totalRecords = visibleRecords.length;
  const builtCount = visibleRecords.filter(r => r.fields?.['Built at Venue']).length;
  const rejectedCount = visibleRecords.filter(r => 
    r.fields?.['Content Status'] === 'Rejected'
  ).length;
  const contentUnlockedCount = visibleRecords.filter(r => 
    r.fields?.['Content Status'] === 'Content Unlocked'
  ).length;

  const deletedAssetRecords = visibleRecords.filter(r => r.fields?.['_assetMissing']);
  const deletedAssetCount = deletedAssetRecords.length;

  const archivedAssetRecords = visibleRecords.filter(r => r.fields?.['_isArchived'] && !r.fields?.['_assetMissing']);
  const archivedAssetCount = archivedAssetRecords.length;

  // ─── Stability Detection for Bulk Ingest ───
  // Rows that have content, aren't sent/built/rejected, and haven't changed recently
  const INGEST_STABILITY_MS = 10 * 60 * 1000; // 10 minutes — safety buffer for Bulk Ingest
  const VISUAL_SETTLING_MS = 2 * 60 * 1000;   // 2 minutes — visual indicator only
  const now = Date.now();
  
  const ingestCandidates = visibleRecords.filter(r => {
    const status = r.fields?.['Content Status'] || '';
    const watchFolder = r.fields?.['Send To Watch Folder'];
    const built = r.fields?.['Built at Venue'];
    const hasAsset = r.fields?.['_assetId'];
    
    // Must have content, not already sent/built, not rejected/unlocked
    return hasAsset && !watchFolder && !built 
      && status !== 'Rejected' && status !== 'Content Unlocked';
  });
  
  const stableReadyRecords = ingestCandidates.filter(r => {
    const lastMod = r.fields?.['_lastModified'];
    if (!lastMod) return true; // No timestamp = old row, treat as stable
    const age = now - new Date(lastMod).getTime();
    return age >= INGEST_STABILITY_MS;
  });
  
  // Visual settling: only show indicator for first 2 minutes (not the full ingest window)
  const settlingRecords = ingestCandidates.filter(r => {
    const lastMod = r.fields?.['_lastModified'];
    if (!lastMod) return false;
    const age = now - new Date(lastMod).getTime();
    return age < VISUAL_SETTLING_MS;
  });
  
  // Build sets for quick lookup in row rendering
  const stableReadyIds = React.useMemo(() => new Set(stableReadyRecords.map(r => r.id)), [stableReadyRecords]);
  const settlingIds = React.useMemo(() => new Set(settlingRecords.map(r => r.id)), [settlingRecords]);

  // ─── Returning Content Detection ───
  // Content that's already ingested at venue from a previous round — needs no QA
  const returningRecords = ingestCandidates.filter(r => {
    const mcg = r.fields?.['_mcgIngest'];
    const marvel = r.fields?.['_marvelIngest'];
    return mcg || marvel; // Asset is already built in playout system
  });
  
  // New content = ingest candidates that are NOT returning
  const returningIds = React.useMemo(() => new Set(returningRecords.map(r => r.id)), [returningRecords]);
  
  // Returning content bypasses stability timer — always ready for auto-ingest
  // Combine: stable new content + ALL returning content = total ready for bulk ingest
  const newStableRecords = stableReadyRecords.filter(r => !returningIds.has(r.id));
  const allReadyForIngest = [...returningRecords, ...newStableRecords].filter(r => !r.fields?.['_isArchived']);

  // Bulk ingest state
  const [bulkIngestProgress, setBulkIngestProgress] = useState(null); // { current, total }

  // Stat card drill-down state
  const [expandedStat, setExpandedStat] = useState(null);
  const [sortByStatus, setSortByStatus] = useState(false); // Toggle: cue order vs status priority

  // ─── Keyboard Shortcuts ───
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger when typing in inputs/textareas/selects
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      if (selectedRecords.size === 0) return;

      const key = e.key.toLowerCase();
      if (key === 'a' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        handleBulkStatusChange('Approved by Lumen8');
      } else if (key === 'r' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        handleBulkStatusChange('Rejected');
      } else if (key === 't' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        handleBulkStatusChange('Ready for Testing');
      } else if (key === 'escape') {
        setSelectedRecords(new Set());
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedRecords, handleBulkStatusChange]);
  
  if (!isConfigured) {
    return (
      <div style={styles.container}>
        <div style={styles.errorState}>
          <h3>Operator View Not Configured</h3>
          <p>Please add operator base environment variables to enable this feature.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div style={styles.container}>
      {/* Keyframes for button animations */}
      <style>{`
        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 4px 15px rgba(47, 214, 181, 0.4), 0 0 20px rgba(47, 214, 181, 0.2); }
          50% { box-shadow: 0 4px 20px rgba(47, 214, 181, 0.6), 0 0 30px rgba(47, 214, 181, 0.4); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translate(-50%, -48%); }
          to { opacity: 1; transform: translate(-50%, -50%); }
        }
      `}</style>
      
      {/* Header */}
      <div style={{
        ...styles.header,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
      }}>
        <div>
          <h1 style={styles.title}>Out of Game Operator View</h1>
          <p style={styles.subtitle}>
            Manage content approval and playout across all clubs
          </p>
        </div>
        {/* Live Collaboration Indicator */}
        <PresenceAvatars maxVisible={4} />
      </div>
      
      {/* Controls */}
      <div style={styles.controls}>
        {/* Club Filter */}
        <div style={styles.controlGroup}>
          <label style={styles.controlLabel}>Club</label>
          <select
            style={styles.select}
            value={selectedClub}
            onChange={(e) => setSelectedClub(e.target.value)}
          >
            {clubs.map(club => (
              <option key={club.id} value={club.id}>
                {club.name}
              </option>
            ))}
          </select>
        </div>
        
        {/* Content Type Toggle */}
        <div style={styles.controlGroup}>
          <label style={styles.controlLabel}>Content Type</label>
          <div style={styles.contentTypeToggle}>
            <button
              style={{
                ...styles.toggleButton,
                ...(contentType === 'overview' ? styles.toggleButtonActive : {}),
                ...(contentType === 'overview' ? { background: 'linear-gradient(135deg, #00c9a7 0%, #0ea5e9 100%)', color: '#fff', borderColor: '#00c9a7' } : {}),
              }}
              onClick={() => setContentType('overview')}
              title="Dashboard — cross-content progress overview"
            >
              📊 Dashboard
            </button>
            <button
              style={{
                ...styles.toggleButton,
                ...(contentType === contentTypes.CG ? styles.toggleButtonActive : {}),
              }}
              onClick={() => setContentType(contentTypes.CG)}
              title="CG Operator View"
            >
              CG
            </button>
            <button
              style={{
                ...styles.toggleButton,
                ...(contentType === contentTypes.RIBBON ? styles.toggleButtonActive : {}),
              }}
              onClick={() => setContentType(contentTypes.RIBBON)}
              title="LED Ribbon Operator View"
            >
              Ribbon
            </button>
            <button
              style={{
                ...styles.toggleButton,
                ...(contentType === contentTypes.IPTV ? styles.toggleButtonActive : {}),
              }}
              onClick={() => setContentType(contentTypes.IPTV)}
              title="IPTV Content"
            >
              IPTV
            </button>
          </div>
        </div>
        
        {/* View/Match Selector */}
        <div style={styles.controlGroup}>
          <label style={styles.controlLabel}>Match / View</label>
          <select
            style={styles.select}
            value={selectedView || ''}
            onChange={(e) => setSelectedView(e.target.value)}
          >
            <option value="">Select match...</option>
            {views.filter(view => !view.name.toLowerCase().includes('all rounds') && view.name.toLowerCase() !== 'all').map(view => (
              <option key={`${view.clubId}-${view.id}`} value={view.name}>
                {selectedClub === 'all' ? `[${view.clubShortName}] ` : ''}{view.name}
              </option>
            ))}
          </select>
        </div>
        
        {/* Spacer */}
        <div style={{ flex: 1 }} />
        
        {contentType !== 'overview' && (<>
        {/* Add Row Button */}
        <div style={styles.controlGroup}>
          <label style={styles.controlLabel}>&nbsp;</label>
          <button
            style={{
              ...styles.toggleButton,
              background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)',
              color: '#fff',
              border: 'none',
              padding: '10px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: 700,
              fontSize: '13px',
              boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
              letterSpacing: '0.3px',
              transform: 'scale(1)',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'scale(1.03)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(99, 102, 241, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(99, 102, 241, 0.3)';
            }}
            onClick={() => setShowAddRowModal(true)}
            title="Add a new row directly to the table"
          >
            <span style={{ fontSize: '14px' }}>+</span>
            Add Row
          </button>
        </div>
        
        {/* Sync All Content Button — teal gradient matching Add Content styling */}
        <div style={{ ...styles.controlGroup, position: 'relative' }}>
          <label style={styles.controlLabel}>&nbsp;</label>
          <button
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              background: (syncProgress || !selectedView) ? THEME.bgCard : 'linear-gradient(135deg, #00c9a7 0%, #0ea5e9 100%)',
              color: (syncProgress || !selectedView) ? THEME.textMuted : '#fff',
              fontSize: '13px',
              fontWeight: 700,
              cursor: (syncProgress || !selectedView) ? 'not-allowed' : 'pointer',
              letterSpacing: '0.3px',
              boxShadow: (syncProgress || !selectedView) ? 'none' : '0 2px 8px rgba(0, 201, 167, 0.3)',
              transform: 'scale(1)',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
            onMouseEnter={(e) => { if (!syncProgress && selectedView) { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 201, 167, 0.4)'; }}}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = (syncProgress || !selectedView) ? 'none' : '0 2px 8px rgba(0, 201, 167, 0.3)'; }}
            onClick={async () => {
              if (syncProgress || !selectedView) return;
              setSyncProgress({ current: 0, total: 0, name: 'Starting...' });
              try {
                const result = await forceResyncAll((p) => setSyncProgress(p), selectedView || null);
                await refresh();
                showSuccess(
                  `Created ${result.created || 0} new operator rows, ${result.synced} already synced (${result.skipped} skipped, ${result.errors} errors)`,
                  { title: `✅ Sync Complete — ${result.total} source rows`, duration: 8000 }
                );
              } catch (err) {
                showAlert({ title: 'Sync Error', message: `Force sync failed: ${err.message}`, type: 'warning' });
              } finally {
                setSyncProgress(null);
              }
            }}
            disabled={!!syncProgress || !selectedView}
            title={selectedView ? 'Sync all MatchFlow content to operator views' : 'Select a match first'}
          >
            {syncProgress 
              ? `⚡ ${syncProgress.current}/${syncProgress.total}` 
              : '⚡ Sync All Content'}
          </button>
          {syncProgress && (
            <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 3, fontSize: 9, color: THEME.textMuted, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {syncProgress.name}
            </div>
          )}
          {!syncProgress && syncStatus && syncStatus.missing > 0 && (
            <SyncStatusTooltip syncStatus={syncStatus} />
          )}
          {!syncProgress && syncStatus && syncStatus.synced && records.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, fontSize: 9, color: THEME.accent, fontWeight: 500, whiteSpace: 'nowrap' }}>
              ✓ {records.length} {contentType === 'cg' ? 'CG' : contentType === 'ribbon' ? 'Ribbon' : contentType === 'iptv' ? 'IPTV' : ''} assets synced
              <span style={{ color: THEME.textMuted, marginLeft: 4 }}>
                ({syncStatus.sourceRecordCount} source rows)
              </span>
            </div>
          )}
        </div>
        
        {/* Download All Button */}
        {['cg', 'ribbon', 'tvc', 'led', 'iptv'].includes(contentType) && (
          <div style={styles.controlGroup}>
            <label style={styles.controlLabel}>&nbsp;</label>
            <button
              style={{
                padding: '10px 18px',
                borderRadius: '8px',
                border: 'none',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
                letterSpacing: '0.3px',
                boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
                transform: 'scale(1)',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(99, 102, 241, 0.4)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(99, 102, 241, 0.3)'; }}
              onClick={() => {
                const byCategory = {};
                const visibleCats = allCategories.length > 0 ? allCategories : Object.keys(filteredRecordsByCategory);
                visibleCats.forEach(cat => {
                  const catRecords = (filteredRecordsByCategory[cat] || [])
                    .filter(r => r.fields?.['_downloadUrl'])
                    .map(r => {
                      let url = r.fields['_downloadUrl'];
                      if (url.includes('dropbox.com')) {
                        url = url.includes('?') ? `${url}&dl=1` : `${url}?dl=1`;
                      }
                      return {
                        name: r.fields['_assetName'] || r.fields['Name'] || 'Unknown',
                        lumen8Id: r.fields['_lumen8Id'] || '',
                        url,
                      };
                    });
                  if (catRecords.length > 0) {
                    byCategory[cat] = catRecords;
                  }
                });
                const totalFiles = Object.values(byCategory).reduce((sum, arr) => sum + arr.length, 0);
                if (totalFiles === 0) {
                  showAlert({ title: 'No Content', message: 'No downloadable content in current view', type: 'warning' });
                  return;
                }
                setDownloadModalItems(byCategory);
                setShowDownloadModal(true);
              }}
              title="Download all content grouped by category"
            >
              📦 Download All ({records.filter(r => r.fields?.['_downloadUrl']).length})
            </button>
          </div>
        )}
        
        {/* Export CSV Button */}
        <div style={styles.controlGroup}>
          <label style={styles.controlLabel}>&nbsp;</label>
          <button
            style={{
              padding: '10px 18px',
              borderRadius: '8px',
              border: `1px solid ${THEME.border}`,
              background: THEME.bgCard,
              color: THEME.textSecondary,
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
              letterSpacing: '0.3px',
              transform: 'scale(1)',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.background = THEME.bgHover; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.background = THEME.bgCard; }}
            onClick={() => {
              const header = ['Name', 'Lumen8 ID', 'Category', 'Content Status', 'Watch Folder', 'Built at Venue', 'Built Time', 'Content Type', 'Club Instructions', 'Lumen8 Comments'];
              const rows = visibleRecords.map(r => {
                const f = r.fields || {};
                return [
                  f['Name'] || '',
                  (Array.isArray(f['Lumen8 ID']) ? f['Lumen8 ID'][0] : f['Lumen8 ID']) || '',
                  f['Category'] || '',
                  f['Content Status'] || '',
                  f['Send To Watch Folder'] ? 'Yes' : 'No',
                  f['Built at Venue'] ? 'Yes' : 'No',
                  f['Built at Venue Time'] || '',
                  (Array.isArray(f['Asset Destination']) ? f['Asset Destination'][0] : f['Asset Destination']) || '',
                  f['Club Instructions'] || '',
                  f['Lumen8 Comments'] || '',
                ];
              });
              const csv = [header, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
              const blob = new Blob([csv], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `Operator_${contentType}_${selectedClub || 'all'}_${new Date().toISOString().slice(0,10)}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            title="Export visible records to CSV"
          >
            📥 Export CSV
          </button>
        </div>
        
        {/* Print Button */}
        <div style={styles.controlGroup}>
          <label style={styles.controlLabel}>&nbsp;</label>
          <button
            style={{
              padding: '10px 18px',
              borderRadius: '8px',
              border: `1px solid ${THEME.border}`,
              background: THEME.bgCard,
              color: THEME.textSecondary,
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
              letterSpacing: '0.3px',
              transform: 'scale(1)',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.background = THEME.bgHover; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.background = THEME.bgCard; }}
            onClick={handlePrint}
            title="Print markup sheet (landscape, light theme)"
          >
            🖨️ Print
          </button>
        </div>
        
        </>)}
      </div>
      
      {/* ── Overview Mode: show unified dashboard ── */}
      {contentType === 'overview' ? (
        <OperatorOverview
          selectedClub={selectedClub}
          selectedView={selectedView}
          onNavigate={(ct) => setContentType(ct)}
        />
      ) : (
      <>
      
      {/* Stats Bar — clickable cards with drill-down */}
      {(() => {
        // Compute ingested/not-ingested counts using the helper
        const ingestedRecords = visibleRecords.filter(r => isRecordIngested(r) && !r.fields?.['Built at Venue']);
        const ingestedCount = ingestedRecords.length;
        const needsIngestRecords = visibleRecords.filter(r => !isRecordIngested(r) && !r.fields?.['Built at Venue'] && r.fields?.['Content Status'] !== 'Rejected' && r.fields?.['Content Status'] !== 'Content Unlocked');
        const needsIngestCount = needsIngestRecords.length;

        const statCards = [
          { key: 'total', value: totalRecords, label: 'Total Assets', color: '#a78bfa', items: null },
          { key: 'needsingest', value: needsIngestCount, label: 'Needs Ingesting', color: '#ef4444', hint: 'Content not yet at venue — needs ingesting', items: needsIngestRecords, alert: needsIngestCount > 0 },
          { key: 'ingested', value: ingestedCount, label: 'Ingested', color: '#34d399', hint: 'Content at venue — ready for operator QA & build', items: ingestedRecords },
          { key: 'built', value: builtCount, label: 'Built ✓', color: '#34d399', hint: 'Content built in XPression and locked', items: visibleRecords.filter(r => r.fields?.['Built at Venue']) },
          ...(deletedAssetCount > 0 ? [{ key: 'deleted', value: deletedAssetCount, label: 'Asset Deleted', color: '#ef4444', hint: 'Linked content has been deleted — needs re-linking', items: deletedAssetRecords, alert: true }] : []),
          ...(archivedAssetCount > 0 ? [{ key: 'archived', value: archivedAssetCount, label: 'Asset Archived', color: '#f59e0b', hint: 'Content has been archived in the library — club needs to restore or replace', items: archivedAssetRecords, alert: true }] : []),
          { key: 'rejected', value: rejectedCount, label: 'Content Rejected', color: '#ef4444', hint: 'Rejected — needs club revision or replacement', items: visibleRecords.filter(r => r.fields?.['Content Status'] === 'Rejected') },
          ...(contentUnlockedCount > 0 ? [{ key: 'unlocked', value: contentUnlockedCount, label: 'Content Unlocked', color: '#a855f7', hint: 'Club has re-opened content for changes', items: visibleRecords.filter(r => r.fields?.['Content Status'] === 'Content Unlocked'), alert: true }] : []),
        ];

        return (
          <>
            {/* ── Match Context Banner ── */}
            {selectedView ? (() => {
              // Format view name: OR_STK_v_COL_MCG → "St Kilda vs Collingwood — MCG"
              const CLUB_NAMES = {
                STK: 'St Kilda', CAR: 'Carlton', ESS: 'Essendon', COL: 'Collingwood',
                RIC: 'Richmond', HAW: 'Hawthorn', MEL: 'Melbourne', GEE: 'Geelong',
                NOR: 'North Melbourne', WBD: 'Western Bulldogs', GWS: 'GWS Giants',
                SYD: 'Sydney', BRL: 'Brisbane', ADL: 'Adelaide', PTA: 'Port Adelaide',
                FRE: 'Fremantle', WCE: 'West Coast', GCS: 'Gold Coast', NMFC: 'North Melbourne',
              };
              const VENUE_NAMES = {
                MCG: 'MCG', MRV: 'Marvel Stadium', SCG: 'SCG', GMHBA: 'GMHBA Stadium',
                AO: 'Adelaide Oval', OPTUS: 'Optus Stadium', GABBA: 'The Gabba',
              };
              let displayName = selectedView;
              let roundLabel = '';
              // Parse: OR_STK_v_COL_MCG or RD1_STK_v_COL_MCG
              const match = selectedView.match(/^(?:OR|RD\d+)_([A-Z]+)_v_([A-Z]+)_(.+)$/);
              if (match) {
                const home = CLUB_NAMES[match[1]] || match[1];
                const away = CLUB_NAMES[match[2]] || match[2];
                const venue = VENUE_NAMES[match[3]] || match[3];
                displayName = `${home} vs ${away}`;
                roundLabel = selectedView.startsWith('OR') ? 'Opening Round' : selectedView.match(/^(RD\d+)/)?.[1]?.replace('RD', 'Round ') || '';
                displayName += ` — ${venue}`;
              }
              return (
                <div style={{
                  padding: '18px 22px',
                  marginBottom: 14,
                  background: 'linear-gradient(135deg, rgba(0,201,167,0.08) 0%, rgba(14,165,233,0.06) 100%)',
                  border: '1px solid rgba(0,201,167,0.25)',
                  borderRadius: 10,
                  borderLeft: '4px solid #00c9a7',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <span style={{ fontSize: 24 }}>🏟️</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#f8fafc', lineHeight: 1.3 }}>
                        {displayName}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                        {roundLabel && (
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#00c9a7', background: 'rgba(0,201,167,0.12)', padding: '2px 10px', borderRadius: 10 }}>
                            {roundLabel}
                          </span>
                        )}
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>
                          {selectedView}
                        </span>
                      </div>
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#00c9a7', background: 'rgba(0,201,167,0.12)', padding: '4px 12px', borderRadius: 12, whiteSpace: 'nowrap', flexShrink: 0 }}>
                      Active View
                    </div>
                  </div>
                  {/* Warning integrated */}
                  <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid rgba(0,201,167,0.12)', display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 11, lineHeight: 1.5 }}>
                    <span style={{ fontSize: 12, flexShrink: 0 }}>⚠️</span>
                    <span style={{ color: '#94a3b8' }}>
                      <strong style={{ color: '#fbbf24' }}>Check the club's MatchFlow Builder for rows with instructions but no content.</strong>{' '}
                      Rows without a CG or LED asset linked won't appear here.
                    </span>
                  </div>
                </div>
              );
            })() : (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '14px 18px',
                marginBottom: 12,
                background: 'rgba(239,68,68,0.06)',
                border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: 8,
                borderLeft: '4px solid #ef4444',
              }}>
                <span style={{ fontSize: 18 }}>⚠️</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#fca5a5' }}>No match selected</div>
                  <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>Select a match from the dropdown above before making any changes</div>
                </div>
              </div>
            )}
            <div style={styles.statsBar}>
              {statCards.map(card => (
                <div
                  key={card.key}
                  onClick={() => card.items ? setExpandedStat(expandedStat === card.key ? null : card.key) : null}
                  style={{
                    ...styles.statCard,
                    borderLeft: `3px solid ${card.color}`,
                    cursor: card.items ? 'pointer' : 'default',
                    outline: expandedStat === card.key ? `2px solid ${card.color}` : 'none',
                    position: 'relative',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={(e) => { if (card.items) e.currentTarget.style.background = THEME.bgHover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = THEME.bgCard; }}
                  title={card.hint || ''}
                >
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <div style={{ ...styles.statValue, color: card.color }}>{card.value}</div>
                    {card.alert && card.value > 0 && (
                      <span style={{ fontSize: 14, animation: 'pulse 2s infinite' }}>⚠</span>
                    )}
                  </div>
                  <div style={styles.statLabel}>{card.label}</div>
                  {card.hint && (
                    <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: 2 }}>{card.hint}</div>
                  )}
                  {card.items && (
                    <div style={{ fontSize: '9px', color: card.color, marginTop: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                      {expandedStat === card.key ? '▼ Collapse' : '▶ View items'}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Drill-down panel */}
            {expandedStat && (() => {
              const card = statCards.find(c => c.key === expandedStat);
              if (!card?.items?.length) return null;
              return (
                <div style={{
                  background: THEME.bgCard,
                  border: `1px solid ${card.color}44`,
                  borderRadius: 8,
                  padding: '12px 16px',
                  marginBottom: 16,
                  maxHeight: 240,
                  overflowY: 'auto',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: card.color }} />
                    <span style={{ fontSize: 13, fontWeight: 700, color: card.color }}>{card.label}</span>
                    <span style={{ fontSize: 11, color: THEME.textMuted }}>— {card.items.length} item{card.items.length !== 1 ? 's' : ''}</span>
                    <div style={{ flex: 1 }} />
                    <button
                      onClick={() => setExpandedStat(null)}
                      style={{ background: 'none', border: 'none', color: THEME.textMuted, cursor: 'pointer', fontSize: 14 }}
                    >✕</button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px 140px', gap: 0, fontSize: 12 }}>
                    <div style={{ fontWeight: 700, color: THEME.textMuted, fontSize: 10, textTransform: 'uppercase', padding: '4px 0', borderBottom: `1px solid ${THEME.border}` }}>Name</div>
                    <div style={{ fontWeight: 700, color: THEME.textMuted, fontSize: 10, textTransform: 'uppercase', padding: '4px 0', borderBottom: `1px solid ${THEME.border}` }}>Category</div>
                    <div style={{ fontWeight: 700, color: THEME.textMuted, fontSize: 10, textTransform: 'uppercase', padding: '4px 0', borderBottom: `1px solid ${THEME.border}` }}>Status</div>
                    {card.items.slice(0, 30).map(r => (
                      <React.Fragment key={r.id}>
                        <div style={{ padding: '5px 0', borderBottom: `1px solid ${THEME.border}22`, color: THEME.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.fields?.['Name'] || r.fields?.['Asset Name'] || '(unnamed)'}
                        </div>
                        <div style={{ padding: '5px 0', borderBottom: `1px solid ${THEME.border}22`, color: THEME.textSecondary }}>
                          {r.fields?.['Category'] || '—'}
                        </div>
                        <div style={{ padding: '5px 0', borderBottom: `1px solid ${THEME.border}22`, color: card.color, fontWeight: 500 }}>
                          {r.fields?.['Content Status'] || 'No Status'}
                        </div>
                      </React.Fragment>
                    ))}
                    {card.items.length > 30 && (
                      <div style={{ gridColumn: '1 / -1', padding: '6px 0', color: THEME.textMuted, fontStyle: 'italic' }}>
                        ...and {card.items.length - 30} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </>
        );
      })()}
      
      {/* Error State */}

      {/* Error State */}
      {error && (
        <div style={styles.errorState}>
          <strong>Error:</strong> {error}
        </div>
      )}
      
      {/* Loading State */}
      {loading && records.length === 0 && (
        <div style={styles.loadingOverlay}>
          Loading content...
        </div>
      )}
      
      {/* Empty State */}
      {!loading && records.length === 0 && !error && (
        <div style={styles.emptyState}>
          <p>No content found for the selected filters.</p>
          <p style={{ fontSize: '13px', marginTop: '8px' }}>
            Try selecting a different club, content type, or match view.
          </p>
        </div>
      )}
      
      {/* ─── Bulk Ingest Banner ─── */}
      {records.length > 0 && (allReadyForIngest.length > 0 || settlingRecords.length > 0) && (
        <div style={{
          marginBottom: 12,
          padding: '14px 18px',
          borderRadius: 10,
          background: allReadyForIngest.length > 0 
            ? 'linear-gradient(135deg, rgba(96,165,250,0.08) 0%, rgba(14,165,233,0.06) 100%)'
            : '#1a1a1d',
          border: allReadyForIngest.length > 0 
            ? '1px solid rgba(96,165,250,0.3)' 
            : `1px solid ${THEME.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 16 }}>🚀</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0' }}>Bulk Ingest</span>
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.6 }}>
              {returningRecords.length > 0 && (
                <div>
                  <b style={{ color: '#34d399' }}>🔄 {returningRecords.length} returning</b> — already ingested at venue from a previous round. No QA needed.
                </div>
              )}
              {newStableRecords.length > 0 && (
                <div>
                  <b style={{ color: '#60a5fa' }}>{newStableRecords.length} new stable</b> — content stable for 30+ mins, no recent edits.
                </div>
              )}
              {settlingRecords.length > 0 && (
                <div>
                  <b style={{ color: '#fbbf24' }}>{settlingRecords.length} settling</b> — recently changed, waiting for stability.
                </div>
              )}
            </div>
          </div>
          
          {allReadyForIngest.length > 0 && (
            <button
              disabled={!!bulkIngestProgress}
              onClick={async () => {
                const count = allReadyForIngest.length;
                const retCount = returningRecords.length;
                const newCount = newStableRecords.length;
                const ok = await confirm({
                  title: `Ingest ${count} Row${count !== 1 ? 's' : ''}?`,
                  message: (retCount > 0 && newCount > 0 
                    ? `This will Ingest:\n\n• ${retCount} returning row${retCount !== 1 ? 's' : ''} — already ingested at venue, no QA needed\n• ${newCount} new stable row${newCount !== 1 ? 's' : ''} — unchanged for 30+ mins\n\nReject anything that needs revision before bulk ingesting.`
                    : retCount > 0
                    ? `This will Ingest ${retCount} returning row${retCount !== 1 ? 's' : ''} that are already ingested at the venue from a previous round.\n\nThese are the same assets — no QA needed.`
                    : `This will Ingest ${newCount} new stable row${newCount !== 1 ? 's' : ''} unchanged for 30+ mins.\n\nReject anything that needs revision first.`
                  ) + (settlingRecords.length > 0 ? `\n\n${settlingRecords.length} recently changed row${settlingRecords.length !== 1 ? 's' : ''} will NOT be included.` : ''),
                  confirmLabel: `⚡ Ingest ${count} Rows`,
                  cancelLabel: 'Cancel',
                  type: 'info',
                });
                if (!ok) return;
                
                setBulkIngestProgress({ current: 0, total: count });
                let done = 0;
                
                const batch = [...allReadyForIngest];
                for (let i = 0; i < batch.length; i += 5) {
                  const chunk = batch.slice(i, i + 5);
                  chunk.forEach(r => {
                    handleToggleWatchFolder(r, true);
                  });
                  done += chunk.length;
                  setBulkIngestProgress({ current: done, total: count });
                  if (i + 5 < batch.length) {
                    await new Promise(resolve => setTimeout(resolve, 300));
                  }
                }
                
                setBulkIngestProgress(null);
                showSuccess(
                  `${count} row${count !== 1 ? 's' : ''} ingested${retCount > 0 ? ` (${retCount} returning, ${newCount} new)` : ''}. Review any "settling" rows manually.`,
                  { title: `⚡ Bulk Ingest Complete — ${count} rows processed`, duration: 8000 }
                );
              }}
              style={{
                padding: '10px 20px',
                borderRadius: 8,
                border: 'none',
                background: bulkIngestProgress 
                  ? THEME.bgCard
                  : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: bulkIngestProgress ? THEME.textMuted : '#fff',
                fontSize: 13,
                fontWeight: 700,
                cursor: bulkIngestProgress ? 'not-allowed' : 'pointer',
                boxShadow: bulkIngestProgress ? 'none' : '0 2px 12px rgba(59,130,246,0.4)',
                whiteSpace: 'nowrap',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              }}
              onMouseEnter={(e) => { if (!bulkIngestProgress) { e.currentTarget.style.transform = 'scale(1.03)'; }}}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              {bulkIngestProgress 
                ? `⚡ Ingesting ${bulkIngestProgress.current}/${bulkIngestProgress.total}...`
                : `⚡ Ingest All Content (${allReadyForIngest.length})`
              }
            </button>
          )}
        </div>
      )}

      {/* Sort Toggle + Content Table by Category */}
      {records.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <button
            onClick={() => setSortByStatus(!sortByStatus)}
            style={{
              padding: '5px 14px', borderRadius: 6,
              border: `1px solid ${sortByStatus ? '#0ea5e9' : THEME.border}`,
              background: sortByStatus ? 'rgba(14, 165, 233, 0.12)' : 'transparent',
              color: sortByStatus ? '#0ea5e9' : THEME.textMuted,
              fontSize: 11, fontWeight: 700, cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {sortByStatus ? '⚡ Sorted by Status Priority' : '📋 Sorted by Cue Order'}
          </button>
        </div>
      )}

      {/* Content Table by Category — horizontal scroll with fixed sticky scrollbar */}
      <style>{`
        /* Fat vertical scrollbar for the whole page */
        ::-webkit-scrollbar {
          width: 16px;
        }
        ::-webkit-scrollbar-track {
          background: ${THEME.bg};
        }
        ::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.2);
          border-radius: 8px;
          border: 3px solid ${THEME.bg};
        }
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.4);
        }
        html {
          scrollbar-width: auto;
          scrollbar-color: rgba(255,255,255,0.2) ${THEME.bg};
        }
        .operator-scroll-container {
          overflow-x: scroll !important;
          overflow-y: visible;
          scrollbar-width: none;
        }
        .operator-scroll-container::-webkit-scrollbar {
          height: 0;
          display: none;
        }
        .operator-fixed-scrollbar {
          position: fixed;
          bottom: 0;
          left: 210px;
          right: 16px;
          z-index: 1000;
          height: 22px;
          background: ${THEME.bgElevated};
          border-top: 2px solid rgba(255,255,255,0.1);
          cursor: pointer;
          user-select: none;
        }
        .operator-fixed-scrollbar-track {
          position: absolute;
          top: 3px;
          left: 8px;
          right: 8px;
          height: 16px;
          background: rgba(255,255,255,0.06);
          border-radius: 8px;
        }
        .operator-fixed-scrollbar-thumb {
          position: absolute;
          top: 0;
          height: 16px;
          min-width: 60px;
          background: rgba(255,255,255,0.3);
          border-radius: 8px;
          cursor: grab;
          transition: background 0.1s;
        }
        .operator-fixed-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.5);
        }
        .operator-fixed-scrollbar-thumb:active {
          background: rgba(255,255,255,0.6);
          cursor: grabbing;
        }
      `}</style>
      <div ref={scrollRef} className="operator-scroll-container" onScroll={onMainScroll} style={{ 
        minWidth: 0, 
        cursor: 'grab',
      }}>
      {(() => {
        // Build ordered category list: use defined order for CG/Ribbon/IPTV, fall back to Airtable order
        const definedOrder = getOperatorCategoryOrder(contentType);
        const dataCats = allCategories.length > 0
          ? [...new Set([...allCategories, ...Object.keys(filteredRecordsByCategory)])]
              .filter(cat => filteredRecordsByCategory[cat] !== undefined)
          : Object.keys(filteredRecordsByCategory);
        
        let orderedCats;
        if (definedOrder.length > 0) {
          // Categories in defined order first, then any remaining in data order
          const inOrder = definedOrder.filter(cat => dataCats.includes(cat));
          const remaining = dataCats.filter(cat => !definedOrder.includes(cat));
          orderedCats = [...inOrder, ...remaining];
        } else {
          orderedCats = dataCats;
        }
        
        // Filter out empty categories — keep Marvel manual categories (even if empty) so operators can add content
        const isMarvelRender = selectedView && (selectedView.toUpperCase().includes('MRV') || selectedView.toUpperCase().includes('MARVEL'));
        const filteredCats = orderedCats.filter(cat => {
          const recs = filteredRecordsByCategory[cat] || [];
          if (recs.length > 0) return true;
          if (isMarvelRender && isManualOrOperatorCategory(cat, contentType)) return true;
          return false;
        });
        
        // Force venue/manual categories to absolute bottom (after Team List and any other content)
        const venueCategories = filteredCats.filter(cat => isManualOrOperatorCategory(cat, contentType));
        const nonVenueCategories = filteredCats.filter(cat => !isManualOrOperatorCategory(cat, contentType));
        const finalCats = [...nonVenueCategories, ...venueCategories];
        
        return finalCats;
      })().map(category => {
        const categoryRecords = filteredRecordsByCategory[category] || [];
        return (
        <CategorySection
          key={category}
          category={category}
          records={categoryRecords}
          selectedRecords={selectedRecords}
          onSelectRecord={handleSelectRecord}
          onSelectAllInCategory={handleSelectAllInCategory}
          onUpdateStatus={(record, newStatus) => {
            updateStatus(record, newStatus);
            const label = newStatus === 'Rejected' ? 'Rejected' : newStatus === 'Ready for Testing' ? 'Returned to Review' : 'Status Updated';
            stampAction(record.id, record, label);
          }}
          onUpdateComments={updateComments}
          onUpdateTakeId={updateTakeId}
          onToggleWatchFolder={handleToggleWatchFolder}
          onForceResend={forceResend}
          onToggleBuiltAtVenue={handleToggleBuiltAtVenue}
          onUnlock={handleUnlock}
          actionedBy={actionedBy}
          onDelete={handleDelete}
          onEdit={handleEdit}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDrop={handleDrop}
          draggingRecord={draggingRecord}
          contentType={contentType}
          sortByStatus={sortByStatus}
          stableReadyIds={stableReadyIds}
          settlingIds={settlingIds}
          returningIds={returningIds}
          isManualCategory={isManualOrOperatorCategory(category, contentType)}
          staleSourceIds={staleSourceIds}
          onQuickAdd={handleQuickAdd}
          onOpenPreview={(preview) => {
            if (preview.type === 'tiled') setActiveRibbonPreview(preview);
            else if (preview.type === '360') setActiveThreeSixtyPreview(preview);
          }}
        />
        );
      })}
      </div>
      
      </>
      )}
      
      {/* Fixed scrollbar — custom rendered, always visible at bottom of viewport */}
      {records.length > 0 && (
        <div ref={stickyRef} className="operator-fixed-scrollbar">
          <div className="operator-fixed-scrollbar-track">
            <div className="operator-fixed-scrollbar-thumb" />
          </div>
        </div>
      )}
      
      {/* Themed Dialogs (confirm/alert) */}
      {DialogComponents}
      
      {/* Toast notifications (top-right) */}
      {ToastComponent}
      
      {/* Add Row Modal */}
      <AddRowModal
        isOpen={showAddRowModal}
        onClose={() => setShowAddRowModal(false)}
        onSubmit={async (fields) => {
          await createRow(selectedClub, fields);
        }}
        categories={allCategories}
        existingCategories={Object.keys(filteredRecordsByCategory)}
        allRecords={records}
        contentType={contentType}
        selectedClub={selectedClub}
        selectedView={selectedView}
        clubs={clubs}
      />
      
      {/* Edit Row Modal */}
      <EditRowModal
        isOpen={!!editingRecord}
        record={editingRecord}
        isManualCategory={editingRecord ? isManualOrOperatorCategory(editingRecord.fields?.['Category'] || '', contentType) : false}
        onClose={() => setEditingRecord(null)}
        onSave={async (recordId, updates) => {
          console.log('[EditRowModal] Saving updates:', updates);
          
          // Import updateOperatorRecord for generic field updates
          const { updateOperatorRecord } = await import('../../services/operatorAirtable');
          
          // Filter out computed fields that can't be updated directly
          const writableUpdates = { ...updates };
          delete writableUpdates['Lumen8 ID']; // Computed lookup field
          delete writableUpdates['Venue']; // Computed lookup field
          
          if (Object.keys(writableUpdates).length > 0) {
            try {
              const updated = await updateOperatorRecord(selectedClub, contentType, recordId, writableUpdates);
              console.log('[EditRowModal] Updated record:', updated);
              
              // Update local state
              if (updated) {
                // Trigger a refresh to get the updated data
                refresh();
              }
            } catch (err) {
              console.error('[EditRowModal] Failed to update:', err);
              throw err;
            }
          }
          
          setEditingRecord(null);
        }}
        onDelete={handleDelete}
        categories={allCategories}
        contentType={contentType}
        availableAssets={availableAssets}
      />
      
      {/* Download Bundles Modal — grouped by category with zip-per-section */}

      {showDownloadModal && (() => {
        const byCategory = downloadModalItems; // Already grouped: { category: [items] }
        const categories = Object.keys(byCategory);
        const totalFiles = Object.values(byCategory).reduce((sum, arr) => sum + arr.length, 0);

        // Zip download handler per category
        // Batched download handler per category — opens direct download links
        const handleDownloadBundle = async (category, items) => {
          const statusEl = document.getElementById(`dl-status-${category.replace(/\s+/g, '_')}`);
          const setStatus = (msg) => { if (statusEl) statusEl.textContent = msg; };

          try {
            setStatus(`Starting ${items.length} downloads...`);
            let started = 0;

            for (const item of items) {
              // Use hidden iframe to trigger download without navigating away
              const iframe = document.createElement('iframe');
              iframe.style.display = 'none';
              iframe.src = item.url;
              document.body.appendChild(iframe);
              started++;
              setStatus(`Downloading ${started}/${items.length}: ${item.name.substring(0, 30)}...`);
              
              // Small delay between downloads to avoid browser throttling
              await new Promise(r => setTimeout(r, 500));
              
              // Clean up iframe after a delay
              setTimeout(() => {
                try { document.body.removeChild(iframe); } catch {}
              }, 10000);
            }

            setStatus(`✅ Started ${started} download${started !== 1 ? 's' : ''} — check your Downloads folder`);
          } catch (err) {
            console.error(`[Download] Bundle error for ${category}:`, err);
            setStatus(`❌ Error: ${err.message}`);
          }
        };

        return (
          <div 
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.7)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 10000,
            }}
            onClick={() => setShowDownloadModal(false)}
          >
            <div 
              style={{
                background: THEME.bgCard,
                borderRadius: '16px',
                padding: '24px',
                maxWidth: '700px',
                width: '90%',
                maxHeight: '85vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <h3 style={{ margin: 0, color: THEME.textPrimary, fontSize: '18px', fontWeight: 700 }}>
                    📦 Download All
                  </h3>
                  <p style={{ margin: '4px 0 0', color: THEME.textMuted, fontSize: '12px' }}>
                    {totalFiles} files across {categories.length} section{categories.length !== 1 ? 's' : ''} — downloads per section to your Downloads folder
                  </p>
                </div>
                <button
                  onClick={() => setShowDownloadModal(false)}
                  style={{ background: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', color: THEME.textMuted, padding: '4px' }}
                >×</button>
              </div>
              
              <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4 }}>
                {categories.map(cat => {
                  const items = byCategory[cat];
                  const safeId = cat.replace(/\s+/g, '_');
                  return (
                    <div key={cat} style={{
                      border: `1px solid ${THEME.border}`,
                      borderRadius: '8px',
                      marginBottom: 10,
                      background: THEME.bgElevated,
                    }}>
                      {/* Section header — fixed height */}
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '10px 16px',
                        borderBottom: `1px solid ${THEME.border}`,
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: THEME.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat}</div>
                          <div style={{ fontSize: '10px', color: THEME.textMuted, marginTop: 1 }}>{items.length} file{items.length !== 1 ? 's' : ''}</div>
                        </div>
                        <span id={`dl-status-${safeId}`} style={{
                          fontSize: '10px',
                          color: THEME.textMuted,
                          fontStyle: 'italic',
                          whiteSpace: 'nowrap',
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }} />
                        <button
                          onClick={() => handleDownloadBundle(cat, items)}
                          style={{
                            padding: '6px 14px',
                            borderRadius: '6px',
                            border: 'none',
                            background: 'linear-gradient(135deg, #00c9a7, #0ea5e9)',
                            color: '#fff',
                            fontSize: '11px',
                            fontWeight: 700,
                            cursor: 'pointer',
                            flexShrink: 0,
                            transition: 'transform 0.15s',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.03)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                          📥 Download
                        </button>
                      </div>
                      
                      {/* File list — compact, scrollable */}
                      <div style={{ maxHeight: 100, overflowY: 'auto', padding: '4px 0' }}>
                        {items.map((item, idx) => (
                          <div key={idx} style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            padding: '3px 16px',
                            fontSize: '11px',
                            color: THEME.textSecondary,
                          }}>
                            <span style={{ color: '#3b82f6', fontSize: 9, flexShrink: 0 }}>•</span>
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
                            <a href={item.url} target="_blank" rel="noopener noreferrer"
                              style={{ color: '#3b82f6', fontSize: 10, textDecoration: 'none', flexShrink: 0 }}
                            >↗</a>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div style={{ marginTop: '16px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowDownloadModal(false)}
                  style={{
                    padding: '10px 20px', borderRadius: '8px',
                    border: `1px solid ${THEME.border}`,
                    background: 'transparent', color: THEME.textSecondary,
                    cursor: 'pointer', fontSize: '13px',
                  }}
                >Close</button>
              </div>
            </div>
          </div>
        );
      })()}
      {/* Dashboard-level Tiled/360 Preview Modals — rendered outside row lifecycle to survive polling re-renders */}
      {activeRibbonPreview && (
        <RibbonPreviewModal
          asset={activeRibbonPreview.asset}
          ribbonItems={activeRibbonPreview.ribbonItems}
          onClose={() => setActiveRibbonPreview(null)}
        />
      )}
      {activeThreeSixtyPreview && (
        <ThreeSixtyPreviewModal
          asset={activeThreeSixtyPreview.asset}
          ribbonItems={activeThreeSixtyPreview.ribbonItems}
          onClose={() => setActiveThreeSixtyPreview(null)}
        />
      )}
    </div>
  );
}

// ================= ADD CONTENT MENU (Dropdown linking to uploaders) =================

// ================= ADD ROW MODAL =================

// Asset Destination filters by content type for Operator View
const DESTINATION_FILTERS = {
  cg: ['Superscreen', 'Corner Screen', 'Marvel', 'MRV'],
  ribbon: ['LED Ribbon', 'LED', 'Ribbon', 'LED - Ribbon', 'LED-Ribbon', 'LED Signage', 'LED - All Types'],
  led: ['LED - All Types', 'LED', 'LED Ribbon', 'LED Signage'],
  tvc: ['Big Screen - TVC', 'Corner Screen - TVC', 'TVC'],
  iptv: ['IPTV', 'iptv', 'IPTV GFX', 'IPTV -', 'MRV-iptv', 'MCG-iptv'],
};

function AddRowModal({ isOpen, onClose, onSubmit, categories, existingCategories, allRecords, contentType, selectedClub, selectedView, clubs }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  
  // Merge categories from API + records as fallback (ensures we always have categories)
  const mergedCategories = React.useMemo(() => {
    const catSet = new Set(categories || []);
    // Also extract from all loaded records as fallback
    if (allRecords) {
      allRecords.forEach(r => {
        const cat = r.fields?.Category;
        if (cat) catSet.add(cat);
      });
    }
    return [...catSet].sort((a, b) => a.localeCompare(b));
  }, [categories, allRecords]);
  const [lumenId, setLumenId] = useState('');
  const [selectedAssetRecordId, setSelectedAssetRecordId] = useState(null);
  const [assetFilter, setAssetFilter] = useState('');
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const [comments, setComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Asset picker state
  const [allAssets, setAllAssets] = useState([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  
  // Fixture state (for linking to specific match views)
  const [fixtures, setFixtures] = useState([]);
  const [selectedFixtureId, setSelectedFixtureId] = useState('');
  const [isLoadingFixtures, setIsLoadingFixtures] = useState(false);
  
  // Auto-detect venue from view name (e.g., "OR_STK_v_COL_MCG" → MCG)
  const detectedVenue = React.useMemo(() => {
    if (!selectedView) return null;
    const viewUpper = selectedView.toUpperCase();
    
    // Check for venue codes in the view name
    if (viewUpper.includes('_MCG') || viewUpper.endsWith('MCG')) return 'MCG';
    if (viewUpper.includes('_MRV') || viewUpper.includes('MARVEL') || viewUpper.endsWith('MRV')) return 'Marvel Stadium';
    if (viewUpper.includes('_GMHBA') || viewUpper.includes('KARDINIA')) return 'GMHBA Stadium';
    if (viewUpper.includes('_AO') || viewUpper.includes('ADELAIDE')) return 'Adelaide Oval';
    if (viewUpper.includes('_OPTUS') || viewUpper.includes('PERTH')) return 'Optus Stadium';
    if (viewUpper.includes('_SCG') || viewUpper.includes('SYDNEY')) return 'SCG';
    if (viewUpper.includes('_GABBA') || viewUpper.includes('BRISBANE')) return 'Gabba';
    
    return null;
  }, [selectedView]);
  
  // Track if modal just opened to reset form
  const prevIsOpen = useRef(false);
  
  // Get current club name for display
  const currentClub = clubs.find(c => c.id === selectedClub);
  const clubName = currentClub?.name || selectedClub;
  
  // Load assets and fixtures when modal opens
  useEffect(() => {
    if (isOpen && !prevIsOpen.current) {
      // Reset form
      setName('');
      setCategory('');
      setLumenId('');
      setSelectedAssetRecordId(null);
      setAssetFilter('');
      setComments('');
      setError('');
      setSelectedFixtureId('');
      setAllAssets([]);
      setFixtures([]);
      
      // Load assets for this content type
      const loadAssets = async () => {
        setIsLoadingAssets(true);
        try {
          const { fetchAllAssets } = await import('../../airtable');
          const assetsMap = await fetchAllAssets();
          const allAssetsList = Array.from(assetsMap.values());
          const destinations = DESTINATION_FILTERS[contentType] || [];
          
          // Log unique destinations for debugging
          const uniqueDestinations = [...new Set(allAssetsList.map(a => a.destination).filter(Boolean))];
          console.log(`[AddRowModal] Total assets: ${allAssetsList.length}`);
          console.log(`[AddRowModal] Unique destinations in library:`, uniqueDestinations);
          console.log(`[AddRowModal] Filtering for ${contentType}:`, destinations);
          
          // Convert map to array and filter by destination
          const filtered = allAssetsList.filter(asset => {
            if (destinations.length === 0) return true;
            return destinations.some(dest => 
              asset.destination?.toLowerCase().includes(dest.toLowerCase())
            );
          });
          
          // Sort by title
          filtered.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
          
          console.log(`[AddRowModal] Filtered to ${filtered.length} ${contentType} assets`);
          setAllAssets(filtered);
        } catch (err) {
          console.error('[AddRowModal] Failed to load assets:', err);
          setAllAssets([]);
        } finally {
          setIsLoadingAssets(false);
        }
      };
      
      // Load fixtures for CG/Ribbon content types
      const loadFixtures = async () => {
        if (selectedClub === 'all' || !['cg', 'ribbon'].includes(contentType)) {
          setFixtures([]);
          return;
        }
        
        setIsLoadingFixtures(true);
        try {
          const { fetchOperatorFixtures } = await import('../../services/operatorAirtable');
          const fixtureRecords = await fetchOperatorFixtures(selectedClub);
          
          // Map to objects with id, name, and venue
          // Log first record's fields to see what's available
          if (fixtureRecords.length > 0) {
            console.log('[AddRowModal] First fixture record fields:', Object.keys(fixtureRecords[0].fields || {}));
            console.log('[AddRowModal] First fixture record:', fixtureRecords[0].fields);
          }
          
          const fixtureList = fixtureRecords.map(r => {
            const fields = r.fields || {};
            // Try many possible field names for the fixture name
            const name = fields['Match ID']  // STK uses this
              || fields.Name 
              || fields['Match Name'] 
              || fields['Fixture'] 
              || fields['Fixture Name']
              || fields['Match']
              || fields['Round']
              || fields['Title']
              || fields['Game']
              || fields['Opposition']
              || fields['Opponent']
              || fields['vs']
              || 'Unknown';
            
            return {
              id: r.id,
              name: name,
              venue: fields.Venue || fields['Venue'] || '',
            };
          });
          
          // Sort by name
          fixtureList.sort((a, b) => a.name.localeCompare(b.name));
          
          console.log(`[AddRowModal] Loaded ${fixtureList.length} fixtures:`, fixtureList.map(f => f.name));
          console.log(`[AddRowModal] Current view: "${selectedView}"`);
          setFixtures(fixtureList);
          
          // Auto-select fixture if selectedView matches a fixture name
          // Try multiple matching strategies
          if (selectedView && selectedView !== 'All Rounds' && selectedView !== 'All Match View Content') {
            const viewLower = selectedView.toLowerCase();
            const viewUpper = selectedView.toUpperCase();
            
            // Parse view name format: OR_STK_v_COL_MCG → opponent=COL, venue=MCG
            const viewParts = viewUpper.split('_');
            const opponentCode = viewParts.find((p, i) => viewParts[i-1] === 'V') || '';
            const venueCode = viewParts[viewParts.length - 1] || '';
            
            // Club code to name mapping
            const CLUB_CODES = {
              'ADE': 'adelaide', 'BRI': 'brisbane', 'CAR': 'carlton', 'COL': 'collingwood',
              'ESS': 'essendon', 'FRE': 'fremantle', 'GEE': 'geelong', 'GCS': 'gold coast',
              'GWS': 'gws', 'HAW': 'hawthorn', 'MEL': 'melbourne', 'NOR': 'north melbourne',
              'POR': 'port adelaide', 'RIC': 'richmond', 'STK': 'st kilda', 'SYD': 'sydney',
              'WCE': 'west coast', 'WBD': 'western bulldogs', 'DOG': 'bulldogs'
            };
            const opponentName = CLUB_CODES[opponentCode] || opponentCode.toLowerCase();
            
            console.log(`[AddRowModal] Parsed view: opponent="${opponentCode}"→"${opponentName}", venue="${venueCode}"`);
            
            let matchingFixture = null;
            
            // Strategy 1: Exact match on fixture name
            matchingFixture = fixtureList.find(f => 
              f.name.toLowerCase() === viewLower
            );
            
            // Strategy 2: Fixture name contains opponent name
            if (!matchingFixture && opponentName) {
              matchingFixture = fixtureList.find(f => 
                f.name.toLowerCase().includes(opponentName)
              );
              if (matchingFixture) console.log(`[AddRowModal] Matched by opponent name: ${opponentName}`);
            }
            
            // Strategy 3: View name contains fixture name or vice versa
            if (!matchingFixture) {
              matchingFixture = fixtureList.find(f => 
                viewLower.includes(f.name.toLowerCase()) ||
                f.name.toLowerCase().includes(viewLower)
              );
            }
            
            // Strategy 4: Match by venue code in fixture name
            if (!matchingFixture && venueCode) {
              matchingFixture = fixtureList.find(f => 
                f.name.toUpperCase().includes(venueCode) ||
                (f.venue && f.venue.toUpperCase().includes(venueCode))
              );
              if (matchingFixture) console.log(`[AddRowModal] Matched by venue: ${venueCode}`);
            }
            
            // Strategy 5: Extract round number and match
            if (!matchingFixture) {
              const viewRound = viewLower.match(/r(?:ound|d)?\.?\s*(\d+)/i);
              if (viewRound) {
                const roundNum = viewRound[1];
                matchingFixture = fixtureList.find(f => {
                  const fixtureRound = f.name.toLowerCase().match(/r(?:ound|d)?\.?\s*(\d+)/i);
                  return fixtureRound && fixtureRound[1] === roundNum;
                });
              }
            }
            
            if (matchingFixture) {
              setSelectedFixtureId(matchingFixture.id);
              console.log(`[AddRowModal] ✅ Auto-selected fixture: ${matchingFixture.name} (${matchingFixture.id})`);
            } else {
              console.log(`[AddRowModal] ⚠️ No matching fixture found for view: ${selectedView}`);
              console.log(`[AddRowModal] Available fixtures:`, fixtureList.map(f => f.name));
            }
          }
        } catch (err) {
          console.error('[AddRowModal] Failed to load fixtures:', err);
          setFixtures([]);
        } finally {
          setIsLoadingFixtures(false);
        }
      };
      
      loadAssets();
      loadFixtures();
    }
    prevIsOpen.current = isOpen;
  }, [isOpen, contentType, selectedClub, selectedView]);
  
  // Filter assets based on search input
  const filteredAssets = allAssets.filter(asset => {
    if (!assetFilter) return true;
    const search = assetFilter.toLowerCase();
    return (
      (asset.title && asset.title.toLowerCase().includes(search)) ||
      (asset.lumenId && asset.lumenId.toLowerCase().includes(search))
    );
  });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (!category) {
      setError('Category is required');
      return;
    }
    if (selectedClub === 'all') {
      setError('Please select a specific club first');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      const fields = {
        Name: name.trim(),
        Category: category,
        'Lumen8 Comments': comments || undefined,
        'Content Status': 'New Submission',
      };
      
      // Note: Manually added rows are identified by the absence of 'Source Record ID'
      // which only exists on rows that flow from MatchFlow Builder via Game Timeline
      
      // If a Lumen8 ID is selected, we need to link the asset via CG Content/Ribbon Content
      // NOT by setting 'Lumen8 ID' directly (it's a computed lookup field)
      if (lumenId && selectedAssetRecordId) {
        // Link via the appropriate content field
        const contentField = contentType === 'cg' ? 'CG Content' : 'Ribbon Content';
        fields[contentField] = [selectedAssetRecordId]; // Airtable expects array for linked records
      }
      
      // Link to fixture if selected (makes row appear in that match view)
      if (selectedFixtureId) {
        fields['Fixtures'] = [selectedFixtureId]; // Airtable expects array for linked records
        console.log(`[AddRowModal] ✅ Linking to fixture: ${selectedFixtureId}`);
      } else {
        console.log(`[AddRowModal] ⚠️ No fixture selected - row will go to All Rounds`);
      }
      
      // NOTE: Venue is a computed/lookup field in Airtable - don't try to set it directly
      // It will be auto-populated from the linked Fixture
      
      console.log('[AddRowModal] Creating row with fields:', JSON.stringify(fields, null, 2));
      
      await onSubmit(fields, selectedClub);
      
      onClose();
    } catch (err) {
      console.error('Failed to create row:', err);
      setError(err.message || 'Failed to create row');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleSelectAsset = (asset) => {
    setLumenId(asset.lumenId || asset.title);
    setAssetFilter(asset.title || asset.lumenId || '');
    setShowAssetPicker(false);
    
    // Auto-fill name if empty
    if (!name && asset.title) {
      setName(asset.title);
    }
    
    // Use the Airtable record ID for linking
    if (asset.id) {
      setSelectedAssetRecordId(asset.id);
      console.log('[AddRowModal] Selected asset:', asset.id, asset.title);
    } else {
      console.warn('[AddRowModal] Asset missing record ID:', asset.title);
      setSelectedAssetRecordId(null);
    }
  };
  
  if (!isOpen) return null;
  
  // Show warning if viewing all clubs
  const isAllClubs = selectedClub === 'all';
  
  // Sort categories - existing ones first, then others
  const sortedCategories = [...mergedCategories].sort((a, b) => {
    const aExists = existingCategories?.includes(a);
    const bExists = existingCategories?.includes(b);
    if (aExists && !bExists) return -1;
    if (!aExists && bExists) return 1;
    return a.localeCompare(b);
  });
  
  return (
    <>
      {/* Backdrop */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          zIndex: 1000,
        }}
        onClick={onClose}
      />
      
      {/* Modal - stop propagation so clicks don't close it */}
      <div 
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: THEME.bgElevated,
          borderRadius: '16px',
          border: `1px solid ${THEME.border}`,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
          zIndex: 1001,
          width: '100%',
          maxWidth: '450px',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: `1px solid ${THEME.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div>
            <h2 style={{ 
              fontSize: '18px', 
              fontWeight: 600, 
              color: THEME.textPrimary,
              margin: 0,
            }}>
              Add New Row
            </h2>
            <p style={{ 
              fontSize: '13px', 
              color: THEME.textMuted, 
              margin: '4px 0 0 0',
            }}>
              {contentType.toUpperCase()} • {isAllClubs ? 'All Clubs' : clubName} {selectedView ? `• ${selectedView}` : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            type="button"
            style={{
              background: 'transparent',
              border: 'none',
              color: THEME.textMuted,
              fontSize: '20px',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            ✕
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          {/* Warning if all clubs selected */}
          {isAllClubs && (
            <div style={{
              padding: '12px 14px',
              background: 'rgba(251, 191, 36, 0.15)',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '13px',
              color: '#fbbf24',
            }}>
              ⚠️ Please select a specific club from the dropdown above to add a new row.
            </div>
          )}
          
          {/* Name */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '12px',
              fontWeight: 500,
              color: THEME.textSecondary,
              marginBottom: '6px',
            }}>
              Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={(e) => e.stopPropagation()}
              placeholder="Enter content name"
              disabled={isAllClubs}
              style={{
                width: '100%',
                padding: '10px 12px',
                background: THEME.bgCard,
                border: `1px solid ${THEME.border}`,
                borderRadius: '8px',
                color: THEME.textPrimary,
                fontSize: '14px',
                outline: 'none',
                opacity: isAllClubs ? 0.5 : 1,
              }}
              autoFocus
            />
          </div>
          
          {/* Category */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '12px',
              fontWeight: 500,
              color: THEME.textSecondary,
              marginBottom: '6px',
            }}>
              Category *
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={isAllClubs}
              style={{
                width: '100%',
                padding: '10px 12px',
                background: THEME.bgCard,
                border: `1px solid ${THEME.border}`,
                borderRadius: '8px',
                color: THEME.textPrimary,
                fontSize: '14px',
                outline: 'none',
                cursor: isAllClubs ? 'not-allowed' : 'pointer',
                opacity: isAllClubs ? 0.5 : 1,
              }}
            >
              <option value="">Select category...</option>
              {existingCategories && existingCategories.length > 0 && (
                <optgroup label="Current Categories">
                  {sortedCategories.filter(cat => existingCategories.includes(cat)).map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </optgroup>
              )}
              <optgroup label="All Categories">
                {sortedCategories.filter(cat => !existingCategories?.includes(cat)).map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </optgroup>
            </select>
          </div>
          
          {/* Fixture (Match) - Required for match views */}
          {['cg', 'ribbon'].includes(contentType) && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 500,
                color: THEME.textSecondary,
                marginBottom: '6px',
              }}>
                Match/Fixture {selectedView && selectedView !== 'All Rounds' && selectedView !== 'All Match View Content' ? <span style={{ color: THEME.orange }}>* REQUIRED</span> : '(optional)'}
              </label>
              <select
                value={selectedFixtureId}
                onChange={(e) => setSelectedFixtureId(e.target.value)}
                disabled={isAllClubs || isLoadingFixtures}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: THEME.bgCard,
                  border: `1px solid ${selectedFixtureId ? THEME.accent : (!selectedFixtureId && selectedView && selectedView !== 'All Rounds' && selectedView !== 'All Match View Content') ? THEME.orange : THEME.border}`,
                  borderRadius: '8px',
                  color: THEME.textPrimary,
                  fontSize: '14px',
                  outline: 'none',
                  cursor: isAllClubs ? 'not-allowed' : 'pointer',
                  opacity: isAllClubs ? 0.5 : 1,
                }}
              >
                <option value="">-- Select a fixture --</option>
                {fixtures.map(fixture => (
                  <option key={fixture.id} value={fixture.id}>{fixture.name}{fixture.venue ? ` - ${fixture.venue}` : ''}</option>
                ))}
              </select>
              <div style={{ 
                fontSize: '11px', 
                color: selectedFixtureId ? THEME.accent : (selectedView && selectedView !== 'All Rounds' && selectedView !== 'All Match View Content') ? THEME.orange : THEME.textMuted, 
                marginTop: '4px',
                fontWeight: selectedFixtureId ? 400 : 500,
              }}>
                {isLoadingFixtures ? '⏳ Loading fixtures...' : 
                  fixtures.length === 0 ? '⚠️ No fixtures found for this club' :
                  selectedFixtureId ? '✓ Row will appear in this match view' : 
                  (selectedView && selectedView !== 'All Rounds' && selectedView !== 'All Match View Content') ?
                    '⚠️ Select a fixture or row will go to All Rounds!' : 
                    'Row will appear in All Rounds only'}
              </div>
            </div>
          )}
          
          {/* Venue - Auto-detected from view (read-only, populated from Fixture) */}
          {['cg', 'ribbon'].includes(contentType) && detectedVenue && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 500,
                color: THEME.textSecondary,
                marginBottom: '6px',
              }}>
                Venue (auto-detected)
              </label>
              <div
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: THEME.bgHover,
                  border: `1px solid ${THEME.accent}`,
                  borderRadius: '8px',
                  color: THEME.textPrimary,
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span style={{ color: THEME.accent }}>📍</span>
                {detectedVenue}
                <span style={{ marginLeft: 'auto', fontSize: '11px', color: THEME.textMuted }}>
                  (from fixture)
                </span>
              </div>
            </div>
          )}
          
          {/* Asset Picker - Link to uploaded content */}
          <div style={{ marginBottom: '16px', position: 'relative' }}>
            <label style={{
              display: 'block',
              fontSize: '12px',
              fontWeight: 500,
              color: THEME.textSecondary,
              marginBottom: '4px',
            }}>
              Link Content
            </label>
            <div style={{
              fontSize: '10px',
              color: THEME.textMuted,
              marginBottom: '6px',
            }}>
              {isLoadingAssets 
                ? '⏳ Loading assets...' 
                : `${allAssets.length} ${contentType.toUpperCase()} assets available`
              }
            </div>
            
            {/* Selected asset display / picker trigger */}
            <div
              onClick={() => !isAllClubs && !isLoadingAssets && setShowAssetPicker(!showAssetPicker)}
              style={{
                width: '100%',
                padding: '10px 12px',
                background: THEME.bgCard,
                border: `1px solid ${selectedAssetRecordId ? THEME.accent : THEME.border}`,
                borderRadius: '8px',
                color: selectedAssetRecordId ? THEME.textPrimary : THEME.textMuted,
                fontSize: '14px',
                cursor: isAllClubs || isLoadingAssets ? 'not-allowed' : 'pointer',
                opacity: isAllClubs ? 0.5 : 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedAssetRecordId ? assetFilter : 'Click to select an asset...'}
              </span>
              <span style={{ color: THEME.textMuted, fontSize: '12px', flexShrink: 0, marginLeft: 8 }}>
                {selectedAssetRecordId ? '✓' : '▼'}
              </span>
            </div>
            
            {/* Clear selection button */}
            {selectedAssetRecordId && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedAssetRecordId(null);
                  setLumenId('');
                  setAssetFilter('');
                }}
                style={{
                  position: 'absolute',
                  right: '36px',
                  top: '50px',
                  background: 'transparent',
                  border: 'none',
                  color: THEME.textMuted,
                  cursor: 'pointer',
                  fontSize: '14px',
                  padding: '4px',
                }}
              >
                ✕
              </button>
            )}
            
            {/* Asset Picker Dropdown */}
            {showAssetPicker && !isAllClubs && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: THEME.bgCard,
                border: `1px solid ${THEME.border}`,
                borderRadius: '8px',
                marginTop: '4px',
                zIndex: 10,
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                maxHeight: '300px',
                display: 'flex',
                flexDirection: 'column',
              }}>
                {/* Filter input */}
                <div style={{ padding: '8px', borderBottom: `1px solid ${THEME.border}` }}>
                  <input
                    type="text"
                    value={assetFilter}
                    onChange={(e) => setAssetFilter(e.target.value)}
                    placeholder="Filter assets..."
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      background: THEME.bgHover,
                      border: `1px solid ${THEME.border}`,
                      borderRadius: '6px',
                      color: THEME.textPrimary,
                      fontSize: '13px',
                      outline: 'none',
                    }}
                  />
                </div>
                
                {/* Asset list */}
                <div style={{ overflowY: 'auto', maxHeight: '240px' }}>
                  {filteredAssets.length > 0 ? (
                    filteredAssets.slice(0, 50).map((asset, idx) => (
                      <div
                        key={asset.id || idx}
                        onClick={() => handleSelectAsset(asset)}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          borderBottom: `1px solid ${THEME.border}`,
                          transition: 'background 0.15s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = THEME.bgHover}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        {/* Thumbnail */}
                        {asset.thumb ? (
                          <img 
                            src={asset.thumb} 
                            alt="" 
                            style={{ 
                              width: 40, 
                              height: 40, 
                              objectFit: 'cover', 
                              borderRadius: 4,
                              flexShrink: 0,
                            }} 
                          />
                        ) : (
                          <div style={{ 
                            width: 40, 
                            height: 40, 
                            background: THEME.bgHover, 
                            borderRadius: 4,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '16px',
                            flexShrink: 0,
                          }}>
                            🎬
                          </div>
                        )}
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ 
                            color: THEME.textPrimary, 
                            fontSize: '13px', 
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {asset.title || 'Untitled'}
                          </div>
                          {asset.destination && (
                            <div style={{ 
                              color: THEME.textMuted, 
                              fontSize: '11px', 
                              marginTop: '2px' 
                            }}>
                              {asset.destination}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ 
                      padding: '20px', 
                      textAlign: 'center', 
                      color: THEME.textMuted,
                      fontSize: '13px',
                    }}>
                      {assetFilter ? `No assets matching "${assetFilter}"` : 'No assets available'}
                    </div>
                  )}
                  {filteredAssets.length > 50 && (
                    <div style={{ 
                      padding: '10px', 
                      textAlign: 'center', 
                      color: THEME.textMuted,
                      fontSize: '11px',
                      borderTop: `1px solid ${THEME.border}`,
                    }}>
                      Showing 50 of {filteredAssets.length} — use filter to narrow results
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Click outside to close */}
            {showAssetPicker && (
              <div 
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 9,
                }}
                onClick={() => setShowAssetPicker(false)}
              />
            )}
          </div>
          
          {/* Comments */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '12px',
              fontWeight: 500,
              color: THEME.textSecondary,
              marginBottom: '6px',
            }}>
              Lumen8 Comments
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              onBlur={(e) => e.stopPropagation()}
              placeholder="Optional notes..."
              rows={2}
              disabled={isAllClubs}
              style={{
                width: '100%',
                padding: '10px 12px',
                background: THEME.bgCard,
                border: `1px solid ${THEME.border}`,
                borderRadius: '8px',
                color: THEME.textPrimary,
                fontSize: '14px',
                outline: 'none',
                resize: 'vertical',
                fontFamily: 'inherit',
                opacity: isAllClubs ? 0.5 : 1,
              }}
            />
          </div>
          
          {/* Error */}
          {error && (
            <div style={{
              padding: '10px 12px',
              background: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              color: '#ef4444',
              fontSize: '13px',
              marginBottom: '16px',
            }}>
              {error}
            </div>
          )}
          
          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '10px 20px',
                background: 'transparent',
                border: `1px solid ${THEME.border}`,
                borderRadius: '8px',
                color: THEME.textSecondary,
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isAllClubs}
              style={{
                padding: '10px 20px',
                background: (isSubmitting || isAllClubs) ? THEME.bgHover : 'linear-gradient(135deg, #2fd6b5 0%, #1a9d87 100%)',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                cursor: (isSubmitting || isAllClubs) ? 'not-allowed' : 'pointer',
                opacity: (isSubmitting || isAllClubs) ? 0.7 : 1,
              }}
            >
              {isSubmitting ? 'Creating...' : 'Create Row'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

// ================= EDIT ROW MODAL =================

function EditRowModal({ isOpen, record, onClose, onSave, onDelete, categories, contentType, availableAssets, isManualCategory }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [lumenId, setLumenId] = useState('');
  const [lumenIdSearch, setLumenIdSearch] = useState('');
  const [showLumenIdDropdown, setShowLumenIdDropdown] = useState(false);
  const [comments, setComments] = useState('');
  const [takeId, setTakeId] = useState('');
  const [playoutDuration, setPlayoutDuration] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [selectedAssetRecordId, setSelectedAssetRecordId] = useState(null);
  
  // Full asset library (loaded on open, filtered by content type destination)
  const [allLibraryAssets, setAllLibraryAssets] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);

  // Load full asset library when modal opens
  useEffect(() => {
    if (isOpen && record) {
      const loadFullLibrary = async () => {
        setIsLoadingAssets(true);
        try {
          const { fetchAllAssets } = await import('../../airtable');
          const assetsMap = await fetchAllAssets();
          const allAssetsList = Array.from(assetsMap.values());
          const destinations = DESTINATION_FILTERS[contentType] || [];
          
          const filtered = allAssetsList.filter(asset => {
            if (destinations.length === 0) return true;
            return destinations.some(dest =>
              asset.destination?.toLowerCase().includes(dest.toLowerCase())
            );
          });
          
          const mapped = filtered.map(asset => ({
            lumenId: asset.lumenId || '',
            name: asset.title || '',
            recordId: asset.id || '',
            thumb: asset.thumb || '',
          })).filter(a => a.lumenId);
          
          console.log(`[EditRowModal] Loaded ${mapped.length} ${contentType} assets from library (sample:`, mapped.slice(0, 2), ')');
          setAllLibraryAssets(mapped);
        } catch (err) {
          console.warn('[EditRowModal] Failed to load asset library:', err);
          setAllLibraryAssets([]);
        } finally {
          setIsLoadingAssets(false);
        }
      };
      loadFullLibrary();
    }
  }, [isOpen, record, contentType]);

  // Use full library if loaded, fall back to available assets from records
  const assetPool = allLibraryAssets.length > 0 ? allLibraryAssets : (availableAssets || []);
  
  // Filter assets based on search
  const filteredAssets = assetPool.filter(asset => {
    if (!lumenIdSearch) return true;
    const searchLower = lumenIdSearch.toLowerCase();
    return (
      (asset.lumenId && asset.lumenId.toLowerCase().includes(searchLower)) ||
      (asset.name && asset.name.toLowerCase().includes(searchLower))
    );
  }).slice(0, 20);
  
  // Initialize form from record when modal opens
  useEffect(() => {
    if (isOpen && record) {
      const fields = record.fields || {};
      setName(fields['Name'] || '');
      setCategory(fields['Category'] || '');
      setStatus(fields['Content Status'] || '');
      setComments(fields['Lumen8 Comments'] || '');
      setTakeId(fields['Take ID'] || '');
      setPlayoutDuration(fields['Playout Duration'] || '');
      const existingLumenId = fields['Lumen8 ID'] || '';
      setLumenId(existingLumenId);
      setLumenIdSearch(existingLumenId);
      setSelectedAssetRecordId(null);
      setError('');
    }
  }, [isOpen, record]);
  
  const handleSelectAsset = (asset) => {
    setLumenId(asset.lumenId);
    setSelectedAssetRecordId(asset.recordId || null);
    setLumenIdSearch(asset.lumenId + (asset.name ? ` - ${asset.name}` : ''));
    setShowLumenIdDropdown(false);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    
    setIsSaving(true);
    setError('');
    
    try {
      const fields = record.fields || {};
      const updates = {};
      
      // Only include changed fields (excluding computed fields like Lumen8 ID, Venue)
      if (name.trim() !== (fields['Name'] || '')) {
        updates['Name'] = name.trim();
      }
      if (category !== (fields['Category'] || '')) {
        updates['Category'] = category;
      }
      if (status !== (fields['Content Status'] || '')) {
        updates['Content Status'] = status;
      }
      // Note: Lumen8 ID is a computed/lookup field - can't update directly
      // Instead, link the asset via CG Content / Ribbon Content field
      if (selectedAssetRecordId) {
        const contentField = contentType === 'cg' ? 'CG Content' : contentType === 'ribbon' ? 'Ribbon Content' : contentType === 'iptv' ? 'IPTV GFX' : null;
        if (contentField) {
          updates[contentField] = [selectedAssetRecordId];
          console.log(`[EditRowModal] Linking asset ${selectedAssetRecordId} via ${contentField}`);
        }
      }
      if (comments !== (fields['Lumen8 Comments'] || '')) {
        updates['Lumen8 Comments'] = comments;
      }
      if (takeId !== (fields['Take ID'] || '')) {
        updates['Take ID'] = takeId;
      }
      // Note: Duration is a computed/lookup field from linked asset — can't update directly
      // Playout Duration is operator-defined (writable) for static CG content
      const newPlayoutDur = playoutDuration ? parseInt(playoutDuration) : null;
      const oldPlayoutDur = fields['Playout Duration'] || null;
      if (newPlayoutDur !== oldPlayoutDur) {
        updates['Playout Duration'] = newPlayoutDur;
      }
      
      console.log('[EditRowModal] Updates to save:', updates);
      
      if (Object.keys(updates).length > 0) {
        await onSave(record.id, updates);
      } else {
        console.log('[EditRowModal] No changes detected');
        onClose();
      }
    } catch (err) {
      console.error('Failed to save:', err);
      setError(err.message || 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };
  
  if (!isOpen || !record) return null;
  
  const fields = record.fields || {};
  const showTakeId = contentType === 'cg' || contentType === 'ribbon';
  
  return (
    <>
      {/* Backdrop */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          zIndex: 1000,
        }}
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: THEME.bgElevated,
          borderRadius: '16px',
          border: `1px solid ${THEME.border}`,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
          zIndex: 1001,
          width: '100%',
          maxWidth: '500px',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: `1px solid ${THEME.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div>
            <h2 style={{ 
              fontSize: '18px', 
              fontWeight: 600, 
              color: THEME.textPrimary,
              margin: 0,
            }}>
              Edit Row
            </h2>
            <p style={{ 
              fontSize: '13px', 
              color: THEME.textMuted, 
              margin: '4px 0 0 0',
            }}>
              {contentType.toUpperCase()} • {fields['Category'] || 'Uncategorized'}
            </p>
          </div>
          <button
            onClick={onClose}
            type="button"
            style={{
              background: 'transparent',
              border: 'none',
              color: THEME.textMuted,
              fontSize: '20px',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            ✕
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          {/* Name */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '12px',
              fontWeight: 500,
              color: THEME.textSecondary,
              marginBottom: '6px',
            }}>
              Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter content name"
              style={{
                width: '100%',
                padding: '10px 12px',
                background: THEME.bgCard,
                border: `1px solid ${THEME.border}`,
                borderRadius: '8px',
                color: THEME.textPrimary,
                fontSize: '14px',
                outline: 'none',
              }}
            />
          </div>
          
          {/* Category */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '12px',
              fontWeight: 500,
              color: THEME.textSecondary,
              marginBottom: '6px',
            }}>
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                background: THEME.bgCard,
                border: `1px solid ${THEME.border}`,
                borderRadius: '8px',
                color: THEME.textPrimary,
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="">Select category...</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          
          {/* Lumen ID - Link to uploaded content */}
          <div style={{ marginBottom: '16px', position: 'relative' }}>
            <label style={{
              display: 'block',
              fontSize: '12px',
              fontWeight: 500,
              color: THEME.textSecondary,
              marginBottom: '6px',
            }}>
              Link Content (Lumen ID)
            </label>
            <input
              type="text"
              value={lumenIdSearch}
              onChange={(e) => {
                setLumenIdSearch(e.target.value);
                setShowLumenIdDropdown(true);
                if (!e.target.value) setLumenId('');
              }}
              onFocus={() => setShowLumenIdDropdown(true)}
              onBlur={() => {
                setTimeout(() => setShowLumenIdDropdown(false), 200);
              }}
              placeholder="Search uploaded content..."
              style={{
                width: '100%',
                padding: '10px 12px',
                background: THEME.bgCard,
                border: `1px solid ${lumenId ? THEME.accent : THEME.border}`,
                borderRadius: '8px',
                color: THEME.textPrimary,
                fontSize: '14px',
                outline: 'none',
              }}
            />
            {lumenId && (
              <span style={{
                position: 'absolute',
                right: '12px',
                top: '32px',
                color: THEME.accent,
                fontSize: '12px',
              }}>
                ✓ Linked
              </span>
            )}
            
            {/* Dropdown */}
            {showLumenIdDropdown && filteredAssets.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                maxHeight: '280px',
                overflowY: 'auto',
                background: THEME.bgCard,
                border: `1px solid ${THEME.accent}33`,
                borderRadius: '8px',
                marginTop: '4px',
                zIndex: 10,
                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              }}>
                {isLoadingAssets && (
                  <div style={{ padding: '10px 12px', fontSize: 11, color: THEME.textMuted, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</span> Loading asset library...
                  </div>
                )}
                {filteredAssets.map((asset, idx) => (
                  <div
                    key={asset.lumenId || idx}
                    onClick={() => handleSelectAsset(asset)}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderBottom: idx < filteredAssets.length - 1 ? `1px solid ${THEME.border}` : 'none',
                      transition: 'background 0.15s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = THEME.bgHover}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    {/* Thumbnail */}
                    <div style={{
                      width: 40, height: 40, borderRadius: 6, flexShrink: 0,
                      background: THEME.bgElevated, border: `1px solid ${THEME.border}`,
                      overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {asset.thumb ? (
                        <img src={asset.thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />
                      ) : (
                        <span style={{ fontSize: 16, opacity: 0.3 }}>🎞️</span>
                      )}
                    </div>
                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: THEME.accent, fontSize: '12px', fontWeight: 600, fontFamily: 'JetBrains Mono, monospace' }}>
                        {asset.lumenId}
                      </div>
                      {asset.name && (
                        <div style={{ color: THEME.textSecondary, fontSize: '11px', marginTop: '1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {asset.name}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Status — hidden for manual/venue categories */}
          {!isManualCategory && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '12px',
              fontWeight: 500,
              color: THEME.textSecondary,
              marginBottom: '6px',
            }}>
              Content Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                background: THEME.bgCard,
                border: `1px solid ${THEME.border}`,
                borderRadius: '8px',
                color: THEME.textPrimary,
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="">Select status...</option>
              {Object.keys(THEME.statusColors).map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          )}
          
          {/* Take ID - only for CG/Ribbon, hidden for manual/venue categories */}
          {showTakeId && !isManualCategory && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 500,
                color: THEME.textSecondary,
                marginBottom: '6px',
              }}>
                Take ID
              </label>
              <input
                type="text"
                value={takeId}
                onChange={(e) => setTakeId(e.target.value)}
                placeholder="e.g. CG001"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: THEME.bgCard,
                  border: `1px solid ${THEME.border}`,
                  borderRadius: '8px',
                  color: THEME.textPrimary,
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
            </div>
          )}
          
          {/* Playout Duration — operator-defined for static CG content, hidden for manual/venue categories */}
          {showTakeId && !isManualCategory && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 500,
                color: THEME.textSecondary,
                marginBottom: '6px',
              }}>
                Playout Duration (seconds)
              </label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {['10', '15', '20', '30'].map(d => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setPlayoutDuration(d)}
                    style={{
                      padding: '6px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600,
                      background: String(playoutDuration) === d ? THEME.accent : THEME.bgCard,
                      color: String(playoutDuration) === d ? '#000' : THEME.textSecondary,
                      border: `1px solid ${String(playoutDuration) === d ? THEME.accent : THEME.border}`,
                      cursor: 'pointer', transition: 'all 0.15s',
                    }}
                  >{d}s</button>
                ))}
                <input
                  type="number"
                  value={playoutDuration}
                  onChange={(e) => setPlayoutDuration(e.target.value)}
                  placeholder="Custom"
                  style={{
                    width: '70px',
                    padding: '6px 10px',
                    background: THEME.bgCard,
                    border: `1px solid ${THEME.border}`,
                    borderRadius: '6px',
                    color: THEME.textPrimary,
                    fontSize: '13px',
                    outline: 'none',
                    textAlign: 'center',
                  }}
                />
              </div>
            </div>
          )}
          
          {/* Comments */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '12px',
              fontWeight: 500,
              color: THEME.textSecondary,
              marginBottom: '6px',
            }}>
              Lumen8 Comments
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Optional notes..."
              rows={3}
              style={{
                width: '100%',
                padding: '10px 12px',
                background: THEME.bgCard,
                border: `1px solid ${THEME.border}`,
                borderRadius: '8px',
                color: THEME.textPrimary,
                fontSize: '14px',
                outline: 'none',
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
          </div>
          
          {/* Error */}
          {error && (
            <div style={{
              padding: '10px 12px',
              background: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              color: '#ef4444',
              fontSize: '13px',
              marginBottom: '16px',
            }}>
              {error}
            </div>
          )}
          
          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* Delete button on left - only for manually added rows (no Source Record ID) */}
            {!record?.fields?.['Source Record ID'] ? (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onDelete && onDelete(record);
                }}
                style={{
                  padding: '10px 16px',
                  background: 'transparent',
                  border: `1px solid #ef4444`,
                  borderRadius: '8px',
                  color: '#ef4444',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                🗑️ Delete
              </button>
            ) : (
              <span style={{ fontSize: 12, color: THEME.textMuted }}>
                🔒 Managed by MatchFlow Builder
              </span>
            )}
            
            {/* Cancel/Save on right */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '10px 20px',
                  background: 'transparent',
                  border: `1px solid ${THEME.border}`,
                  borderRadius: '8px',
                  color: THEME.textSecondary,
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                style={{
                  padding: '10px 20px',
                  background: isSaving ? THEME.bgHover : 'linear-gradient(135deg, #2fd6b5 0%, #1a9d87 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  opacity: isSaving ? 0.7 : 1,
                }}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}

// ================= COMMENT EDIT MODAL =================

function CommentEditModal({ isOpen, onClose, value, onSave, title = 'Edit Comment' }) {
  const [text, setText] = useState(value || '');
  const [isSaving, setIsSaving] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      setText(value || '');
    }
  }, [isOpen, value]);
  
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(text);
      onClose();
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setIsSaving(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <>
      {/* Backdrop */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          zIndex: 1000,
        }}
        onClick={onClose}
      />
      
      {/* Modal */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: THEME.bgElevated,
        borderRadius: '12px',
        border: `1px solid ${THEME.border}`,
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
        zIndex: 1001,
        width: '100%',
        maxWidth: '500px',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${THEME.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: 600, 
            color: THEME.textPrimary,
            margin: 0,
          }}>
            {title}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: THEME.textMuted,
              fontSize: '18px',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            ✕
          </button>
        </div>
        
        {/* Content */}
        <div style={{ padding: '20px' }}>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Enter comment..."
            rows={5}
            autoFocus
            style={{
              width: '100%',
              padding: '12px',
              background: THEME.bgCard,
              border: `1px solid ${THEME.border}`,
              borderRadius: '8px',
              color: THEME.textPrimary,
              fontSize: '14px',
              lineHeight: 1.5,
              outline: 'none',
              resize: 'vertical',
              fontFamily: 'inherit',
              minHeight: '120px',
            }}
          />
          
          {/* Character count */}
          <div style={{
            textAlign: 'right',
            marginTop: '8px',
            fontSize: '11px',
            color: THEME.textMuted,
          }}>
            {text.length} characters
          </div>
        </div>
        
        {/* Actions */}
        <div style={{ 
          padding: '16px 20px',
          borderTop: `1px solid ${THEME.border}`,
          display: 'flex', 
          gap: '12px', 
          justifyContent: 'flex-end',
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              background: 'transparent',
              border: `1px solid ${THEME.border}`,
              borderRadius: '6px',
              color: THEME.textSecondary,
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            style={{
              padding: '8px 16px',
              background: isSaving ? THEME.bgHover : THEME.accent,
              border: 'none',
              borderRadius: '6px',
              color: '#000',
              fontSize: '13px',
              fontWeight: 600,
              cursor: isSaving ? 'not-allowed' : 'pointer',
              opacity: isSaving ? 0.7 : 1,
            }}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </>
  );
}

// ================= VIEW TEXT MODAL (Read-only) =================

function ViewTextModal({ isOpen, onClose, value, title = 'View' }) {
  if (!isOpen) return null;
  
  return (
    <>
      {/* Backdrop */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          zIndex: 1000,
        }}
        onClick={onClose}
      />
      
      {/* Modal */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: THEME.bgElevated,
        borderRadius: '12px',
        border: `1px solid ${THEME.border}`,
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
        zIndex: 1001,
        width: '100%',
        maxWidth: '500px',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${THEME.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: 600, 
            color: THEME.textPrimary,
            margin: 0,
          }}>
            {title}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: THEME.textMuted,
              fontSize: '18px',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            ✕
          </button>
        </div>
        
        {/* Content */}
        <div style={{ padding: '20px' }}>
          <div style={{
            padding: '12px',
            background: THEME.bgCard,
            border: `1px solid ${THEME.border}`,
            borderRadius: '8px',
            color: THEME.textPrimary,
            fontSize: '14px',
            lineHeight: 1.6,
            minHeight: '80px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}>
            {value || <span style={{ color: THEME.textMuted, fontStyle: 'italic' }}>No content</span>}
          </div>
        </div>
        
        {/* Actions */}
        <div style={{ 
          padding: '16px 20px',
          borderTop: `1px solid ${THEME.border}`,
          display: 'flex', 
          justifyContent: 'flex-end',
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              background: THEME.bgCard,
              border: `1px solid ${THEME.border}`,
              borderRadius: '6px',
              color: THEME.textSecondary,
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
}

// ================= CLICKABLE COMMENT CELL =================

// eslint-disable-next-line no-unused-vars
function CommentCell({ value, onSave, disabled, placeholder = 'Add comment...' }) {
  const [showEditModal, setShowEditModal] = useState(false);
  
  const displayValue = value && value.length > 20 ? value.substring(0, 20) + '...' : value;
  
  return (
    <>
      <div
        onClick={() => !disabled && setShowEditModal(true)}
        style={{
          padding: '6px 8px',
          background: THEME.bgCard,
          border: `1px solid ${THEME.border}`,
          borderRadius: '4px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: '11px',
          color: value ? THEME.textPrimary : THEME.textMuted,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          opacity: disabled ? 0.6 : 1,
          transition: 'border-color 0.15s ease',
        }}
        onMouseEnter={(e) => !disabled && (e.currentTarget.style.borderColor = THEME.borderHover)}
        onMouseLeave={(e) => e.currentTarget.style.borderColor = THEME.border}
        title={value || placeholder}
      >
        {displayValue || placeholder}
      </div>
      
      <CommentEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        value={value}
        onSave={onSave}
        title="Edit Lumen8 Comment"
      />
    </>
  );
}

// ================= CLUB INSTRUCTIONS (2-line inline, grey container) =================

function ClubInstructionsInline({ value }) {
  const [showModal, setShowModal] = useState(false);
  const hasContent = value && value.trim().length > 0;
  
  if (!hasContent) {
    return <span style={{ fontSize: 11, color: '#555', padding: '4px 0' }}>—</span>;
  }
  
  // Show truncated single-line preview with "+ more" button
  const preview = value.replace(/\n/g, ' ').trim();
  const isLong = preview.length > 30;
  
  return (
    <>
      <div
        onClick={() => setShowModal(true)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          cursor: 'pointer',
          maxWidth: '100%',
        }}
      >
        <span style={{
          fontSize: 11,
          fontWeight: 500,
          color: '#c4c4cc',
          lineHeight: 1.4,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
          minWidth: 0,
        }}>
          {isLong ? preview.slice(0, 30) + '…' : preview}
        </span>
        <span style={{
          fontSize: 9,
          color: '#60a5fa',
          fontWeight: 700,
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}>
          + more
        </span>
      </div>
      
      <ViewTextModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        value={value}
        title="Club Instructions"
      />
    </>
  );
}

// ================= CLICKABLE VIEW CELL (Read-only) =================

// eslint-disable-next-line no-unused-vars
function ViewableTextCell({ value, placeholder = '-', title = 'Club Instructions' }) {
  const [showModal, setShowModal] = useState(false);
  
  const displayValue = value && value.length > 25 ? value.substring(0, 25) + '…' : value;
  const hasContent = value && value.length > 0;
  const isLong = value && value.length > 25;
  
  return (
    <>
      <div
        onClick={() => hasContent && setShowModal(true)}
        style={{
          cursor: hasContent ? 'pointer' : 'default',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: hasContent ? '#fbbf24' : THEME.textMuted,
          fontSize: '12px',
          fontWeight: hasContent ? 600 : 400,
          padding: hasContent ? '3px 6px' : '3px 0',
          background: hasContent ? 'rgba(251, 191, 36, 0.08)' : 'transparent',
          borderRadius: hasContent ? 3 : 0,
          border: hasContent ? '1px dashed rgba(251, 191, 36, 0.25)' : 'none',
        }}
        title={hasContent ? 'Click to view full instructions' : ''}
      >
        {displayValue || placeholder}
        {isLong && (
          <span style={{ color: THEME.accent, marginLeft: '4px', fontSize: '10px', fontWeight: 700 }}>↗</span>
        )}
      </div>
      
      <ViewTextModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        value={value}
        title={title}
      />
    </>
  );
}

// ================= DROPBOX URL HELPERS =================

function isDropboxUrl(url) {
  return url && (url.includes("dropbox.com") || url.includes("dl.dropboxusercontent.com"));
}

/**
 * Analyze Dropbox URL to determine if it's embeddable
 * Returns: { embeddable: boolean, type: 'image'|'video'|'mov'|'folder', embedUrl: string|null }
 */
function analyzeDropboxUrl(url) {
  if (!url || !isDropboxUrl(url)) {
    return { embeddable: false, type: null, embedUrl: null };
  }
  
  // Normalize URL for extension checking (remove query params)
  const urlPath = url.split('?')[0].toLowerCase();
  
  // Folder URLs (no extension, or /fo/ path, /sh/ path for shared folders)
  if (url.includes("/fo/") || url.includes("/folder/") || url.includes("/sh/")) {
    return { embeddable: false, type: "folder", embedUrl: null };
  }
  
  // Check for file extensions anywhere in the URL path
  const hasImageExt = /\.(jpg|jpeg|png|gif)/i.test(urlPath);
  const hasMp4Ext = /\.mp4/i.test(urlPath);
  const hasMovExt = /\.mov/i.test(urlPath);
  const hasWebmExt = /\.webm/i.test(urlPath);
  
  // MOV files - need Dropbox's player, can't embed directly
  if (hasMovExt) {
    return { embeddable: false, type: "mov", embedUrl: null };
  }
  
  // Images (jpg, png, gif) - can embed with raw=1
  if (hasImageExt) {
    const embedUrl = convertToDropboxRaw(url);
    return { embeddable: true, type: "image", embedUrl };
  }
  
  // MP4 and WebM - can embed with raw=1
  if (hasMp4Ext || hasWebmExt) {
    const embedUrl = convertToDropboxRaw(url);
    return { embeddable: true, type: "video", embedUrl };
  }
  
  // No recognized extension - might still be playable, try anyway
  // This handles URLs like dropbox.com/scl/fi/xxxxx without extension in path
  if (url.includes("/scl/fi/") || url.includes("/s/")) {
    // Shared file link - try to make it raw
    const embedUrl = convertToDropboxRaw(url);
    return { embeddable: true, type: "unknown", embedUrl };
  }
  
  return { embeddable: false, type: "unknown", embedUrl: null };
}

/**
 * Convert Dropbox share URL to raw/embeddable URL
 */
function convertToDropboxRaw(url) {
  if (!url) return null;
  
  // Already a raw URL
  if (url.includes("dl.dropboxusercontent.com")) {
    return url;
  }
  
  // Convert www.dropbox.com to dl.dropboxusercontent.com
  // Or just change dl=0 to raw=1
  let embedUrl = url;
  
  // Replace domain for direct access
  embedUrl = embedUrl.replace("www.dropbox.com", "dl.dropboxusercontent.com");
  
  // Handle query params
  if (embedUrl.includes("?")) {
    // Remove dl=0 and add raw=1
    embedUrl = embedUrl.replace(/[?&]dl=\d/, "");
    embedUrl = embedUrl.replace(/[?&]raw=\d/, "");
    embedUrl += (embedUrl.includes("?") ? "&" : "?") + "raw=1";
  } else {
    embedUrl += "?raw=1";
  }
  
  return embedUrl;
}

// ================= PREVIEW MODAL =================

function PreviewModal({ isOpen, onClose, thumbnailUrl, previewUrl, assetName, lumen8Id, dropboxUrl }) {
  const [closeHovered, setCloseHovered] = useState(false);
  
  // Handle escape key to close
  useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (!isOpen) return;
    
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  // Use previewUrl first (Asset Preview URL), then fall back to dropboxUrl
  // previewUrl should have the direct file link with extension
  const effectivePreviewUrl = previewUrl || dropboxUrl || '';
  
  // Analyze Dropbox URL
  const dropboxInfo = analyzeDropboxUrl(effectivePreviewUrl);
  
  // Debug logging
  console.log('[PreviewModal] previewUrl:', previewUrl);
  console.log('[PreviewModal] dropboxUrl:', dropboxUrl);
  console.log('[PreviewModal] effectivePreviewUrl:', effectivePreviewUrl);
  console.log('[PreviewModal] dropboxInfo:', dropboxInfo);
  console.log('[PreviewModal] thumbnailUrl:', thumbnailUrl);
  
  // For Dropbox URLs, always try to convert to raw URL for playback
  // Even if analyzeDropboxUrl doesn't recognize the extension, try raw conversion
  let displayUrl = null;
  let displayIsVideo = false;
  
  if (dropboxInfo.embeddable) {
    // Known embeddable format (jpg, png, gif, mp4)
    displayUrl = dropboxInfo.embedUrl;
    displayIsVideo = dropboxInfo.type === "video";
  } else if (isDropboxUrl(effectivePreviewUrl) && dropboxInfo.type !== "folder" && dropboxInfo.type !== "mov") {
    // Try to convert any Dropbox URL to raw (might work for some files)
    displayUrl = convertToDropboxRaw(effectivePreviewUrl);
    // Check if it looks like a video
    displayIsVideo = /\.(mp4|webm|mov)(\?|$|&)/i.test(effectivePreviewUrl);
  } else if (!isDropboxUrl(effectivePreviewUrl) && effectivePreviewUrl) {
    // Non-Dropbox URL, use directly
    displayUrl = effectivePreviewUrl;
    displayIsVideo = /\.(mp4|webm|mov)(\?|$|&)/i.test(effectivePreviewUrl);
  }
  
  // Fall back to thumbnail only if we have nothing else
  if (!displayUrl && thumbnailUrl) {
    displayUrl = thumbnailUrl;
    displayIsVideo = false;
  }
  
  console.log('[PreviewModal] displayUrl:', displayUrl);
  console.log('[PreviewModal] displayIsVideo:', displayIsVideo);
  
  // Modal styles
  const modalStyles = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.85)",
      zIndex: 1000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 40,
    },
    content: {
      position: "relative",
      maxWidth: "min(720px, calc(100vw - 340px))",
      maxHeight: "80vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    closeBtn: {
      position: "absolute",
      top: -40,
      right: -40,
      width: 36,
      height: 36,
      borderRadius: "50%",
      backgroundColor: closeHovered ? "rgba(255, 255, 255, 0.2)" : "rgba(255, 255, 255, 0.1)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
      color: "#fff",
      fontSize: 24,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "background-color 0.2s",
    },
    media: {
      maxWidth: "100%",
      maxHeight: "70vh",
      objectFit: "contain",
      borderRadius: 8,
      boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
    },
    title: {
      color: "#fff",
      fontSize: 14,
      fontWeight: 500,
      marginTop: 16,
      textAlign: "center",
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    lumen8Id: {
      color: THEME.accent,
      fontSize: 12,
      fontFamily: 'JetBrains Mono, monospace',
      marginTop: 4,
    },
    actions: {
      display: "flex",
      gap: 12,
      marginTop: 16,
    },
    actionBtn: {
      padding: "8px 16px",
      fontSize: 13,
      fontWeight: 500,
      color: "#fff",
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
      borderRadius: 6,
      cursor: "pointer",
      textDecoration: "none",
      display: "flex",
      alignItems: "center",
      gap: 6,
      transition: "background-color 0.2s",
    },
    placeholder: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      width: 400,
      height: 300,
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      borderRadius: 8,
      color: "rgba(255, 255, 255, 0.5)",
    },
    placeholderIcon: {
      fontSize: 64,
      marginBottom: 16,
    },
    placeholderText: {
      fontSize: 14,
    },
  };

  return (
    <div 
      style={modalStyles.overlay}
      onClick={(e) => {
        // Close when clicking the overlay background
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div style={modalStyles.content}>
        {/* Close button */}
        <button
          onClick={onClose}
          onMouseEnter={() => setCloseHovered(true)}
          onMouseLeave={() => setCloseHovered(false)}
          style={modalStyles.closeBtn}
        >
          ×
        </button>

        {/* Media content */}
        {displayUrl ? (
          displayIsVideo ? (
            <video
              src={displayUrl}
              controls
              autoPlay
              style={modalStyles.media}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <img
              src={displayUrl}
              alt={assetName || 'Preview'}
              style={modalStyles.media}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          )
        ) : (
          <div style={modalStyles.placeholder}>
            <span style={modalStyles.placeholderIcon}>
              {dropboxInfo.type === "video" || dropboxInfo.type === "mov" ? "🎬" : "🖼️"}
            </span>
            <span style={modalStyles.placeholderText}>
              {dropboxInfo.type === "folder" 
                ? "Folder preview not available" 
                : dropboxInfo.type === "mov"
                  ? "MOV files must be opened in Dropbox"
                  : "No preview available"}
            </span>
          </div>
        )}

        {/* Title */}
        <div style={modalStyles.title}>{assetName || "Untitled"}</div>
        {lumen8Id && <div style={modalStyles.lumen8Id}>{lumen8Id}</div>}

        {/* Action buttons */}
        <div style={modalStyles.actions}>
          {effectivePreviewUrl && (
            <a
              href={effectivePreviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={modalStyles.actionBtn}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(47, 214, 181, 0.3)";
                e.currentTarget.style.borderColor = "#2fd6b5";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.2)";
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
              </svg>
              {isDropboxUrl(effectivePreviewUrl) ? 'Open in Dropbox' : 'Open in New Tab'}
            </a>
          )}
          {dropboxUrl && !isDropboxUrl(effectivePreviewUrl) && (
            <a
              href={dropboxUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={modalStyles.actionBtn}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(47, 214, 181, 0.3)";
                e.currentTarget.style.borderColor = "#2fd6b5";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.2)";
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
              </svg>
              View in Dropbox
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
